import { flightSide, flightExportDetails } from '../../utils/flightDisplay';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { Clock, Calendar as CalendarIcon, MapPin, User, Settings, ChevronDown, ChevronUp, Sparkles, List, LayoutGrid, LayoutList, ChevronRight, Car, CheckSquare, Square, X, Plus, Check, StickyNote as StickyNoteIcon, Plane, Building, UtensilsCrossed, Handshake, Users, FileDown, FolderOpen, BadgeCheck, Mic } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import TimelineComponentCard from './TimelineComponentCard';
import { HotelStayCard, HotelCheckoutCard, findHotelTransfer, describeStop, hotelTransferEnds, URBANRIDE_ELIGIBLE_TYPES } from './HotelStayCards';
import { TripTimelineCard, tripCardKind, findLinkedRide } from './TripTimelineCards';
import { passesTimeFilter } from '../../utils/timelineFilters';
import { parseFlightNumber } from '../../utils/airlineUtils';
import UrbanrideQuickBooking from './UrbanrideQuickBooking';
import UrbanrideBookingSummary from './UrbanrideBookingSummary';
import StickyNote from './StickyNote';
import StickyNoteModal from './StickyNoteModal';
import ConfidentialitySettingsModal from './ConfidentialitySettingsModal';
import BundleSelectionSummary from './BundleSelectionSummary';
import { Button } from '../ui/button';
import { AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatTimePreserveTimezone, formatWeekdayDate as fmtWeekdayDate, parseLocalDate } from '../../utils/dateTimeUtils';
import { useStickyNoteEvents, useComponentEvents, useFlightUpdateEvents, SSEEventType } from '../../contexts/SSEContext';
import {
  listStickyNotes,
  createStickyNote,
  updateStickyNote,
  deleteStickyNote,
  toggleNoteExpanded,
  updateNotePosition
} from '../../services/stickyNoteService';

// Parse datetime string without timezone conversion for consistent sorting
// This ensures sorting uses the stored local time, not browser timezone
// Resolve a flight leg's departure/arrival into a clean datetime string.
//
// Leg datetimes arrive in several shapes depending on how the flight was
// created: parsed confirmations use separate departure_date + departure_time;
// manually-added flights (and the edit round-trip) store a full
// departure_datetime and can even leave a full "YYYY-MM-DDTHH:MM:SS" in the
// *_time field. Naively doing `${date}T${time}` then yields a malformed value
// like "2026-07-29T2026-07-29T10:45:00", which sorts and displays wrong (the
// leg falls to the bottom of the timeline). This normalizes all of those to a
// single "YYYY-MM-DDTHH:MM[:SS]" (or a bare value when that's all there is).
const ISO_DT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const resolveLegDateTime = (leg, kind) => {
  if (!leg) return null;
  const dt = leg[`${kind}_datetime`];
  const date = leg[`${kind}_date`];
  const time = leg[`${kind}_time`];
  // A field that already holds a full datetime wins outright.
  if (dt && ISO_DT.test(String(dt))) return String(dt);
  if (time && ISO_DT.test(String(time))) return String(time);
  if (date && ISO_DT.test(String(date))) return String(date);
  // Otherwise combine a plain date + plain time, guarding against a *_time
  // that carries its own "...T..." prefix.
  const datePart = date ? String(date).split('T')[0] : '';
  let timePart = time ? String(time) : '';
  if (timePart.includes('T')) timePart = timePart.split('T').pop() || '';
  if (datePart && timePart) return `${datePart}T${timePart}`;
  if (datePart) return datePart;
  if (dt) return String(dt);
  return timePart || null;
};

const parseDatetimeForSortValue = (dateStr) => {
  if (!dateStr) return null;
  try {
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})T?(\d{2})?:?(\d{2})?:?(\d{2})?/);
    if (!match) return null;
    const [, year, month, day, hour = '00', minute = '00', second = '00'] = match;
    return Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10)
    );
  } catch (e) {
    return null;
  }
};

// Curated customer-service phone numbers for common travel providers, used to
// enrich the PDF Quick Reference. Airline confirmations don't carry a phone and
// the airline dataset has none, so airlines are looked up by IATA code; hotel
// chains and car companies are matched by a substring of the provider name. A
// phone parsed from the confirmation (e.g. a hotel's own number) takes priority.
const PROVIDER_PHONES = {
  airlines: {
    WN: '1-800-435-9792', AA: '1-800-433-7300', DL: '1-800-221-1212',
    UA: '1-800-864-8331', B6: '1-800-538-2583', AS: '1-800-252-7522',
    NK: '1-855-728-3555', F9: '1-801-401-9000', HA: '1-800-367-5320',
    G4: '1-702-505-8888', SY: '1-800-359-6786',
  },
  hotels: [
    ['marriott', '1-888-236-2427'], ['ritz', '1-800-542-8680'],
    ['hilton', '1-800-445-8667'], ['hyatt', '1-800-323-7249'],
    ['intercontinental', '1-877-834-3613'], ['holiday inn', '1-800-465-4329'],
    ['wyndham', '1-800-407-9832'], ['best western', '1-800-780-7234'],
    ['choice', '1-877-424-6423'], ['four seasons', '1-800-819-5053'],
    ['fairmont', '1-800-441-1414'], ['omni', '1-888-444-6664'],
  ],
  cars: [
    ['hertz', '1-800-654-3131'], ['avis', '1-800-352-7900'],
    ['enterprise', '1-855-266-9289'], ['budget', '1-800-218-7992'],
    ['national', '1-844-382-6875'], ['alamo', '1-844-354-6962'],
    ['dollar', '1-800-800-4000'], ['thrifty', '1-877-283-0898'],
    ['sixt', '1-888-749-8227'],
  ],
};

// Resolve a { name, phone } for the Quick Reference line of a PDF component.
const resolveProviderInfo = (comp, compType) => {
  const cd = comp.component_data || comp.originalComponent?.component_data || {};
  const leg = comp.flightLeg || {};
  const stripCode = (s) => String(s || '').replace(/\s*\([A-Z0-9]{2,3}\)\s*$/, '').trim();

  if (compType === 'commercial_flight' || compType === 'private_flight' || comp.isFlightLeg) {
    const name = stripCode(leg.airline_full_name || leg.airline_name || leg.airline || cd.airline_full_name || cd.airline_name);
    // Derive the 2-letter IATA code: an explicit short code field, the "(XX)"
    // suffix on the name, or the prefix of the flight number.
    let code = '';
    const shortish = [leg.airline_name, leg.airline].find(v => /^[A-Z0-9]{2,3}$/i.test(String(v || '')));
    if (shortish) code = String(shortish).toUpperCase();
    if (!code) {
      const m = String(leg.airline_full_name || leg.airline_name || leg.airline || '').match(/\(([A-Z0-9]{2,3})\)\s*$/i);
      if (m) code = m[1].toUpperCase();
    }
    if (!code) {
      const parsed = parseFlightNumber(leg.flight_number || '');
      if (parsed.airlineCode) code = parsed.airlineCode.toUpperCase();
    }
    return { name: name || 'Airline', phone: PROVIDER_PHONES.airlines[code] || '' };
  }

  if (compType === 'hotel') {
    const name = cd.hotel_name || comp.hotelData?.hotel_name || comp.title || 'Hotel';
    const parsedPhone = cd.phone || comp.hotelData?.phone || '';
    const lower = String(name).toLowerCase();
    const chain = PROVIDER_PHONES.hotels.find(([key]) => lower.includes(key));
    return { name, phone: parsedPhone || (chain ? chain[1] : '') };
  }

  if (compType === 'rental_car' || compType === 'car_rental') {
    const name = cd.rental_company || comp.title || 'Rental Car';
    const parsedPhone = cd.phone || cd.company_phone || '';
    const lower = String(name).toLowerCase();
    const company = PROVIDER_PHONES.cars.find(([key]) => lower.includes(key));
    return { name, phone: parsedPhone || (company ? company[1] : '') };
  }

  if (compType === 'chauffeur_service') {
    return { name: cd.provider_name || 'Urbanride', phone: cd.provider_phone || cd.phone || '' };
  }

  return { name: '', phone: '' };
};

// Droppable zone component
const DroppableZone = ({ id, children, className = "" }) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'border-ui-violet-solid bg-ui-violet-bg' : ''}`}
      style={{
        pointerEvents: 'auto',
        zIndex: 10
      }}
    >
      {children}
    </div>
  );
};

// Combined Drop Zone + Insert Button - single space between components
const DropZoneWithInsert = ({ dropId, onInsert, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { setNodeRef, isOver } = useDroppable({
    id: dropId,
  });

  return (
    <div
      ref={setNodeRef}
      className={`relative h-6 sm:h-7 w-full flex items-center justify-center cursor-pointer transition-all duration-200 my-0.5 rounded ${className} ${
        isOver ? 'bg-ui-violet-bg border-2 border-dashed border-ui-violet-solid' : 'border-2 border-transparent'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onInsert}
      style={{
        pointerEvents: 'auto',
        zIndex: 10
      }}
    >
      {/* Drop indicator line - shows when hovering (not dragging) */}
      <div className={`absolute inset-x-4 h-0.5 transition-all duration-200 ${
        isOver ? 'opacity-0' : isHovered ? 'bg-ui-violet-line opacity-100' : 'opacity-0'
      }`} />

      {/* Plus button - shows on hover (not when dragging over) */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{
          scale: isHovered && !isOver ? 1 : 0,
          opacity: isHovered && !isOver ? 1 : 0
        }}
        transition={{ duration: 0.15 }}
        className="relative z-10 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-ui-violet-strong hover:bg-ui-violet-strong/90 text-ui-on-primary shadow-lg flex items-center justify-center transition-colors"
        title="Add component here"
        onClick={(e) => {
          e.stopPropagation();
          onInsert();
        }}
      >
        <Plus className="w-4 h-4" />
      </motion.button>

      {/* Drop indicator text - shows when dragging over */}
      {isOver && (
        <span className="text-xs text-ui-violet-fg font-medium">Drop here</span>
      )}
    </div>
  );
};

// Insert Component Button - appears between components on hover (legacy, kept for end zone)
const InsertComponentButton = ({ onClick, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative h-4 sm:h-6 w-full flex items-center justify-center group cursor-pointer ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {/* Hover line */}
      <div className={`absolute inset-x-4 h-0.5 bg-ui-violet-line transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />

      {/* Plus button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{
          scale: isHovered ? 1 : 0,
          opacity: isHovered ? 1 : 0
        }}
        transition={{ duration: 0.15 }}
        className="relative z-10 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-ui-violet-strong hover:bg-ui-violet-strong/90 text-ui-on-primary shadow-lg flex items-center justify-center transition-colors"
        title="Add component here"
      >
        <Plus className="w-4 h-4" />
      </motion.button>
    </div>
  );
};

// Helper function to get hotel full location (copied from TripTimeline.js)
const getHotelFullLocation = (hotelData, fallbackLocation) => {
  if (hotelData?.hotel_address && hotelData.hotel_address.trim()) {
    return hotelData.hotel_address;
  }
  // Also check 'address' field for imported hotel confirmations
  if (hotelData?.address && hotelData.address.trim()) {
    return hotelData.address;
  }
  if (hotelData?.location && hotelData.location.trim()) {
    return hotelData.location;
  }
  return fallbackLocation || 'Location TBD';
};

// Helper function to check if a component has TBD time (actually TBD, not just supports it)
const isTBDComponent = (component) => {
  const compData = component?.component_data || {};
  const startDateTime = component?.start_datetime;
  const hasDateInData = !!(compData.meeting_date || compData.event_date || compData.restaurant_date);

  // SMART TBD DETECTION: Use actual component state rather than potentially incorrect flags

  // SPECIAL CASE: Private flights without departure/arrival dates are TBD
  if (component?.component_type === 'private_flight') {
    // For private flight LEGS (processed components), check the flightLeg data
    if (component?.isPrivateFlightLeg && component?.flightLeg) {
      const leg = component.flightLeg;
      const hasDepartureDate = leg.departure_date && leg.departure_date.trim() !== '';
      const hasDepartureTime = leg.departure_time && leg.departure_time.trim() !== '' && leg.departure_time.toLowerCase() !== 'tbd';
      const hasArrivalDate = leg.arrival_date && leg.arrival_date.trim() !== '';
      const hasArrivalTime = leg.arrival_time && leg.arrival_time.trim() !== '' && leg.arrival_time.toLowerCase() !== 'tbd';

      const hasFullDep = hasDepartureDate && hasDepartureTime;
      const hasFullArr = hasArrivalDate && hasArrivalTime;

      // Not TBD if either side has a complete date + time
      if (hasFullDep || hasFullArr) return false;

      // Nothing at all → TBD
      if (!hasDepartureDate && !hasDepartureTime && !hasArrivalDate && !hasArrivalTime) {
        return true;
      }

      // Partial info (date without time, or time without date) → TBD
      return true;
    }

    // For private flight components (not legs), check component_data
    const details = compData.details || compData || {};
    const hasDepartureDate = details.departure_date && details.departure_date.trim() !== '';
    const hasArrivalDate = details.arrival_date && details.arrival_date.trim() !== '';
    const hasDepartureTime = details.departure_time && details.departure_time.trim() !== '' && details.departure_time.toLowerCase() !== 'tbd';
    const hasArrivalTime = details.arrival_time && details.arrival_time.trim() !== '' && details.arrival_time.toLowerCase() !== 'tbd';

    // If private flight has no dates/times set, it's TBD and draggable
    if (!hasDepartureDate && !hasArrivalDate && !hasDepartureTime && !hasArrivalTime) {
      return true;
    }

    // Not TBD if either side has a complete date + time
    if ((hasDepartureDate && hasDepartureTime) || (hasArrivalDate && hasArrivalTime)) {
      return false;
    }

    // Partial date/time info → TBD
    if (hasDepartureDate || hasArrivalDate || hasDepartureTime || hasArrivalTime) {
      return true;
    }
  }

  // 1. Check TBD flags first - these take priority over datetime values
  // Check both component level and component_data level for backwards compatibility
  const dateIsTBD = component?.date_is_tbd === true || compData.date_is_tbd === true;
  const timeIsTBD = component?.time_is_tbd === true || compData.time_is_tbd === true;

  if (dateIsTBD || timeIsTBD) {
    return true;
  }

  // 2. If component has an actual start_datetime and no TBD flags, it's NOT TBD
  if (startDateTime && typeof startDateTime === 'string' && startDateTime.trim() !== '') {
    // Exception: if start_datetime contains TBD-TIME format, it IS TBD
    if (startDateTime.includes('-TBD-TIME')) {
      return true;
    }
    // Otherwise, if it has a real datetime and no TBD flags, it's fixed
    return false;
  }

  // 3. If no start_datetime but has date info in component_data, it's likely TBD
  if (!startDateTime && hasDateInData) {
    return true;
  }

  // 4. If component title contains "TBD", it's probably TBD
  if (component?.title && component.title.toLowerCase().includes('tbd')) {
    return true;
  }

  // 5. If component has no start_datetime at all, it's TBD
  // This covers bundles, memos, and any component created without dates
  if (!startDateTime || (typeof startDateTime === 'string' && startDateTime.trim() === '')) {
    return true;
  }

  // 6. Default to NOT TBD for everything else
  return false;
};

// Helper function to check if a component supports TBD dates/times (for UI purposes)
const canHaveTBDDateTime = (component) => {
  // Components that can have TBD dates/times
  const tbdSupportedTypes = ['meeting', 'restaurant', 'event', 'private_flight', 'bundle'];
  return tbdSupportedTypes.includes(component.component_type);
};

// Check if a component type is eligible for Urbanride selection
const isUrbanrideEligible = (componentType) => {
  return URBANRIDE_ELIGIBLE_TYPES.includes(componentType);
};

// A timeline item a hotel transfer can start or end at
const isTransferStop = (component) => !component.bundle_id && isUrbanrideEligible(component.originalComponent?.component_type || component.component_type);

// Icon + colour config used by the compact row for each component type
const COMPACT_TYPE_CONFIG = {
  hotel:             { Icon: Building,        gradient: 'linear-gradient(135deg,rgb(var(--ui-indigo-strong)),rgb(var(--ui-violet-strong)))', solidColor: 'rgb(var(--ui-indigo-line))' },
  meeting:           { Icon: Users,           gradient: 'linear-gradient(135deg,rgb(var(--ui-green-strong)),rgb(var(--ui-teal-strong)))', solidColor: 'rgb(var(--ui-green-line))' },
  speaker_meeting:   { Icon: Mic,             gradient: 'linear-gradient(135deg,rgb(var(--ui-red-strong)),rgb(var(--ui-pink-strong)))', solidColor: 'rgb(var(--ui-red-line))' },
  restaurant:        { Icon: UtensilsCrossed, gradient: 'linear-gradient(135deg,rgb(var(--ui-orange-strong)),rgb(var(--ui-amber-strong)))', solidColor: 'rgb(var(--ui-orange-line))' },
  private_flight:    { Icon: Plane,           gradient: 'linear-gradient(135deg,rgb(var(--ui-violet-strong)),rgb(var(--ui-indigo-strong)))', solidColor: 'rgb(var(--ui-violet-line))' },
  commercial_flight: { Icon: Plane,           gradient: 'linear-gradient(135deg,rgb(var(--ui-blue-strong)),rgb(var(--ui-teal-strong)))', solidColor: 'rgb(var(--ui-blue-line))' },
  flight:            { Icon: Plane,           gradient: 'linear-gradient(135deg,rgb(var(--ui-blue-strong)),rgb(var(--ui-teal-strong)))', solidColor: 'rgb(var(--ui-blue-line))' },
  rental_car:        { Icon: Car,             gradient: 'linear-gradient(135deg,rgb(var(--ui-green-strong)),rgb(var(--ui-green-fg)))', solidColor: 'rgb(var(--ui-green-line))' },
  car_rental:        { Icon: Car,             gradient: 'linear-gradient(135deg,rgb(var(--ui-green-strong)),rgb(var(--ui-green-fg)))', solidColor: 'rgb(var(--ui-green-line))' },
  chauffeur_service: { Icon: Car,             gradient: 'linear-gradient(135deg,rgb(var(--ui-teal-strong)),rgb(var(--ui-teal-fg)))', solidColor: 'rgb(var(--ui-teal-line))' },
  airport_vip_service:{ Icon: BadgeCheck,     gradient: 'linear-gradient(135deg,rgb(var(--ui-amber-strong)),rgb(var(--ui-orange-strong)))', solidColor: 'rgb(var(--ui-amber-line))' },
  memo:              { Icon: StickyNoteIcon,  gradient: 'linear-gradient(135deg,rgb(var(--ui-amber-strong)),rgb(var(--ui-amber-fg)))', solidColor: 'rgb(var(--ui-amber-line))' },
  event:             { Icon: CalendarIcon,    gradient: 'linear-gradient(135deg,rgb(var(--ui-pink-strong)),rgb(var(--ui-red-strong)))', solidColor: 'rgb(var(--ui-pink-line))' },
  bundle:            { Icon: FolderOpen,      gradient: 'linear-gradient(135deg,rgb(var(--ui-violet-strong)),rgb(var(--ui-violet-fg)))', solidColor: 'rgb(var(--ui-violet-line))' },
  general:           { Icon: CalendarIcon,    gradient: 'linear-gradient(135deg,rgb(var(--ui-muted)),rgb(var(--ui-ink-3)))', solidColor: 'rgb(var(--ui-line))' },
};

// Compact single-line row with expandable full card
const CompactTimelineRow = ({
  component,
  typeStyle,
  isExpanded,
  onToggleExpand,
  rowIndex,
  hasNotes,
  // Urbanride selection mode props
  selectionMode = false,
  isSelected = false,
  onToggleSelect = () => {},
  isSelectable = false,
  // All props forwarded to TimelineComponentCard when expanded
  onEditComponent,
  onDeleteComponent,
  onReassignComponent,
  allTrips,
  currentTripId,
  trip,
  insideNotes,
  onToggleNoteExpand,
  onEditNote,
  onDeleteNote,
  onNotePositionChange,
  onNoteMoveToComponent,
  currentUserId,
  isConfidential,
  canToggleConfidentiality,
  onToggleConfidentiality,
  onOpenConfidentialityModal,
  onRevealConfidential,
  onConcealConfidential,
  isRevealingId,
  isConcealingId,
  onQuickEdit,
  onAddVipServiceForLeg,
  onInlineUpdateComponent,
  formatDateTime,
  getStatusColor,
  getComponentTypeName,
  componentTypeStyles,
  executives = [],
}) => {
  const { user } = useAuth();
  const prefs = user?.ui_preferences || { date_input_format: 'US', time_view_format: 'AMPM' };
  const contentRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const ct = component.originalComponent?.component_type || component.component_type;
  const cfg = COMPACT_TYPE_CONFIG[ct] || COMPACT_TYPE_CONFIG.general;
  const { Icon } = cfg;
  const cd = component.component_data || {};
  const isComponentTBD = isTBDComponent(component);

  // ── People list (for hover tooltip) ──────────────────────────────────────
  const getPeopleList = () => {
    const names = [];
    const execById = {};
    (executives || []).forEach(e => {
      execById[e.id] = e.full_name || [e.first_name, e.last_name].filter(Boolean).join(' ');
    });
    switch (ct) {
      case 'private_flight':
      case 'commercial_flight':
      case 'flight': {
        const pax = cd.passengers || cd.additional_travelers || [];
        pax.forEach(p => {
          const name = p.full_name || p.name || [p.first_name, p.last_name].filter(Boolean).join(' ');
          if (name && !names.includes(name)) names.push(name);
        });
        const leg = component.flightLeg;
        if (leg) {
          (leg.seat_assignments || []).forEach(s => {
            const name = s.passenger_name || (s.executive_id && execById[s.executive_id]);
            if (name && !names.includes(name)) names.push(name);
          });
          (leg.leg_passengers || leg.passengers || []).forEach(p => {
            const name = p.full_name || p.name || [p.first_name, p.last_name].filter(Boolean).join(' ');
            if (name && !names.includes(name)) names.push(name);
          });
        }
        break;
      }
      case 'hotel': {
        (cd.room_details || []).forEach(r => {
          (r.guest_names || []).forEach(n => { if (n && !names.includes(n)) names.push(n); });
          (r.guest_ids || []).forEach(id => {
            const n = execById[id];
            if (n && !names.includes(n)) names.push(n);
          });
        });
        if (names.length === 0) {
          (cd.assigned_executives || []).forEach(id => {
            const n = execById[id];
            if (n && !names.includes(n)) names.push(n);
          });
          (cd.executive_names || []).forEach(n => { if (n && !names.includes(n)) names.push(n); });
        }
        if (names.length === 0) {
          // speaker_bureau mode: single free-text primary guest / linked Speaker
          const pg = cd.primary_guest_speaker_name || cd.primary_guest_name;
          if (pg) names.push(pg);
        }
        break;
      }
      case 'meeting': {
        if (cd.primary_executive_id) {
          const n = execById[cd.primary_executive_id];
          if (n) names.push(n);
        }
        (cd.meeting_attendees || cd.attendees || []).forEach(a => {
          if (a.name && !names.includes(a.name)) names.push(a.name);
        });
        break;
      }
      case 'restaurant': {
        if (cd.reservation_holder_name && !names.includes(cd.reservation_holder_name))
          names.push(cd.reservation_holder_name);
        (cd.restaurant_attendees || []).forEach(a => {
          if (a.name && !names.includes(a.name)) names.push(a.name);
        });
        break;
      }
      case 'chauffeur_service': {
        (cd.passengers || []).forEach(p => {
          const name = p.name || [p.firstName, p.lastName].filter(Boolean).join(' ');
          if (name) names.push(name + (p.extra > 0 ? ` +${p.extra}` : ''));
        });
        if (names.length === 0 && cd.passenger_name) names.push(cd.passenger_name);
        break;
      }
      case 'memo': {
        if (cd.primary_executive_id) {
          const n = execById[cd.primary_executive_id];
          if (n) names.push(n);
        }
        (cd.memo_attendees || []).forEach(a => {
          if (a.name && !names.includes(a.name)) names.push(a.name);
        });
        break;
      }
      case 'event': {
        (cd.attendees || cd.event_attendees || []).forEach(a => {
          const name = a.name || a.full_name;
          if (name && !names.includes(name)) names.push(name);
        });
        break;
      }
      default:
        break;
    }
    return names;
  };

  const people = getPeopleList();

  // ── Headline ──────────────────────────────────────────────────────────────
  const getHeadline = () => {
    switch (ct) {
      case 'private_flight':
        if (component.isPrivateFlightLeg && component.flightLeg) {
          const leg = component.flightLeg;
          const dep = leg.departure_airport_iata || leg.departure_airport_icao?.slice(-3) || leg.departure_airport || '?';
          const arr = leg.arrival_airport_iata || leg.arrival_airport_icao?.slice(-3) || leg.arrival_airport || '?';
          const depFbo = leg.departure_fbo_name || leg.departure_fbo || cd.departure_fbo_name || cd.departure_fbo || '';
          const arrFbo = leg.arrival_fbo_name || leg.arrival_fbo || cd.arrival_fbo_name || cd.arrival_fbo || '';
          const depStr = depFbo ? `${dep} (${depFbo})` : dep;
          const arrStr = arrFbo ? `${arr} (${arrFbo})` : arr;
          return `${depStr} → ${arrStr}`;
        }
        // Top-level private flight (not a leg)
        {
          const depFbo = cd.departure_fbo_name || cd.departure_fbo || '';
          const arrFbo = cd.arrival_fbo_name || cd.arrival_fbo || '';
          const depStr = depFbo ? `(${depFbo})` : '';
          const arrStr = arrFbo ? `(${arrFbo})` : '';
          const title = component.title || 'Private Flight';
          return depStr || arrStr
            ? `${title}${depStr ? ' ' + depStr : ''}${arrStr ? ' → ' + arrStr : ''}`
            : title;
        }
      case 'commercial_flight':
      case 'flight':
        if (component.flightLeg) {
          const leg = component.flightLeg;
          const dep = leg.departure_airport || '?';
          const arr = leg.arrival_airport || '?';
          const rawFn = leg.flight_number || leg.flightNumber || '';
          const airlineCode = leg.airline_code || leg.iata_code || leg.airline || '';
          // Build "AA 401" style: if rawFn already starts with the airline code keep as-is
          const fn = rawFn
            ? (airlineCode && !rawFn.toUpperCase().startsWith(airlineCode.toUpperCase())
                ? `${airlineCode} ${rawFn}`
                : rawFn)
            : airlineCode;
          return fn ? `${dep} → ${arr} · ${fn}` : `${dep} → ${arr}`;
        }
        return component.title || 'Flight';
      case 'hotel':
        return (cd.hotel_name || component.title || 'Hotel') +
          (component.isHotelEvent ? (component.id?.includes('checkout') ? ' · Check-out' : ' · Check-in') : '');
      case 'restaurant':
        return cd.restaurant_name || component.title || 'Restaurant';
      case 'rental_car':
      case 'car_rental':
        return cd.pickup_location || component.title || 'Rental Car';
      case 'chauffeur_service':
        return component.title || cd.passenger_name || 'Chauffeur Service';
      case 'airport_vip_service': {
        const dirLabel = cd.service_direction === 'departure' ? 'Departure'
          : cd.service_direction === 'arrival' ? 'Arrival'
          : '';
        const apt = cd.airport_code || '';
        const provider = cd.provider_name || '';
        const head = [apt, dirLabel, 'VIP Greet'].filter(Boolean).join(' ');
        return provider ? `${head} · ${provider}` : (head || component.title || 'Airport VIP Service');
      }
      case 'memo':
        return component.title || (cd.memo_content || '').slice(0, 80) || 'Memo';
      default:
        return component.title || ct;
    }
  };

  // ── Date / Time ───────────────────────────────────────────────────────────
  const compactStartDate = component.start_datetime ? parseLocalDate(component.start_datetime) : null;
  const compactDate = compactStartDate ? format(compactStartDate, 'EEE MMM d') : null;
  const compactTime = component.start_datetime
    ? formatTimePreserveTimezone(component.start_datetime, prefs) +
      (component.end_datetime ? ' – ' + formatTimePreserveTimezone(component.end_datetime, prefs) : '')
    : null;

  // ── Dot background ────────────────────────────────────────────────────────
  const dotStyle = { backgroundImage: cfg.gradient };

  const isEven = rowIndex % 2 === 0;
  const rowBg = isEven ? 'bg-ui-ink/[0.03]' : '';

  const hasConfirmation = !!(component.confirmation_number || cd.confirmation_number);

  // In Urbanride mode clicking an eligible row adds it; non-eligible rows expand instead
  const handleRowClick = () => {
    if (selectionMode && isSelectable) {
      onToggleSelect(component);
    } else if (!selectionMode) {
      onToggleExpand(component.id);
    }
    // non-selectable in selection mode → no action
  };

  return (
    <div className="relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
    <div className={`rounded-lg overflow-hidden ${isExpanded ? 'ring-1 ring-ui-ink/10' : ''}`}>
      {/* ── Single-line row ─────────────────────────────────────────────── */}
      <button
        onClick={handleRowClick}
        className={`w-full flex items-center gap-2 h-10 px-2 sm:px-3 text-left transition-colors rounded-lg
          ${selectionMode && isSelected
            ? 'bg-ui-blue-bg'
            : isExpanded
              ? 'bg-ui-ink/10 rounded-b-none'
              : `${rowBg} hover:bg-ui-ink/10`
          }
          ${selectionMode && !isSelectable ? 'opacity-50 cursor-default' : ''}`}
      >
        {/* Type dot + icon */}
        <span
          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
          style={dotStyle}
        >
          <Icon className="w-2.5 h-2.5 text-ui-on-primary" />
        </span>

        {/* Date */}
        <span className={`hidden sm:block text-xs w-[88px] flex-shrink-0 tabular-nums
          text-ui-ink-3`}>
          {isComponentTBD
            ? <span className="text-ui-amber-fg font-medium">TBD</span>
            : (compactDate || '—')}
        </span>

        {/* Time */}
        <span className={`hidden md:block text-xs w-[110px] flex-shrink-0 tabular-nums
          text-ui-muted`}>
          {!isComponentTBD && compactTime ? compactTime : ''}
        </span>

        {/* Headline */}
        <span className={`flex-1 min-w-0 text-sm font-medium truncate
          text-ui-ink`}>
          {getHeadline()}
        </span>

        {/* Note badge */}
        {hasNotes && !selectionMode && (
          <span className="flex-shrink-0 text-ui-amber-fg" title="Has sticky notes">
            <StickyNoteIcon className="w-3.5 h-3.5" />
          </span>
        )}

        {/* Confirmation badge — hide in Urbanride mode to save space */}
        {hasConfirmation && !selectionMode && (
          <span className={`hidden sm:flex flex-shrink-0 items-center gap-1 text-xs px-1.5 py-0.5 rounded
            bg-ui-green-bg text-ui-green-fg`}>
            <Check className="w-2.5 h-2.5" />
            Confirmed
          </span>
        )}

        {/* TBD badge (mobile) */}
        {isComponentTBD && !selectionMode && (
          <span className="sm:hidden flex-shrink-0 text-xs font-semibold text-ui-amber-fg">TBD</span>
        )}

        {/* Urbanride selection mode: Add / Added / N/A badge */}
        {selectionMode && isSelectable && (
          <span className={`flex-shrink-0 flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
            isSelected
              ? 'bg-ui-primary text-ui-on-primary'
              : 'bg-ui-hover text-ui-ink-2 hover:bg-ui-primary-hover hover:text-ui-on-primary'
          }`}>
            {isSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
            {isSelected ? 'Added' : 'Add'}
          </span>
        )}
        {selectionMode && !isSelectable && (
          <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded text-ui-faint`}>N/A</span>
        )}

        {/* Expand chevron — only in normal mode */}
        {!selectionMode && (
          <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform duration-200
            ${isExpanded ? 'rotate-90' : ''}
            text-ui-muted`} />
        )}
      </button>

      {/* ── Expanded full card (not shown in Urbanride mode) ─────────────── */}
      {isExpanded && !selectionMode && (
        <div
          ref={contentRef}
          className={`border-l-2 ml-3 pl-3 pb-3 pt-1
            border-ui-dash`}
        >
          <TimelineComponentCard
            component={component}
            typeStyle={typeStyle || { backgroundColor: 'rgb(var(--ui-hover))', borderColor: cfg.solidColor }}
            formatDateTime={formatDateTime}
            getStatusColor={getStatusColor}
            getComponentTypeName={getComponentTypeName}
            onEdit={onEditComponent}
            onDelete={onDeleteComponent}
            onReassign={onReassignComponent}
            allTrips={allTrips}
            currentTripId={currentTripId}
            viewMode="full"
            trip={trip}
            isHidden={false}
            onToggleHide={() => {}}
            insideNotes={insideNotes}
            onToggleNoteExpand={onToggleNoteExpand}
            onEditNote={onEditNote}
            onDeleteNote={onDeleteNote}
            onNotePositionChange={onNotePositionChange}
            onNoteMoveToComponent={onNoteMoveToComponent}
            currentUserId={currentUserId}
            isConfidential={isConfidential}
            canToggleConfidentiality={canToggleConfidentiality}
            onToggleConfidentiality={onToggleConfidentiality}
            onOpenConfidentialityModal={onOpenConfidentialityModal}
            onRevealConfidential={onRevealConfidential}
            onConcealConfidential={onConcealConfidential}
            isRevealingId={isRevealingId}
            isConcealingId={isConcealingId}
            onQuickEdit={onQuickEdit}
            onAddVipServiceForLeg={onAddVipServiceForLeg}
            onInlineUpdateComponent={onInlineUpdateComponent}
          />
        </div>
      )}
    </div>

      {/* ── People hover tooltip ─────────────────────────────────────────── */}
      {isHovered && people.length > 0 && (
        <div className={`absolute left-8 bottom-full mb-1.5 z-50 min-w-[160px] max-w-[260px] rounded-lg shadow-xl border px-3 py-2 pointer-events-none bg-ui-raised border-ui-control text-ui-ink`}>
          <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1.5 text-ui-muted`}>
            Guests / Passengers
          </p>
          {people.map((name, i) => (
            <p key={i} className="text-xs leading-5 truncate">{name}</p>
          ))}
          {/* Arrow pointing down */}
          <span className={`absolute left-6 top-full w-0 h-0`}
            style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
              borderTop: '5px solid rgb(var(--ui-control))' }} />
        </div>
      )}
    </div>
  );
};

// Draggable Timeline Card Component with enhanced visual indicators
const DraggableTimelineCard = ({
  component,
  onEditComponent,
  onDeleteComponent,
  onReassignComponent, // New: callback for moving component to another trip
  allTrips = [], // New: list of all trips for reassign dropdown
  currentTripId, // New: current trip ID to exclude from dropdown
  isDraggable = false,
  formatDateTime,
  getStatusColor,
  getComponentTypeName,
  componentTypeStyles,
  viewMode = 'full', // Add viewMode prop
  trip, // Add trip prop
  // Selection mode props
  selectionMode = false,
  isSelected = false,
  onToggleSelect = () => {},
  isSelectable = false,
  // Hide component props
  isHidden = false,
  onToggleHide = () => {},
  // Sticky notes inside this component
  insideNotes = [],
  onToggleNoteExpand,
  onEditNote,
  onDeleteNote,
  onNotePositionChange,
  onNoteMoveToComponent,
  currentUserId,
  // Component confidentiality props
  isConfidential = false,
  canToggleConfidentiality = false,
  onToggleConfidentiality,
  onOpenConfidentialityModal,
  // Confidential placeholder reveal/conceal handling
  onRevealConfidential,
  onConcealConfidential,
  isRevealingId = null,
  isConcealingId = null,
  onQuickEdit, // Quick Edit callback for chauffeur_service components
  onAddVipServiceForLeg, // Callback to open Add Component modal pre-filled for an Airport VIP greet
  onInlineUpdateComponent, // Inline update callback for toggling anchor etc.
  designedCard // Full view: props to render the item as its designed card
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: component.id,
    disabled: !isDraggable
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get type style for this component
  const typeStyle = componentTypeStyles[component.component_type] || {
    backgroundColor: 'rgb(var(--ui-hover))',
    borderColor: 'rgb(var(--ui-control))'
  };

  const isComponentTBD = isTBDComponent(component);

  // Speaker portal accounts: TBD/FIXED are planner scheduling concepts —
  // hide the badges in their view-only timeline
  const { user: cardViewer } = useAuth();
  const viewerIsSpeaker = cardViewer?.role === 'speaker';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable && !selectionMode ? listeners : {})}
      className={`relative ${isDraggable && !selectionMode ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}
        transition-all duration-200 ${isDragging ? 'z-50' : ''}
        ${viewMode === 'cards' ? 'max-w-2xl' : ''}`}
      onClick={selectionMode && isSelectable ? () => onToggleSelect(component) : undefined}
    >
      {/* Add to Itinerary button for Urbanride mode - positioned at top right of card */}
      {/* Always shows "Add to Itinerary" to allow adding same location multiple times */}
      {selectionMode && isSelectable && (
        <div className="absolute -top-2 -right-1 sm:-right-2 z-30">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(component);
            }}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-1.5 rounded-full text-xs font-medium transition-all shadow-md bg-ui-primary text-ui-on-primary hover:bg-ui-primary-hover active:bg-ui-primary-hover active:scale-[0.98] touch-target"
          >
            <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Add to Itinerary</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      )}

      {/* Not eligible indicator in selection mode - show muted badge */}
      {selectionMode && !isSelectable && (
        <div className="absolute -top-2 -right-1 sm:-right-2 z-30">
          <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-ui-hover text-ui-muted">
            <span className="hidden sm:inline">Not available</span>
            <span className="sm:hidden">N/A</span>
          </div>
        </div>
      )}

      {/* Drag indicator for draggable items (hidden in selection mode) */}
      {/* On mobile, the handle sits flush at -left-2 to fit within parent padding (px-3) */}
      {isDraggable && !selectionMode && (
        <div className="absolute -left-2 sm:-left-6 top-1/2 transform -translate-y-1/2 z-10">
          <div className={`flex flex-col space-y-0.5 sm:space-y-1 p-1 sm:p-2 rounded-md sm:rounded-lg shadow-sm border bg-ui-raised border-ui-control`}>
            <div className={`w-1 h-1 rounded-full bg-ui-muted`}></div>
            <div className={`w-1 h-1 rounded-full bg-ui-muted`}></div>
            <div className={`w-1 h-1 rounded-full bg-ui-muted`}></div>
          </div>
        </div>
      )}

      {/* Enhanced TBD badge - position adjusted when in selection mode to avoid overlap with Add button */}
      {isComponentTBD && !viewerIsSpeaker && (
        <div className={`absolute z-20 ${selectionMode ? '-top-2 left-1 sm:left-2' : '-top-2 -right-1 sm:-right-2'}`}>
          <div className="bg-ui-orange-strong text-ui-on-primary text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-semibold shadow-lg border-2 border-ui-surface flex items-center gap-0.5 sm:gap-1">
            <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            <span className="text-[10px] sm:text-xs">TBD</span>
          </div>
        </div>
      )}

      {/* Fixed item indicator - position adjusted when in selection mode to avoid overlap with Add button */}
      {!isComponentTBD && component.start_datetime && !viewerIsSpeaker && (
        <div className={`absolute z-20 ${selectionMode ? '-top-2 left-1 sm:left-2' : '-top-2 -right-1 sm:-right-2'}`}>
          <div className="bg-ui-faint text-ui-on-primary text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-semibold shadow-lg border-2 border-ui-surface flex items-center gap-0.5 sm:gap-1">
            <CalendarIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            <span className="text-[10px] sm:text-xs">FIXED</span>
          </div>
        </div>
      )}

      {/* Selection order badge - removed since we now have the "Added" button at top right */}

      <div>
        <TimelineComponentCard
          component={component}
          typeStyle={typeStyle}
          formatDateTime={formatDateTime}
          getStatusColor={getStatusColor}
          getComponentTypeName={getComponentTypeName}
          onEdit={selectionMode ? undefined : onEditComponent}
          onDelete={selectionMode ? undefined : onDeleteComponent}
          onReassign={selectionMode ? undefined : onReassignComponent}
          allTrips={allTrips}
          currentTripId={currentTripId}
          viewMode={viewMode}
          trip={trip}
          isHidden={isHidden}
          onToggleHide={onToggleHide}
          insideNotes={insideNotes}
          onToggleNoteExpand={onToggleNoteExpand}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          onNotePositionChange={onNotePositionChange}
          onNoteMoveToComponent={onNoteMoveToComponent}
          currentUserId={currentUserId}
          isConfidential={isConfidential}
          canToggleConfidentiality={canToggleConfidentiality}
          onToggleConfidentiality={onToggleConfidentiality}
          onOpenConfidentialityModal={onOpenConfidentialityModal}
          onRevealConfidential={onRevealConfidential}
          onConcealConfidential={onConcealConfidential}
          isRevealingId={isRevealingId}
          isConcealingId={isConcealingId}
          onQuickEdit={onQuickEdit}
          onAddVipServiceForLeg={onAddVipServiceForLeg}
          onInlineUpdateComponent={onInlineUpdateComponent}
          designedCard={selectionMode ? undefined : designedCard}
        />
      </div>
    </div>
  );
};

// View Toggle Component for Timeline 2.0
const Timeline2ViewToggle = ({ viewMode, onToggle }) => (
  <div className="flex items-center gap-2 sm:gap-3">
    <div className="hidden sm:flex items-center gap-2 text-sm text-ui-ink-3">
      <span>View:</span>
    </div>

    <div className="flex bg-ui-surface rounded-lg border border-ui-line p-0.5 sm:p-1">
      <Button
        variant={viewMode === 'summary' ? "default" : "ghost"}
        size="sm"
        onClick={() => onToggle('summary')}
        className={`px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-all touch-target active:scale-[0.98] ${
          viewMode === 'summary' ? 'bg-ui-violet-bg text-ui-violet-fg hover:bg-ui-violet-bg shadow-sm' : 'text-ui-ink-3 hover:bg-ui-hover'
        }`}
      >
        <List className="w-3.5 h-3.5 sm:w-3 sm:h-3 sm:mr-1" />
        <span className="hidden sm:inline">Summary</span>
      </Button>

      <Button
        variant={viewMode === 'compact' ? "default" : "ghost"}
        size="sm"
        onClick={() => onToggle('compact')}
        className={`px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-all touch-target active:scale-[0.98] ${
          viewMode === 'compact' ? 'bg-ui-violet-bg text-ui-violet-fg hover:bg-ui-violet-bg shadow-sm' : 'text-ui-ink-3 hover:bg-ui-hover'
        }`}
        title="Compact — one row per item, click to expand"
      >
        <LayoutList className="w-3.5 h-3.5 sm:w-3 sm:h-3 sm:mr-1" />
        <span className="hidden sm:inline">Compact</span>
      </Button>

      <Button
        variant={viewMode === 'cards' ? "default" : "ghost"}
        size="sm"
        onClick={() => onToggle('cards')}
        className={`px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-all touch-target active:scale-[0.98] ${
          viewMode === 'cards' ? 'bg-ui-violet-bg text-ui-violet-fg hover:bg-ui-violet-bg shadow-sm' : 'text-ui-ink-3 hover:bg-ui-hover'
        }`}
        title="Cards — flight legs rendered like the executive dashboard timeline"
      >
        <Plane className="w-3.5 h-3.5 sm:w-3 sm:h-3 sm:mr-1" />
        <span className="hidden sm:inline">Cards</span>
      </Button>

      <Button
        variant={viewMode === 'full' ? "default" : "ghost"}
        size="sm"
        onClick={() => onToggle('full')}
        className={`px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-all touch-target active:scale-[0.98] ${
          viewMode === 'full' ? 'bg-ui-violet-bg text-ui-violet-fg hover:bg-ui-violet-bg shadow-sm' : 'text-ui-ink-3 hover:bg-ui-hover'
        }`}
      >
        <LayoutGrid className="w-3.5 h-3.5 sm:w-3 sm:h-3 sm:mr-1" />
        <span className="hidden sm:inline">Full</span>
      </Button>
    </div>
  </div>
);

// Airport code to name mapping for common airports
const AIRPORT_NAMES = {
  'TLV': 'Ben Gurion Airport',
  'DXB': 'Dubai International Airport',
  'CDG': 'Charles de Gaulle Airport',
  'JFK': 'John F. Kennedy International Airport',
  'LAX': 'Los Angeles International Airport',
  'LHR': 'London Heathrow Airport',
  'FRA': 'Frankfurt Airport',
  'AMS': 'Amsterdam Airport Schiphol',
  'ORD': 'Chicago O\'Hare International Airport',
  'ATL': 'Hartsfield-Jackson Atlanta International Airport',
  'SFO': 'San Francisco International Airport',
  'LAS': 'McCarran International Airport',
  'MIA': 'Miami International Airport',
  'BOS': 'Logan International Airport',
  'SEA': 'Seattle-Tacoma International Airport',
  'DEN': 'Denver International Airport',
  'PHX': 'Phoenix Sky Harbor International Airport',
  'DFW': 'Dallas/Fort Worth International Airport',
  'IAH': 'George Bush Intercontinental Airport',
  'EWR': 'Newark Liberty International Airport',
  'LGA': 'LaGuardia Airport',
  'BWI': 'Baltimore/Washington International Airport',
  'DCA': 'Ronald Reagan Washington National Airport',
  'IAD': 'Washington Dulles International Airport'
};

// Custom collision detection that prioritizes timeline-start and timeline-end drop zones
const customCollisionDetection = (args) => {
  // First, check for pointer within special drop zones (timeline-start, timeline-end)
  const pointerCollisions = pointerWithin(args);

  // Filter for our special drop zones
  const specialZones = pointerCollisions.filter(({ id }) =>
    id === 'timeline-start' || id === 'timeline-end'
  );

  // If we found a special zone, prioritize it
  if (specialZones.length > 0) {
    return specialZones;
  }

  // Check for pointer within drop-after zones using pointerWithin first
  const dropAfterZones = pointerCollisions.filter(({ id }) =>
    typeof id === 'string' && id.startsWith('drop-after-')
  );

  if (dropAfterZones.length > 0) {
    return dropAfterZones;
  }

  // Also check rectIntersection for drop-after zones (more forgiving with smaller targets)
  const rectCollisions = rectIntersection(args);
  const dropAfterRectZones = rectCollisions.filter(({ id }) =>
    typeof id === 'string' && id.startsWith('drop-after-')
  );

  if (dropAfterRectZones.length > 0) {
    return dropAfterRectZones;
  }

  // Fall back to closest center for component cards
  const centerCollisions = closestCenter(args);
  return centerCollisions;
};

// Local helpers to avoid undefined props
const defaultGetStatusColor = (status) => {
  const s = String(status || 'pending').toLowerCase();
  if (s.includes('confirm')) return 'text-ui-green-fg bg-ui-green-bg';
  if (s.includes('cancel')) return 'text-ui-red-fg bg-ui-red-bg';
  if (s.includes('progress')) return 'text-ui-amber-fg bg-ui-amber-bg';
  return 'text-ui-ink-2 bg-ui-hover';
};
const defaultGetComponentTypeName = (t) => ({
  hotel: 'Hotel',
  meeting: 'Meeting',
  speaker_meeting: 'Speaker Meeting',
  restaurant: 'Restaurant',
  private_flight: 'Private Flight',
  commercial_flight: 'Commercial Flight',
  rental_car: 'Rental Car',
  chauffeur_service: 'Chauffeur Service',
  airport_vip_service: 'Airport VIP Service',
  memo: 'Memo',
  bundle: 'Bundle',
  general: 'General'
}[t] || (t || 'Component'));

const Timeline2 = ({
  components,
  trip,
  executives = [],
  allExecutives = [],
  onEditComponent = () => {},
  onDeleteComponent = () => {},
  onReassignComponent, // New: callback for moving component to another trip
  onAddVipServiceForLeg, // Open Add Component flow pre-filled for an Airport VIP greet on a flight leg
  allTrips = [], // New: list of all trips for reassign dropdown
  onRefreshComponents = () => {},
  formatDateTime,
  getStatusColor = defaultGetStatusColor,
  getComponentTypeName = defaultGetComponentTypeName,
  componentTypeStyles = {
    hotel: { backgroundColor: 'rgb(var(--ui-indigo-bg))', borderColor: 'rgb(var(--ui-indigo-line))' },
    meeting: { backgroundColor: 'rgb(var(--ui-green-bg))', borderColor: 'rgb(var(--ui-green-line))' },
    speaker_meeting: { backgroundColor: 'rgb(var(--ui-red-bg))', borderColor: 'rgb(var(--ui-red-line))' },
    restaurant: { backgroundColor: 'rgb(var(--ui-orange-bg))', borderColor: 'rgb(var(--ui-orange-line))' },
    private_flight: { backgroundColor: 'rgb(var(--ui-violet-bg))', borderColor: 'rgb(var(--ui-violet-line))' },
    commercial_flight: { backgroundColor: 'rgb(var(--ui-blue-bg))', borderColor: 'rgb(var(--ui-blue-line))' },
    rental_car: { backgroundColor: 'rgb(var(--ui-green-bg))', borderColor: 'rgb(var(--ui-green-line))' },
    memo: { backgroundColor: 'rgb(var(--ui-amber-bg))', borderColor: 'rgb(var(--ui-amber-line))' },
    bundle: { backgroundColor: 'rgb(var(--ui-violet-bg))', borderColor: 'rgb(var(--ui-violet-line))' },
    general: { backgroundColor: 'rgb(var(--ui-sunk))', borderColor: 'rgb(var(--ui-line))' }
  },
  // New props for external control (compact header mode)
  hideHeader = false,
  externalUrbanrideMode,
  onUrbanrideModeChange,
  externalViewMode,
  onExternalViewModeChange,
  onAddNoteClick,
  triggerAddNote = 0, // Increment to trigger note creation from parent
  // PDF Export props for external control
  externalPdfExportMode,
  onPdfExportModeChange,
  externalPdfSelectedItems,
  onPdfSelectedItemsChange,
  triggerPdfExport = 0, // Increment to trigger PDF generation from parent
  triggerPdfSelectAll = 0, // Increment to trigger select all from parent
  pdfFormat = 'executive', // 'executive' | 'simple'
  onInlineUpdateComponent, // Inline update callback (e.g. toggling anchor_to_arrival on a leg)
  readOnly = false // View-only mode (speaker portal): no drag, no confidentiality controls
}) => {
  // Get user context for preferences
  const { user, setUser } = useAuth();
  // Speaker portal accounts are always read-only, even if the parent forgot the prop
  const isReadOnly = readOnly || user?.role === 'speaker';

  // Urbanride selection mode state - support external control
  const [internalUrbanrideMode, setInternalUrbanrideMode] = useState(false);
  const urbanrideSelectionMode = externalUrbanrideMode !== undefined ? externalUrbanrideMode : internalUrbanrideMode;
  const setUrbanrideSelectionMode = onUrbanrideModeChange || setInternalUrbanrideMode;
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [showUrbanrideBooking, setShowUrbanrideBooking] = useState(false);
  // Quick Edit state - tracks the component being edited via Quick Edit
  const [quickEditComponent, setQuickEditComponent] = useState(null);
  const [quickEditIncomingComponent, setQuickEditIncomingComponent] = useState(null);

  // Bundle selection mode state
  const [bundleSelectionMode, setBundleSelectionMode] = useState(null); // null or bundle component
  const [bundleSelectedIds, setBundleSelectedIds] = useState(new Set());
  const [bundleOriginalIds, setBundleOriginalIds] = useState(new Set());
  const [bundleSaving, setBundleSaving] = useState(false);

  // Sticky notes state
  const [stickyNotes, setStickyNotes] = useState([]);
  const [showStickyNoteModal, setShowStickyNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // Confidentiality modal state
  const [showConfidentialityModal, setShowConfidentialityModal] = useState(false);
  const [confidentialityTarget, setConfidentialityTarget] = useState(null);

  // Confidential placeholder reveal/conceal state
  const [revealingComponentId, setRevealingComponentId] = useState(null);
  const [concealingComponentId, setConcealingComponentId] = useState(null);

  // PDF export mode state - support external control
  const [internalPdfExportMode, setInternalPdfExportMode] = useState(false);
  const [internalPdfSelectedItems, setInternalPdfSelectedItems] = useState(new Set());
  const pdfExportMode = externalPdfExportMode !== undefined ? externalPdfExportMode : internalPdfExportMode;
  const setPdfExportMode = onPdfExportModeChange || setInternalPdfExportMode;
  const pdfSelectedItems = externalPdfSelectedItems !== undefined ? externalPdfSelectedItems : internalPdfSelectedItems;
  const setPdfSelectedItems = onPdfSelectedItemsChange || setInternalPdfSelectedItems;

  // State for drag-to-place new note
  const [isPlacingNote, setIsPlacingNote] = useState(false);
  const [placingNotePosition, setPlacingNotePosition] = useState({ x: 0, y: 0 });
  const [pendingNotePosition, setPendingNotePosition] = useState(null); // Position to use when modal opens
  const timelineContainerRef = useRef(null);

  // Quick add component state - for inserting TBD components between existing ones
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddAnchor, setQuickAddAnchor] = useState(null); // { beforeComponentId, afterComponentId } or { position: 'start'|'end' }
  const [isCreatingComponent, setIsCreatingComponent] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const quickAddTitleInputRef = useRef(null);
  const [availableComponentTypes, setAvailableComponentTypes] = useState([]);
  const [urbanrideQuickbookEnabled, setUrbanrideQuickbookEnabled] = useState(false);

  // ── Bundle expand/collapse state ─────────────────────────────────────────
  // Bundles are collapsed by default — children hidden from timeline.
  // When expanded, a sub-timeline of children appears below the bundle card.
  const [expandedBundleIds, setExpandedBundleIds] = useState(new Set());

  const toggleBundleExpand = useCallback((bundleId) => {
    setExpandedBundleIds(prev => {
      const next = new Set(prev);
      next.has(bundleId) ? next.delete(bundleId) : next.add(bundleId);
      return next;
    });
  }, []);

  // ── Compact view state ──────────────────────────────────────────────────────
  const [compactExpandedIds, setCompactExpandedIds] = useState(new Set());
  const compactRowRefs = useRef({});

  const toggleCompactExpanded = useCallback((id) => {
    setCompactExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // ── Timeline Filters ────────────────────────────────────────────────────────
  // typeFilters: Set of component_type strings that are EXCLUDED (hidden)
  const [typeFilters, setTypeFilters] = useState(new Set());
  const [showTypeFilterDropdown, setShowTypeFilterDropdown] = useState(false);
  // dateFilter: 'all' | 'today' | 'tomorrow' | 'upcoming' | 'range'
  // (semantics shared with the exec dashboard via utils/timelineFilters)
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFilterRange, setDateFilterRange] = useState({ start: '', end: '' });
  const [showDateFilterDropdown, setShowDateFilterDropdown] = useState(false);
  const typeFilterRef = useRef(null);
  const dateFilterRef = useRef(null);

  // Fetch available component types and feature flags for the organization
  useEffect(() => {
    const fetchAvailableComponents = async () => {
      try {
        const { getApiBase, getAuthHeaders } = await import('../../utils/api');
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const response = await axios.get(`${apiBase}/organizations/available-components`, { headers });
        setAvailableComponentTypes(response.data.available_components || []);
      } catch (err) {
        console.error('Error fetching available components:', err);
        // Fallback to all component types if API fails
        setAvailableComponentTypes([
          'commercial_flight', 'private_flight', 'hotel', 'restaurant',
          'meeting', 'event', 'rental_car', 'chauffeur_service',
          'airport_vip_service', 'memo'
        ]);
      }
    };

    const fetchUrbanrideFeature = async () => {
      try {
        const { getApiBase, getAuthHeaders } = await import('../../utils/api');
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const response = await axios.get(`${apiBase}/organizations/feature-check/urbanride_quickbook`, { headers });
        setUrbanrideQuickbookEnabled(response.data.enabled || false);
      } catch (err) {
        console.error('Error checking urbanride_quickbook feature:', err);
        setUrbanrideQuickbookEnabled(false);
      }
    };

    fetchAvailableComponents();
    fetchUrbanrideFeature();
  }, []);

  // Autofocus title input when quick add modal opens
  useEffect(() => {
    if (showQuickAddModal && quickAddTitleInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        quickAddTitleInputRef.current?.focus();
      }, 100);
    }
  }, [showQuickAddModal]);

  // Fetch sticky notes when trip changes
  useEffect(() => {
    const fetchStickyNotes = async () => {
      if (!trip?.id) return;
      try {
        const notes = await listStickyNotes(trip.id);
        setStickyNotes(notes);
      } catch (err) {
        console.error('Failed to fetch sticky notes:', err);
      }
    };
    fetchStickyNotes();
  }, [trip?.id]);

  // Handle SSE events for sticky notes (real-time updates)
  const handleStickyNoteEvent = useCallback((event) => {
    const eventType = event.type;
    const noteData = event.data?.sticky_note;

    // Only process events for this trip
    if (event.trip_id !== trip?.id) return;

    if (eventType === SSEEventType.STICKY_NOTE_CREATED && noteData) {
      setStickyNotes((prev) => {
        // Don't add if already exists
        if (prev.some((n) => n.id === noteData.id)) return prev;
        return [...prev, noteData];
      });
    } else if (eventType === SSEEventType.STICKY_NOTE_UPDATED && noteData) {
      setStickyNotes((prev) =>
        prev.map((n) => (n.id === noteData.id ? { ...n, ...noteData } : n))
      );
    } else if (eventType === SSEEventType.STICKY_NOTE_DELETED && noteData) {
      setStickyNotes((prev) => prev.filter((n) => n.id !== noteData.id));
    }
  }, [trip?.id]);

  useStickyNoteEvents(handleStickyNoteEvent, [trip?.id]);

  // Handle SSE events for trip components (real-time updates)
  const handleComponentEvent = useCallback((event) => {
    // Only process events for this trip
    if (event.trip_id !== trip?.id) return;

    // Notify parent to refresh trip data
    if (onRefreshComponents) {
      onRefreshComponents();
    }
  }, [trip?.id, onRefreshComponents]);

  useComponentEvents(handleComponentEvent, [trip?.id, onRefreshComponents]);

  // Handle SSE events for flight updates (real-time FlightAware updates)
  const handleFlightUpdateEvent = useCallback((event) => {
    // Check if this flight update is for a component in this trip
    if (event.trip_id && event.trip_id !== trip?.id) return;

    // Refresh components to get updated flight data (gates, times, delays, status)
    if (onRefreshComponents) {
      console.log('[Timeline2] Flight update event received, refreshing components:', event.type);
      onRefreshComponents();
    }
  }, [trip?.id, onRefreshComponents]);

  useFlightUpdateEvents(handleFlightUpdateEvent, [trip?.id, onRefreshComponents]);

  // Handle creating a new sticky note - start placement mode
  const handleCreateNote = () => {
    // If external handler is provided (compact header mode), use it
    if (onAddNoteClick) {
      onAddNoteClick();
      return;
    }
    setIsPlacingNote(true);
    setPendingNotePosition(null);
  };

  // Watch for triggerAddNote prop changes from compact header
  useEffect(() => {
    if (triggerAddNote > 0) {
      setIsPlacingNote(true);
      setPendingNotePosition(null);
    }
  }, [triggerAddNote]);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showTypeFilterDropdown && typeFilterRef.current && !typeFilterRef.current.contains(e.target)) {
        setShowTypeFilterDropdown(false);
      }
      if (showDateFilterDropdown && dateFilterRef.current && !dateFilterRef.current.contains(e.target)) {
        setShowDateFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTypeFilterDropdown, showDateFilterDropdown]);

  // Handle mouse move during note placement
  const handlePlacingNoteMouseMove = useCallback((e) => {
    if (!isPlacingNote) return;
    setPlacingNotePosition({ x: e.clientX, y: e.clientY });
  }, [isPlacingNote]);

  // Handle mouse up - finish placement and open modal
  const handlePlacingNoteMouseUp = useCallback((e) => {
    if (!isPlacingNote) return;

    setIsPlacingNote(false);

    // Find which component we're over (if any)
    const elementsAtPoint = document.elementsFromPoint(e.clientX, e.clientY);

    // Look for a timeline component card
    let targetComponentId = null;
    let offsetX = 5;
    let offsetY = 5;

    for (const el of elementsAtPoint) {
      // Check if this element or its parents have a data-component-id attribute
      const componentCard = el.closest('[data-component-id]');
      if (componentCard) {
        targetComponentId = componentCard.getAttribute('data-component-id');

        // Calculate offset position within the component
        const cardRect = componentCard.getBoundingClientRect();
        const relativeX = e.clientX - cardRect.left;
        const relativeY = e.clientY - cardRect.top;
        offsetX = Math.max(0, Math.min(90, (relativeX / cardRect.width) * 100));
        offsetY = Math.max(0, Math.min(90, (relativeY / cardRect.height) * 100));
        break;
      }
    }

    // Set the position for the new note
    if (targetComponentId) {
      setPendingNotePosition({
        position_type: 'inside',
        attached_to_component_id: targetComponentId,
        offset_x: Math.round(offsetX),
        offset_y: Math.round(offsetY),
        rank: 0
      });
    } else {
      // Default to end of timeline if not dropped on a component
      setPendingNotePosition({
        position_type: 'end',
        rank: 0
      });
    }

    // Open the modal
    setEditingNote(null);
    setShowStickyNoteModal(true);
  }, [isPlacingNote]);

  // Cancel placement with Escape key
  const handlePlacingNoteKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isPlacingNote) {
      setIsPlacingNote(false);
    }
  }, [isPlacingNote]);

  // Attach/detach global event listeners for note placement
  useEffect(() => {
    if (isPlacingNote) {
      document.addEventListener('mousemove', handlePlacingNoteMouseMove);
      document.addEventListener('mouseup', handlePlacingNoteMouseUp);
      document.addEventListener('keydown', handlePlacingNoteKeyDown);

      return () => {
        document.removeEventListener('mousemove', handlePlacingNoteMouseMove);
        document.removeEventListener('mouseup', handlePlacingNoteMouseUp);
        document.removeEventListener('keydown', handlePlacingNoteKeyDown);
      };
    }
  }, [isPlacingNote, handlePlacingNoteMouseMove, handlePlacingNoteMouseUp, handlePlacingNoteKeyDown]);

  // Handle editing a sticky note
  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowStickyNoteModal(true);
  };

  // Handle saving a sticky note (create or update)
  const handleSaveNote = async (noteData, noteId) => {
    try {
      if (noteId) {
        // Update existing note
        const updated = await updateStickyNote(noteId, noteData);
        setStickyNotes(prev => prev.map(n => n.id === noteId ? updated : n));
      } else {
        // Create new note
        const created = await createStickyNote(trip.id, noteData);
        setStickyNotes(prev => [...prev, created]);
      }
      setShowStickyNoteModal(false);
      setEditingNote(null);
    } catch (err) {
      console.error('Failed to save sticky note:', err);
      throw err; // Re-throw to let modal show error
    }
  };

  // Handle deleting a sticky note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      await deleteStickyNote(noteId);
      setStickyNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Failed to delete sticky note:', err);
    }
  };

  // Handle toggling note expansion
  const handleToggleNoteExpand = async (noteId, isExpanded) => {
    try {
      const updated = await toggleNoteExpanded(noteId, isExpanded);
      setStickyNotes(prev => prev.map(n => n.id === noteId ? updated : n));
    } catch (err) {
      console.error('Failed to toggle note expansion:', err);
    }
  };

  // Handle repositioning a sticky note via drag-and-drop
  const handleRepositionNote = async (noteId, newPosition) => {
    try {
      const updated = await updateNotePosition(noteId, newPosition);
      setStickyNotes(prev => prev.map(n => n.id === noteId ? updated : n));
    } catch (err) {
      console.error('Failed to reposition sticky note:', err);
    }
  };

  // Handle floating note position change (within component)
  const handleFloatingNotePositionChange = async (noteId, offsetX, offsetY) => {
    try {
      // Find the current note to preserve other position fields
      const currentNote = stickyNotes.find(n => n.id === noteId);
      if (!currentNote) return;

      const newPosition = {
        ...currentNote.position,
        offset_x: offsetX,
        offset_y: offsetY
      };

      const updated = await updateNotePosition(noteId, newPosition);
      setStickyNotes(prev => prev.map(n => n.id === noteId ? updated : n));
    } catch (err) {
      console.error('Failed to update floating note position:', err);
    }
  };

  // Handle moving a floating note to a different component
  const handleMoveNoteToComponent = async (noteId, targetComponentId, offsetX, offsetY) => {
    try {
      const newPosition = {
        position_type: 'inside',
        attached_to_component_id: targetComponentId,
        offset_x: offsetX,
        offset_y: offsetY,
        rank: 0
      };

      const updated = await updateNotePosition(noteId, newPosition);
      setStickyNotes(prev => prev.map(n => n.id === noteId ? updated : n));
    } catch (err) {
      console.error('Failed to move note to component:', err);
    }
  };

  // PDF Export Mode handlers
  const handleEnterPdfExportMode = useCallback(() => {
    setPdfExportMode(true);
    // Select all items by default when entering PDF mode
    // We'll populate this after allComponents is available
  }, []);

  const handleExitPdfExportMode = useCallback(() => {
    setPdfExportMode(false);
    setPdfSelectedItems(new Set());
  }, []);

  const togglePdfItemSelection = useCallback((componentId) => {
    setPdfSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(componentId)) {
        newSet.delete(componentId);
      } else {
        newSet.add(componentId);
      }
      return newSet;
    });
  }, []);

  const selectAllPdfItems = useCallback((componentIds) => {
    setPdfSelectedItems(new Set(componentIds));
  }, []);

  const deselectAllPdfItems = useCallback(() => {
    setPdfSelectedItems(new Set());
  }, []);

  // ============ PDF GENERATION FUNCTIONS ============

  // Shared PDF helpers
  const createPdfHelpers = (doc, margin) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    const checkPageBreak = (neededHeight = 30) => {
      if (yPosition + neededHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    const getY = () => yPosition;
    const setY = (y) => { yPosition = y; };
    const addY = (delta) => { yPosition += delta; };

    // Stored datetimes are wall-clock at the event's location; the backend may
    // append the location's UTC offset (e.g. hotel check_in/check_out). Parse
    // the date/time components straight from the string (parseLocalDate) so the
    // PDF shows the same time as the timeline — new Date() would re-interpret
    // the offset and shift everything into the viewer's browser timezone.
    const formatDateTime = (dateTimeStr) => {
      if (!dateTimeStr) return 'TBD';
      try {
        const date = parseLocalDate(dateTimeStr);
        if (!date) return dateTimeStr;
        return format(date, 'MMM d, yyyy h:mm a');
      } catch {
        return dateTimeStr;
      }
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return 'TBD';
      try {
        const date = parseLocalDate(dateStr);
        if (!date) return dateStr;
        return format(date, 'MMM d, yyyy');
      } catch {
        return dateStr;
      }
    };

    const formatTime = (dateTimeStr) => {
      if (!dateTimeStr) return '';
      try {
        // Time must come from the string itself, not a Date round-trip
        if (!/T\d{2}:\d{2}/.test(String(dateTimeStr))) return '';
        const date = parseLocalDate(dateTimeStr);
        if (!date) return '';
        return format(date, 'h:mm a');
      } catch {
        return '';
      }
    };

    const formatDayHeader = (dateStr) => {
      if (!dateStr) return 'TBD';
      try {
        const date = parseLocalDate(dateStr);
        if (!date) return dateStr;
        return format(date, 'EEEE, MMMM d').toUpperCase();
      } catch {
        return dateStr;
      }
    };

    const getDateKey = (dateTimeStr) => {
      if (!dateTimeStr) return 'TBD';
      try {
        const date = parseLocalDate(dateTimeStr);
        if (!date) return 'TBD';
        return format(date, 'yyyy-MM-dd');
      } catch {
        return 'TBD';
      }
    };

    const addFooters = (tripTitle) => {
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 150, 150);
        doc.text(`Generated ${new Date().toISOString()} · Snapshot; check Veyage for updates`, margin, pageHeight - 10);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 10);
        doc.setTextColor(0, 0, 0);
      }
    };

    return {
      pageWidth, pageHeight, contentWidth,
      checkPageBreak, getY, setY, addY,
      formatDateTime, formatDate, formatTime, formatDayHeader, getDateKey,
      addFooters
    };
  };

  // Simple list format (original)
  const generateSimplePdf = useCallback((selectedItems, allComponentsList, tripData, notesList) => {
    const doc = new jsPDF();
    const margin = 20;
    const helpers = createPdfHelpers(doc, margin);
    const { pageWidth, contentWidth, checkPageBreak, getY, addY, formatDateTime, formatDate, addFooters } = helpers;

    let yPosition = margin;
    const setY = (y) => { yPosition = y; };

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    const tripTitle = tripData?.title || tripData?.name || 'Trip Timeline';
    doc.text(tripTitle, margin, yPosition);
    yPosition += 10;

    // Trip dates
    if (tripData?.start_date || tripData?.end_date) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${formatDate(tripData.start_date)} - ${formatDate(tripData.end_date)}`, margin, yPosition);
      yPosition += 8;
    }

    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    const selectedComponents = allComponentsList.filter(comp => selectedItems.has(comp.id));

    if (selectedComponents.length === 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'italic');
      doc.text('No items selected for export.', margin, yPosition);
    } else {
      const typeLabels = {
        commercial_flight: 'Flight', private_flight: 'Private Flight', hotel: 'Hotel',
        restaurant: 'Restaurant', meeting: 'Meeting', speaker_meeting: 'Speaker Meeting', event: 'Event',
        rental_car: 'Rental Car', chauffeur_service: 'Chauffeur',
        airport_vip_service: 'VIP Greet', memo: 'Memo'
      };

      selectedComponents.forEach((component, index) => {
        if (yPosition + 40 > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPosition = margin;
        }

        const componentType = component.originalComponent?.component_type || component.component_type;
        const typeLabel = typeLabels[componentType] || componentType || 'Item';

        doc.setFillColor(100, 100, 180);
        doc.roundedRect(margin, yPosition - 4, 50, 7, 1, 1, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(typeLabel.toUpperCase(), margin + 2, yPosition + 1);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(component.title || 'Untitled', margin, yPosition);
        yPosition += 6;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        if (component.isFlightLeg && component.flightLeg) {
          const lines = doc.splitTextToSize(flightExportDetails(component.flightLeg, user?.ui_preferences).join('\n'), contentWidth);
          doc.text(lines, margin, yPosition);
          yPosition += lines.length * 5;
        } else if (component.start_datetime) {
          doc.text(formatDateTime(component.start_datetime), margin, yPosition);
          yPosition += 5;
        }
        if (component.location) {
          const locLines = doc.splitTextToSize(component.location, contentWidth);
          doc.text(locLines, margin, yPosition);
          yPosition += locLines.length * 5;
        }

        if (index < selectedComponents.length - 1) {
          yPosition += 3;
          doc.setDrawColor(230, 230, 230);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 6;
        }
      });
    }

    addFooters(tripTitle);
    doc.save(`${tripTitle.replace(/[^a-zA-Z0-9]/g, '_')}_timeline.pdf`);
  }, []);

  // Executive / Speaker format with day-by-day layout.
  // options.variant: 'executive' (default) | 'speaker'. The speaker variant
  // leads the header with the speaker name + Program ID(s) instead of the trip
  // title/summary, and drops the per-component "Confirmation" line on speaker
  // meetings (the program id lives in the header instead).
  const generateExecutivePdf = useCallback((selectedItems, allComponentsList, tripData, notesList, options = {}) => {
    const { variant = 'executive', speakerName = null, programIds = [] } = options;
    const isSpeakerVariant = variant === 'speaker';
    const doc = new jsPDF();
    const margin = 15;
    const helpers = createPdfHelpers(doc, margin);
    const { pageWidth, pageHeight, contentWidth, formatDateTime, formatDate, formatTime, formatDayHeader, getDateKey, addFooters } = helpers;

    let yPosition = margin;

    const checkPageBreak = (neededHeight = 20) => {
      if (yPosition + neededHeight > pageHeight - margin - 15) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Truncate a string with an ellipsis so it renders on a single line within
    // maxWidth at the current font. Keeps each component headline to one line.
    const fitOneLine = (text, maxWidth) => {
      const str = String(text || '');
      if (!str) return '';
      if (doc.getTextWidth(str) <= maxWidth) return str;
      let t = str;
      while (t.length > 1 && doc.getTextWidth(t.replace(/\s+$/, '') + '…') > maxWidth) {
        t = t.slice(0, -1);
      }
      return t.replace(/\s+$/, '') + '…';
    };

    const tripTitle = tripData?.title || tripData?.name || 'Trip Itinerary';
    const selectedComponents = allComponentsList.filter(comp => selectedItems.has(comp.id));

    // Header title: speaker name for the speaker variant, trip title otherwise.
    const headerTitle = isSpeakerVariant ? (speakerName || tripTitle) : tripTitle;

    // ===== COVER / HEADER =====
    const headerHeight = isSpeakerVariant && programIds.length > 0 ? 50 : 45;
    doc.setFillColor(45, 55, 72); // Dark slate
    doc.rect(0, 0, pageWidth, headerHeight, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(headerTitle, margin, 22);

    const dateRange = `${formatDate(tripData?.start_date)} - ${formatDate(tripData?.end_date)}`;

    if (isSpeakerVariant) {
      // Program ID(s) directly under the speaker name.
      if (programIds.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        const idLabel = programIds.length > 1 ? 'Program IDs' : 'Program ID';
        doc.text(`${idLabel}: ${programIds.join(', ')}`, margin, 32);
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(dateRange, margin, programIds.length > 0 ? 41 : 32);
      yPosition = headerHeight + 10;
    } else {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(dateRange, margin, 32);

      // Trip summary counts
      const flightCount = selectedComponents.filter(c => ['commercial_flight', 'private_flight'].includes(c.originalComponent?.component_type || c.component_type) || c.isFlightLeg).length;
      const hotelCount = selectedComponents.filter(c => (c.originalComponent?.component_type || c.component_type) === 'hotel' || c.isHotelEvent).length;
      const meetingCount = selectedComponents.filter(c => (c.originalComponent?.component_type || c.component_type) === 'meeting').length;
      const otherCount = selectedComponents.length - flightCount - hotelCount - meetingCount;

      const summaryParts = [];
      if (flightCount > 0) summaryParts.push(`${flightCount} flight${flightCount > 1 ? 's' : ''}`);
      if (hotelCount > 0) summaryParts.push(`${hotelCount} hotel${hotelCount > 1 ? 's' : ''}`);
      if (meetingCount > 0) summaryParts.push(`${meetingCount} meeting${meetingCount > 1 ? 's' : ''}`);
      if (otherCount > 0) summaryParts.push(`${otherCount} other`);

      doc.setFontSize(9);
      doc.text(summaryParts.join('  |  '), margin, 40);
      yPosition = 55;
    }

    doc.setTextColor(0, 0, 0);

    // ===== QUICK REFERENCE BOX =====
    const confirmations = [];
    const seenConfirmations = new Set();
    selectedComponents.forEach(comp => {
      // Check all possible paths for confirmation number
      const compData = comp.component_data || comp.originalComponent?.component_data || {};
      const confNum = comp.confirmation_number ||
                      comp.originalComponent?.confirmation_number ||
                      compData.confirmation_number ||
                      comp.hotelData?.confirmation_number ||
                      comp.flightLeg?.confirmation_number;
      const compType = comp.originalComponent?.component_type || comp.component_type;
      const typeLabels = { commercial_flight: 'Flight', private_flight: 'Flight', hotel: 'Hotel', rental_car: 'Car', car_rental: 'Car', chauffeur_service: 'Car' };
      // Normalize confirmation number (trim whitespace) and skip if already seen
      const normalizedConfNum = confNum?.toString().trim();
      if (normalizedConfNum && typeLabels[compType] && !seenConfirmations.has(normalizedConfNum)) {
        seenConfirmations.add(normalizedConfNum);
        const provider = resolveProviderInfo(comp, compType);
        confirmations.push({
          type: typeLabels[compType],
          number: normalizedConfNum,
          providerName: provider.name,
          providerPhone: provider.phone,
        });
      }
    });

    if (confirmations.length > 0) {
      doc.setFillColor(249, 250, 251);
      doc.setDrawColor(229, 231, 235);
      doc.roundedRect(margin, yPosition, contentWidth, Math.min(confirmations.length * 6 + 14, 50), 3, 3, 'FD');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.text('QUICK REFERENCE', margin + 5, yPosition + 8);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(8);
      let refY = yPosition + 16;
      confirmations.slice(0, 5).forEach(conf => {
        // Format: "Flight: Southwest Airlines (1-800-435-9792) - BHNQUS"
        // Provider name and phone are included when known; each part is
        // omitted gracefully when unavailable.
        let line = `${conf.type}:`;
        if (conf.providerName) line += ` ${conf.providerName}`;
        if (conf.providerPhone) line += ` (${conf.providerPhone})`;
        if (conf.providerName || conf.providerPhone) line += ` -`;
        line += ` ${conf.number}`;
        doc.text(fitOneLine(line, contentWidth - 10), margin + 5, refY);
        refY += 6;
      });

      yPosition += Math.min(confirmations.length * 6 + 18, 54);
    }

    // ===== GROUP BY DATE =====
    const componentsByDate = {};
    selectedComponents.forEach(comp => {
      const dateKey = comp.isFlightLeg && comp.flightLeg ? flightSide(comp.flightLeg, 'departure').calendarDate || 'TBD' : getDateKey(comp.start_datetime);
      if (!componentsByDate[dateKey]) {
        componentsByDate[dateKey] = [];
      }
      componentsByDate[dateKey].push(comp);
    });

    // Sort dates
    const sortedDates = Object.keys(componentsByDate).sort((a, b) => {
      if (a === 'TBD') return 1;
      if (b === 'TBD') return -1;
      return a.localeCompare(b);
    });

    // Type emojis for PDF icons
    const typeEmojis = {
      commercial_flight: '✈️',
      private_flight: '🛩️',
      hotel: '🏨',
      restaurant: '🍽️',
      meeting: '👥',
      speaker_meeting: '🎤',
      event: '🎉',
      rental_car: '🚗',
      chauffeur_service: '🚘',
      airport_vip_service: '⭐',
      memo: '📝'
    };

    // Helper to render emoji to canvas and get image data
    const emojiToImage = (emoji, size = 32) => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.font = `${size - 4}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(emoji, size / 2, size / 2 + 2);
      return canvas.toDataURL('image/png');
    };

    // Pre-render all emoji images for better performance
    const emojiImages = {};
    Object.entries(typeEmojis).forEach(([type, emoji]) => {
      emojiImages[type] = emojiToImage(emoji);
    });

    // Helper to draw emoji icon in PDF
    const drawEmojiIcon = (x, y, type) => {
      const imgData = emojiImages[type];
      if (imgData) {
        // Add emoji as image (7x7mm size, positioned to align with text)
        doc.addImage(imgData, 'PNG', x, y - 5, 7, 7);
      }
    };

    // ===== RENDER EACH DAY =====
    sortedDates.forEach((dateKey, dateIndex) => {
      const dayComponents = componentsByDate[dateKey];

      // Day header - check for enough space for header (14) plus at least one component (35)
      // This prevents orphaned day headers at the bottom of a page
      checkPageBreak(50);

      doc.setFillColor(79, 70, 229); // Indigo
      doc.rect(margin, yPosition, contentWidth, 10, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const dayLabel = dateKey === 'TBD' ? 'PENDING' : formatDayHeader(dateKey);
      doc.text(dayLabel, margin + 4, yPosition + 7);

      doc.setTextColor(0, 0, 0);
      yPosition += 14;

      // Sort by time within the day (using local time values, not browser timezone)
      dayComponents.sort((a, b) => {
        const ta = parseDatetimeForSortValue(a.start_datetime);
        const tb = parseDatetimeForSortValue(b.start_datetime);
        if (ta === null) return 1;
        if (tb === null) return -1;
        return ta - tb;
      });

      // Render each component
      dayComponents.forEach((component, compIndex) => {
        checkPageBreak(35);

        const compType = component.originalComponent?.component_type || component.component_type;
        const time = component.isFlightLeg && component.flightLeg ? flightSide(component.flightLeg, 'departure', user?.ui_preferences).time : formatTime(component.start_datetime);
        const compData = component.component_data || component.originalComponent?.component_data || {};

        // Time column
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(79, 70, 229);
        doc.text(time || '--:--', margin + 2, yPosition);

        // Emoji icon (rendered as image)
        drawEmojiIcon(margin + 25, yPosition, compType);

        // Title / headline - always a single line. Flights use the concise
        // airport-code route (e.g. "LAS - LAX") as the headline, which also
        // removes the duplicate route line that used to sit above the
        // confirmation number; everything else truncates its title to fit.
        doc.setTextColor(17, 24, 39);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        let headlineText;
        if (component.isFlightLeg && component.flightLeg) {
          const leg = component.flightLeg;
          const depCode = leg.departure_airport || leg.departure_airport_code || '';
          const arrCode = leg.arrival_airport || leg.arrival_airport_code || '';
          headlineText = (depCode && arrCode) ? `${depCode} - ${arrCode}` : (component.title || 'Flight');
        } else {
          headlineText = component.title || 'Untitled';
        }
        doc.text(fitOneLine(headlineText, contentWidth - 45), margin + 38, yPosition);
        yPosition += 6;

        // Details based on type
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(75, 85, 99);

        // Flight details (route is already the headline, so it is not repeated)
        if (component.isFlightLeg && component.flightLeg) {
          const leg = component.flightLeg;
          const clockLines = doc.splitTextToSize(flightExportDetails(leg, user?.ui_preferences).join('\n'), contentWidth - 45);
          checkPageBreak(clockLines.length * 4 + 12);
          doc.text(clockLines, margin + 38, yPosition);
          yPosition += clockLines.length * 4;
          const flightInfo = [];
          if (leg.airline || leg.flight_number) flightInfo.push(`${leg.airline || ''} ${leg.flight_number || ''}`.trim());
          if (leg.seat) flightInfo.push(`Seat ${leg.seat}`);
          // Only show class_type if it's more than a single letter (booking class codes are not useful)
          if (leg.class_type && leg.class_type.length > 2) flightInfo.push(leg.class_type);
          if (flightInfo.length > 0) {
            doc.text(fitOneLine(flightInfo.join('  |  '), contentWidth - 45), margin + 38, yPosition);
            yPosition += 4;
          }
        }

        // Hotel details
        if (component.isHotelEvent && component.hotelData) {
          const hotelInfo = [];
          if (component.hotelData.room_type) hotelInfo.push(component.hotelData.room_type);
          const confNum = component.hotelData.confirmation_number || component.confirmation_number;
          if (confNum) hotelInfo.push(`Conf: ${confNum}`);
          if (hotelInfo.length > 0) {
            doc.text(hotelInfo.join('  |  '), margin + 38, yPosition);
            yPosition += 4;
          }
        }

        // Location (skip for flights since we already show airports)
        if (component.location && !component.isFlightLeg) {
          const locLines = doc.splitTextToSize(component.location, contentWidth - 45);
          doc.text(locLines, margin + 38, yPosition);
          yPosition += locLines.length * 4;
        }

        // Restaurant details
        if (compType === 'restaurant') {
          const restInfo = [];
          if (compData.party_size) restInfo.push(`Party of ${compData.party_size}`);
          if (compData.cuisine_type) restInfo.push(compData.cuisine_type);
          if (restInfo.length > 0) {
            doc.text(restInfo.join('  |  '), margin + 38, yPosition);
            yPosition += 4;
          }
        }

        // Meeting details
        if (compType === 'meeting' && compData.attendees?.length > 0) {
          const attendeeText = `With: ${compData.attendees.slice(0, 3).map(a => a.name).join(', ')}${compData.attendees.length > 3 ? '...' : ''}`;
          doc.text(attendeeText, margin + 38, yPosition);
          yPosition += 4;
        }

        // Confirmation number (if not already shown). Skipped for speaker
        // meetings in the speaker variant - the program id is shown in the
        // header as "Program ID" instead of a per-component "Confirmation".
        const confNum = component.confirmation_number || component.originalComponent?.confirmation_number;
        const skipConfirmation = isSpeakerVariant && compType === 'speaker_meeting';
        if (confNum && !component.isHotelEvent && !skipConfirmation) {
          doc.setTextColor(107, 114, 128);
          doc.text(`Confirmation: ${confNum}`, margin + 38, yPosition);
          yPosition += 4;
        }

        // Separator
        yPosition += 4;
        if (compIndex < dayComponents.length - 1) {
          doc.setDrawColor(229, 231, 235);
          doc.setLineDashPattern([2, 2], 0);
          doc.line(margin + 38, yPosition, pageWidth - margin, yPosition);
          doc.setLineDashPattern([], 0);
          yPosition += 6;
        }
      });

      yPosition += 8;
    });

    // ===== NOTES SECTION =====
    const relevantNotes = notesList.filter(note => {
      const pos = note.position;
      if (pos.position_type === 'attached' || pos.position_type === 'inside') {
        return selectedItems.has(pos.attached_to_component_id);
      }
      return pos.position_type === 'start' || pos.position_type === 'end';
    });

    if (relevantNotes.length > 0) {
      checkPageBreak(30);
      yPosition += 5;

      doc.setFillColor(254, 243, 199);
      doc.setDrawColor(252, 211, 77);
      const notesBoxHeight = Math.min(relevantNotes.length * 12 + 15, 60);
      doc.roundedRect(margin, yPosition, contentWidth, notesBoxHeight, 3, 3, 'FD');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(146, 64, 14);
      doc.text('NOTES', margin + 5, yPosition + 8);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 53, 15);
      doc.setFontSize(8);
      let noteY = yPosition + 16;
      relevantNotes.slice(0, 4).forEach(note => {
        const noteLines = doc.splitTextToSize(`- ${note.content || ''}`, contentWidth - 15);
        doc.text(noteLines[0], margin + 5, noteY);
        noteY += 10;
      });
    }

    addFooters(headerTitle);
    const fileBase = isSpeakerVariant ? headerTitle : tripTitle;
    doc.save(`${(fileBase || 'itinerary').replace(/[^a-zA-Z0-9]/g, '_')}_itinerary.pdf`);
  }, []);

  // Simplified format - clean, text-based layout inspired by client sample
  const generateSimplifiedPdf = useCallback((selectedItems, allComponentsList, tripData, notesList) => {
    const doc = new jsPDF();
    const margin = 20;
    const helpers = createPdfHelpers(doc, margin);
    const { pageWidth, pageHeight, contentWidth, formatDate, formatTime, getDateKey, addFooters } = helpers;

    let yPosition = margin;

    const checkPageBreak = (neededHeight = 20) => {
      if (yPosition + neededHeight > pageHeight - margin - 15) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    const tripTitle = tripData?.title || tripData?.name || 'Trip Itinerary';
    const selectedComponents = allComponentsList.filter(comp => selectedItems.has(comp.id));

    // ===== HEADER =====
    // Trip title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(tripTitle, margin, yPosition);
    yPosition += 8;

    // Date range
    if (tripData?.start_date || tripData?.end_date) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const dateRange = `${formatDate(tripData?.start_date)} - ${formatDate(tripData?.end_date)}`;
      doc.text(dateRange, margin, yPosition);
      yPosition += 12;
    } else {
      yPosition += 6;
    }

    // Simple line separator
    doc.setDrawColor(180, 180, 180);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;
    doc.setTextColor(0, 0, 0);

    // ===== GROUP BY DATE =====
    const componentsByDate = {};
    selectedComponents.forEach(comp => {
      const dateKey = comp.isFlightLeg && comp.flightLeg ? flightSide(comp.flightLeg, 'departure').calendarDate || 'TBD' : getDateKey(comp.start_datetime);
      if (!componentsByDate[dateKey]) {
        componentsByDate[dateKey] = [];
      }
      componentsByDate[dateKey].push(comp);
    });

    // Sort dates
    const sortedDates = Object.keys(componentsByDate).sort((a, b) => {
      if (a === 'TBD') return 1;
      if (b === 'TBD') return -1;
      return a.localeCompare(b);
    });

    // Type labels for display
    const typeLabels = {
      commercial_flight: 'Flight',
      private_flight: 'Private Flight',
      hotel: 'Hotel',
      restaurant: 'Restaurant',
      meeting: 'Meeting',
      speaker_meeting: 'Speaker Meeting',
      event: 'Event',
      rental_car: 'Rental Car',
      chauffeur_service: 'Transportation',
      airport_vip_service: 'Airport VIP Service',
      memo: 'Note'
    };

    // Format day header like "Sunday, January 19th"
    const formatSimplifiedDayHeader = (dateStr) => {
      if (!dateStr || dateStr === 'TBD') return 'Pending';
      try {
        // parseLocalDate keeps "yyyy-MM-dd" keys on their own calendar day
        // (new Date() would parse them as UTC midnight and can shift a day)
        const date = parseLocalDate(dateStr);
        if (!date) return dateStr;
        return format(date, 'EEEE, MMMM do');
      } catch {
        return dateStr;
      }
    };

    // ===== RENDER EACH DAY =====
    sortedDates.forEach((dateKey, dateIndex) => {
      const dayComponents = componentsByDate[dateKey];

      // Day header
      checkPageBreak(30);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(formatSimplifiedDayHeader(dateKey), margin, yPosition);
      yPosition += 8;

      // Sort by time within the day (using local time values, not browser timezone)
      dayComponents.sort((a, b) => {
        const ta = parseDatetimeForSortValue(a.start_datetime);
        const tb = parseDatetimeForSortValue(b.start_datetime);
        if (ta === null) return 1;
        if (tb === null) return -1;
        return ta - tb;
      });

      // Render each component
      dayComponents.forEach((component) => {
        checkPageBreak(25);

        const compType = component.originalComponent?.component_type || component.component_type;
        const compData = component.component_data || component.originalComponent?.component_data || {};
        const time = component.isFlightLeg && component.flightLeg ? flightSide(component.flightLeg, 'departure', user?.ui_preferences).time : formatTime(component.start_datetime);
        const typeLabel = typeLabels[compType] || 'Item';

        // Time and type label on same line
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);

        let lineText = '';
        if (time) {
          lineText = `${time} `;
        }
        lineText += `${typeLabel}:`;
        doc.text(lineText, margin, yPosition);
        yPosition += 5;

        // Title/Description
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const titleText = component.title || 'Untitled';
        const titleLines = doc.splitTextToSize(titleText, contentWidth);
        doc.text(titleLines, margin + 5, yPosition);
        yPosition += titleLines.length * 4.5;

        // Flight-specific details
        if (component.isFlightLeg && component.flightLeg) {
          const leg = component.flightLeg;
          doc.setTextColor(60, 60, 60);
          const clockLines = doc.splitTextToSize(flightExportDetails(leg, user?.ui_preferences).join('\n'), contentWidth - 5);
          checkPageBreak(clockLines.length * 4.5 + 12);
          doc.text(clockLines, margin + 5, yPosition);
          yPosition += clockLines.length * 4.5;

          // Line 2: Airline code + flight number (Terminal X Gate Y)
          // e.g., "BA 305 (Terminal 2 Gate 5)"
          const airlineCode = leg.airline_code || leg.iata_code || leg.airline || '';
          const flightNumber = leg.flight_number || '';

          if (airlineCode || flightNumber) {
            // Airline code and flight number
            let flightInfo = `${airlineCode} ${flightNumber}`.trim();

            // Terminal and gate in parentheses
            let terminalGateParts = [];
            if (leg.departure_terminal) {
              terminalGateParts.push(`Terminal ${leg.departure_terminal}`);
            }
            if (leg.departure_gate) {
              terminalGateParts.push(`Gate ${leg.departure_gate}`);
            }

            if (terminalGateParts.length > 0) {
              flightInfo += ` (${terminalGateParts.join(' ')})`;
            }

            if (flightInfo) {
              doc.text(flightInfo, margin + 5, yPosition);
              yPosition += 4.5;
            }
          }

          if (leg.seat) {
            doc.text(`Seat: ${leg.seat}`, margin + 5, yPosition);
            yPosition += 4.5;
          }
          doc.setTextColor(0, 0, 0);
        }

        // Hotel-specific details
        if (component.isHotelEvent && component.hotelData) {
          doc.setTextColor(60, 60, 60);
          if (component.hotelData.room_type) {
            doc.text(component.hotelData.room_type, margin + 5, yPosition);
            yPosition += 4.5;
          }
          doc.setTextColor(0, 0, 0);
        }

        // Location (except for flights which show airports)
        if (component.location && !component.isFlightLeg) {
          doc.setTextColor(60, 60, 60);
          const locLines = doc.splitTextToSize(component.location, contentWidth - 5);
          doc.text(locLines, margin + 5, yPosition);
          yPosition += locLines.length * 4.5;
          doc.setTextColor(0, 0, 0);
        }

        // Restaurant details
        if (compType === 'restaurant' && compData.party_size) {
          doc.setTextColor(60, 60, 60);
          doc.text(`Party of ${compData.party_size}`, margin + 5, yPosition);
          yPosition += 4.5;
          doc.setTextColor(0, 0, 0);
        }

        // Confirmation number
        const confNum = component.confirmation_number ||
                        component.originalComponent?.confirmation_number ||
                        compData.confirmation_number ||
                        component.hotelData?.confirmation_number ||
                        component.flightLeg?.confirmation_number;
        if (confNum) {
          doc.setTextColor(80, 80, 80);
          doc.text(`Conf #: ${confNum}`, margin + 5, yPosition);
          yPosition += 4.5;
          doc.setTextColor(0, 0, 0);
        }

        // Notes attached to this component
        const componentNotes = notesList.filter(note => {
          const pos = note.position;
          return (pos.position_type === 'attached' || pos.position_type === 'inside') &&
                 pos.attached_to_component_id === (component.originalComponent?.id || component.componentId);
        });

        if (componentNotes.length > 0) {
          doc.setTextColor(100, 80, 0);
          doc.setFont('helvetica', 'italic');
          componentNotes.forEach(note => {
            if (note.content) {
              const noteLines = doc.splitTextToSize(`*${note.content}`, contentWidth - 5);
              noteLines.forEach(line => {
                checkPageBreak(6);
                doc.text(line, margin + 5, yPosition);
                yPosition += 4.5;
              });
            }
          });
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
        }

        yPosition += 3;
      });

      // Space between days
      yPosition += 5;
    });

    // ===== GENERAL NOTES SECTION =====
    const generalNotes = notesList.filter(note => {
      const pos = note.position;
      return pos.position_type === 'start' || pos.position_type === 'end';
    });

    if (generalNotes.length > 0) {
      checkPageBreak(20);
      yPosition += 5;

      doc.setDrawColor(180, 180, 180);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', margin, yPosition);
      yPosition += 6;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      generalNotes.forEach(note => {
        if (note.content) {
          checkPageBreak(10);
          const noteLines = doc.splitTextToSize(`- ${note.content}`, contentWidth);
          doc.text(noteLines, margin, yPosition);
          yPosition += noteLines.length * 4.5 + 2;
        }
      });
    }

    addFooters(tripTitle);
    doc.save(`${tripTitle.replace(/[^a-zA-Z0-9]/g, '_')}_simplified.pdf`);
  }, []);

  // Main PDF generation dispatcher
  const generateTimelinePdf = useCallback((selectedItems, allComponentsList, tripData, notesList, formatType = 'executive') => {
    if (formatType === 'simple') {
      generateSimplePdf(selectedItems, allComponentsList, tripData, notesList);
    } else if (formatType === 'simplified') {
      generateSimplifiedPdf(selectedItems, allComponentsList, tripData, notesList);
    } else if (formatType === 'speaker') {
      // Derive the speaker name and Program ID(s) from the selected speaker
      // meeting components (they carry speaker_name and program_id).
      let speakerName = null;
      const programIds = [];
      const seenIds = new Set();
      allComponentsList
        .filter(c => selectedItems.has(c.id))
        .forEach(c => {
          const type = c.originalComponent?.component_type || c.component_type;
          if (type !== 'speaker_meeting') return;
          const cd = c.component_data || c.originalComponent?.component_data || {};
          if (!speakerName && cd.speaker_name) speakerName = cd.speaker_name;
          const pid = cd.program_id || cd.confirmation_number || c.confirmation_number;
          const norm = pid ? String(pid).trim() : '';
          if (norm && !seenIds.has(norm)) {
            seenIds.add(norm);
            programIds.push(norm);
          }
        });
      generateExecutivePdf(selectedItems, allComponentsList, tripData, notesList, {
        variant: 'speaker',
        speakerName,
        programIds,
      });
    } else {
      generateExecutivePdf(selectedItems, allComponentsList, tripData, notesList);
    }
  }, [generateSimplePdf, generateSimplifiedPdf, generateExecutivePdf]);

  // Get notes for a specific position
  const getNotesAtPosition = (positionType, componentId = null) => {
    return stickyNotes.filter(note => {
      const pos = note.position;
      if (pos.position_type !== positionType) return false;
      if (positionType === 'attached') {
        return pos.attached_to_component_id === componentId;
      }
      return true;
    }).sort((a, b) => (a.position?.rank || 0) - (b.position?.rank || 0));
  };

  // Get notes positioned between two components
  const getNotesBetween = (beforeId, afterId) => {
    return stickyNotes.filter(note => {
      const pos = note.position;
      if (pos.position_type !== 'between') return false;
      return pos.anchor_before_id === beforeId && pos.anchor_after_id === afterId;
    }).sort((a, b) => (a.position?.rank || 0) - (b.position?.rank || 0));
  };

  // Get notes positioned inside a component
  const getNotesInside = (componentId) => {
    return stickyNotes.filter(note => {
      const pos = note.position;
      if (pos.position_type !== 'inside') return false;
      return pos.attached_to_component_id === componentId;
    }).sort((a, b) => (a.position?.rank || 0) - (b.position?.rank || 0));
  };

  // Toggle selection mode
  const toggleUrbanrideSelectionMode = () => {
    if (urbanrideSelectionMode) {
      // Exiting selection mode - clear selections
      setSelectedComponents([]);
    }
    setUrbanrideSelectionMode(!urbanrideSelectionMode);
  };

  // Toggle component selection
  // Use the unique component.id (not originalId) to allow selecting individual flight legs
  // This is important for chauffeur bookings where users want to select specific arrivals/departures
  // For example, a round-trip flight has multiple legs but users typically only need ground
  // transportation for the arrival at their destination, not connecting flights
  const toggleComponentSelection = (component) => {
    // Always add component to selection (allows same component multiple times)
    // This supports use cases like: airport -> hotel -> meeting -> dinner -> hotel (return)
    // Users remove items from the summary panel on the right side
    setSelectedComponents([...selectedComponents, component]);
  };

  // Check if a component is selected
  // Use the unique component.id to check individual flight legs
  const isComponentSelected = (component) => {
    const componentId = component.id;
    return selectedComponents.some(c => c.id === componentId);
  };

  // Start the booking process
  const startUrbanrideBooking = () => {
    if (selectedComponents.length >= 2) {
      setShowUrbanrideBooking(true);
    }
  };

  // Handle booking success
  const handleUrbanrideBookingSuccess = (result) => {
    setShowUrbanrideBooking(false);
    setSelectedComponents([]);
    setUrbanrideSelectionMode(false);
    // Refresh components to show updated Urbanride references
    onRefreshComponents();
  };

  // Book a ride between two timeline items straight from a card (hotel
  // arrival/departure transfers). With only one end known, open selection
  // mode with it picked so the user chooses the other end.
  const bookTransferBetween = (from, to) => {
    if (from && to) {
      setSelectedComponents([from, to]);
      setShowUrbanrideBooking(true);
    } else {
      setSelectedComponents([from || to].filter(Boolean));
      setUrbanrideSelectionMode(true);
    }
  };

  // Handle booking cancel
  const handleUrbanrideBookingCancel = () => {
    setShowUrbanrideBooking(false);
    // A booking started from a card leaves nothing selected behind
    if (!urbanrideSelectionMode) {
      setSelectedComponents([]);
      return;
    }
    // Remove return leg if it was added
    setSelectedComponents(prev => prev.filter(c => !c.isReturnLeg));
  };

  // Open Quick Edit modal for an existing chauffeur_service component
  const handleQuickEdit = (component) => {
    setQuickEditComponent(component);
  };

  // Close Quick Edit panel
  const handleQuickEditCancel = () => {
    setQuickEditComponent(null);
    setQuickEditIncomingComponent(null);
  };

  // Handle successful Quick Edit update
  const handleQuickEditSuccess = () => {
    setQuickEditComponent(null);
    setQuickEditIncomingComponent(null);
    onRefreshComponents();
  };

  // Handle clicking a timeline card while in quickedit mode
  const handleQuickEditCardClick = (component) => {
    setQuickEditIncomingComponent(component);
  };

  // ── Bundle selection mode handlers ──────────────────────────────────
  const handleStartBundleSelection = (bundleComponent) => {
    const childIds = new Set(bundleComponent.component_data?.child_component_ids || []);
    setBundleSelectionMode(bundleComponent);
    setBundleSelectedIds(new Set(childIds));
    setBundleOriginalIds(new Set(childIds));
  };

  const handleBundleToggleComponent = (component) => {
    const compId = component.originalComponent?.id || component.id;
    setBundleSelectedIds(prev => {
      const next = new Set(prev);
      next.has(compId) ? next.delete(compId) : next.add(compId);
      return next;
    });
  };

  const handleBundleSave = async () => {
    if (!bundleSelectionMode) return;
    setBundleSaving(true);
    try {
      const { getApiBase, getAuthHeaders } = await import('../../utils/api');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(
        `${apiBase}/trips/${trip.id}/bundles/${bundleSelectionMode.id}/members`,
        { component_ids: [...bundleSelectedIds] },
        { headers }
      );
      setBundleSelectionMode(null);
      setBundleSelectedIds(new Set());
      setBundleOriginalIds(new Set());
      onRefreshComponents();
    } catch (err) {
      alert('Failed to update bundle: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBundleSaving(false);
    }
  };

  const handleBundleCancel = () => {
    setBundleSelectionMode(null);
    setBundleSelectedIds(new Set());
    setBundleOriginalIds(new Set());
  };

  const bundleHasChanges = useMemo(() => {
    if (!bundleSelectionMode) return false;
    if (bundleOriginalIds.size !== bundleSelectedIds.size) return true;
    for (const id of bundleSelectedIds) {
      if (!bundleOriginalIds.has(id)) return true;
    }
    return false;
  }, [bundleSelectionMode, bundleSelectedIds, bundleOriginalIds]);

  // Handle reordering components in the booking summary
  const handleReorderComponents = (newOrder) => {
    setSelectedComponents(newOrder);
  };

  // Handle removing a component from the selection
  const handleRemoveFromSelection = (index) => {
    // Remove by index to support duplicate components in the itinerary
    setSelectedComponents(prev => prev.filter((_, i) => i !== index));
  };

  // Handle adding a manual location to the itinerary
  const handleAddManualLocation = (manualComponent) => {
    // Add the manual component to the selected components
    setSelectedComponents(prev => [...prev, manualComponent]);
  };

  // All component type configurations for quick add modal
  const allQuickAddComponentTypes = [
    { type: 'commercial_flight', title: 'Commercial Flight', icon: Plane, color: 'bg-ui-blue-bg text-ui-blue-fg', lightColor: 'bg-ui-blue-bg' },
    { type: 'private_flight', title: 'Private Flight', icon: Plane, color: 'bg-ui-violet-bg text-ui-violet-fg', lightColor: 'bg-ui-violet-bg' },
    { type: 'hotel', title: 'Hotel', icon: Building, color: 'bg-ui-green-bg text-ui-green-fg', lightColor: 'bg-ui-green-bg' },
    { type: 'restaurant', title: 'Restaurant', icon: UtensilsCrossed, color: 'bg-ui-orange-bg text-ui-orange-fg', lightColor: 'bg-ui-orange-bg' },
    { type: 'meeting', title: 'Meeting', icon: Handshake, color: 'bg-ui-indigo-bg text-ui-indigo-fg', lightColor: 'bg-ui-indigo-bg' },
    { type: 'event', title: 'Event', icon: CalendarIcon, color: 'bg-ui-pink-bg text-ui-pink-fg', lightColor: 'bg-ui-pink-bg' },
    { type: 'rental_car', title: 'Rental Car', icon: Car, color: 'bg-ui-teal-bg text-ui-teal-fg', lightColor: 'bg-ui-teal-bg' },
    { type: 'chauffeur_service', title: 'Chauffeur Service', icon: Car, color: 'bg-ui-hover text-ui-ink-3', lightColor: 'bg-ui-sunk' },
    { type: 'airport_vip_service', title: 'Airport VIP Service', icon: BadgeCheck, color: 'bg-ui-amber-bg text-ui-amber-fg', lightColor: 'bg-ui-amber-bg' },
    { type: 'memo', title: 'Memo', icon: StickyNoteIcon, color: 'bg-ui-amber-bg text-ui-amber-fg', lightColor: 'bg-ui-amber-bg' },
  ];

  // Filter component types based on organization's available components
  const quickAddComponentTypes = useMemo(() => {
    if (availableComponentTypes.length === 0) {
      return allQuickAddComponentTypes; // Show all while loading
    }
    return allQuickAddComponentTypes.filter(ct => availableComponentTypes.includes(ct.type));
  }, [availableComponentTypes]);

  // Handle opening quick add modal at a specific position
  const handleOpenQuickAdd = useCallback((anchorInfo) => {
    setQuickAddAnchor(anchorInfo);
    setShowQuickAddModal(true);
  }, []);

  // Handle closing quick add modal
  const handleCloseQuickAdd = useCallback(() => {
    setShowQuickAddModal(false);
    setQuickAddAnchor(null);
    setQuickAddTitle('');
  }, []);

  // Handle creating a TBD component from quick add modal
  const handleQuickAddComponent = useCallback(async (componentType) => {
    if (!trip?.id || isCreatingComponent) return;

    setIsCreatingComponent(true);
    try {
      const { getApiBase, getAuthHeaders } = await import('../../utils/api');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Use custom title if provided, otherwise generate default
      const defaultTitle = `New ${allQuickAddComponentTypes.find(t => t.type === componentType)?.title || 'Component'} (TBD)`;
      const title = quickAddTitle.trim() || defaultTitle;

      // Create minimal TBD component data based on type
      let componentData = {
        component_type: componentType,
        title: title,
        status: 'pending',
        date_is_tbd: true,
        time_is_tbd: true,
      };

      // Add type-specific minimal data
      switch (componentType) {
        case 'meeting':
          componentData.component_data = { time_is_tbd: true };
          break;
        case 'restaurant':
          componentData.component_data = { time_is_tbd: true };
          break;
        case 'event':
          componentData.component_data = { time_is_tbd: true };
          break;
        case 'hotel':
          componentData.component_data = {};
          break;
        case 'commercial_flight':
          componentData.component_data = { flight_legs: [] };
          break;
        case 'private_flight':
          componentData.component_data = { details: {} };
          break;
        case 'rental_car':
          componentData.component_data = {};
          break;
        case 'chauffeur_service':
          componentData.component_data = {};
          break;
        case 'airport_vip_service':
          componentData.component_data = { time_is_tbd: true };
          break;
        default:
          componentData.component_data = {};
      }

      // Create the component
      const createResponse = await axios.post(
        `${apiBase}/trips/${trip.id}/components`,
        componentData,
        { headers }
      );

      // API returns { ok: true, component: { id: ..., ... } }
      const newComponentId = createResponse.data?.component?.id;

      // If we have anchor info, save the position using timeline2_order
      if (quickAddAnchor && newComponentId) {
        let anchorData = null;

        if (quickAddAnchor.position === 'start') {
          anchorData = { position: 'start', rank: 0 };
        } else if (quickAddAnchor.position === 'end') {
          anchorData = { position: 'end', rank: 0 };
        } else if (quickAddAnchor.beforeComponentId && quickAddAnchor.afterComponentId) {
          anchorData = {
            primary_anchor_id: quickAddAnchor.beforeComponentId,
            secondary_anchor_id: quickAddAnchor.afterComponentId,
            position: 'between',
            rank: 0
          };
        } else if (quickAddAnchor.afterComponentId) {
          anchorData = {
            primary_anchor_id: quickAddAnchor.afterComponentId,
            position: 'before',
            rank: 0
          };
        } else if (quickAddAnchor.beforeComponentId) {
          anchorData = {
            primary_anchor_id: quickAddAnchor.beforeComponentId,
            position: 'after',
            rank: 0
          };
        }

        if (anchorData) {
          // Use the same endpoint as persistTBDPositioning - update the trip with timeline2_order
          await axios.put(
            `${apiBase}/trips/${trip.id}`,
            { timeline2_order: { [newComponentId]: anchorData } },
            { headers }
          );

          // Update local timeline2Order state immediately so position is applied before refresh
          setTimeline2Order(prev => ({ ...prev, [newComponentId]: anchorData }));
        }
      }

      // Refresh components to show the new one
      onRefreshComponents();
      handleCloseQuickAdd();
    } catch (err) {
      console.error('Failed to create component:', err);
      alert('Failed to create component. Please try again.');
    } finally {
      setIsCreatingComponent(false);
    }
  }, [trip?.id, quickAddAnchor, quickAddTitle, isCreatingComponent, onRefreshComponents, handleCloseQuickAdd]);

  // Process components to include individual flight legs and hotel check-in/out events
  // (Same logic as original timeline for consistency)
  const processedComponents = useMemo(() => {
    const processed = [];
    
    components.forEach(component => {
      // Handle commercial flight legs
      if ((component.component_type === 'commercial_flight' || 
           component.component_type === 'flight') &&
          component.component_data?.flight_legs && 
          component.component_data.flight_legs.length > 0) {
        
        // Create separate timeline items for each commercial flight leg
        component.component_data.flight_legs.forEach((leg, legIndex) => {
          // Generate title with airport names and 3-letter codes using available data
          const depCode = leg.departure_airport || leg.departure_airport_code || '';
          const arrCode = leg.arrival_airport || leg.arrival_airport_code || '';
          
          // Get airport names from mapping or use code as fallback
          const depName = AIRPORT_NAMES[depCode] || `${depCode} Airport`;
          const arrName = AIRPORT_NAMES[arrCode] || `${arrCode} Airport`;
          
          const depDisplay = depCode ? `${depName} (${depCode})` : 'TBD';
          const arrDisplay = arrCode ? `${arrName} (${arrCode})` : 'TBD';
          const legTitle = `${depDisplay} - ${arrDisplay}`;
          
          processed.push({
            ...component,
            id: `${component.id}-leg-${legIndex}`,
            originalId: component.id,
            title: legTitle,
            start_datetime: resolveLegDateTime(leg, 'departure'),
            end_datetime: resolveLegDateTime(leg, 'arrival'),
            location: leg.departure_airport,
            status: component.status, // CRITICAL: Explicitly preserve status field
            isFlightLeg: true,
            legIndex: legIndex,
            totalLegs: component.component_data.flight_legs.length,
            flightLeg: leg,
            originalComponent: component
          });
        });
      }
      // Handle private flight legs
      else if (component.component_type === 'private_flight' &&
          component.component_data?.private_flight_legs &&
          component.component_data.private_flight_legs.length > 0) {

        // Helper: resolve which datetime a leg should use as its timeline anchor.
        // departure is the default; arrival is used when explicitly set or when
        // departure info is absent but arrival info is present. Uses the shared
        // resolver so departure_datetime / full-datetime *_time fields are
        // handled the same as commercial legs.
        const getLegAnchorDatetime = (leg) => {
          const dep = resolveLegDateTime(leg, 'departure');
          const arr = resolveLegDateTime(leg, 'arrival');
          const isTbd = (v) => !v || String(v).trim() === '' || String(v).toLowerCase().includes('tbd');
          const hasDep = !isTbd(dep);
          const hasArr = !isTbd(arr);
          if (hasDep && hasArr) return leg.anchor_to_arrival ? arr : dep;
          if (hasDep) return dep;
          if (hasArr) return arr;
          return null; // TBD
        };

        // Create separate timeline items for each private flight leg
        component.component_data.private_flight_legs.forEach((leg, legIndex) => {
          // Generate title with airport names and 3-letter codes  
          const depName = leg.departure_airport_name || leg.departure_airport_display || leg.departure_airport || 'TBD';
          const depCode = leg.departure_airport_iata || leg.iata_code || leg.departure_airport_code?.slice(-3) || leg.departure_airport_icao?.slice(-3) || '';
          const arrName = leg.arrival_airport_name || leg.arrival_airport_display || leg.arrival_airport || 'TBD';
          const arrCode = leg.arrival_airport_iata || leg.arrival_iata_code || leg.arrival_airport_code?.slice(-3) || leg.arrival_airport_icao?.slice(-3) || '';
          
          const depDisplay = depCode ? `${depName} (${depCode})` : depName;
          const arrDisplay = arrCode ? `${arrName} (${arrCode})` : arrName;
          const legTitle = `${depDisplay} - ${arrDisplay}`;
          
          processed.push({
            ...component,
            id: `${component.id}-private-leg-${legIndex}`,
            originalId: component.id,
            title: legTitle,
            start_datetime: getLegAnchorDatetime(leg),
            end_datetime: resolveLegDateTime(leg, 'arrival'),
            location: leg.departure_airport_name || leg.departure_airport_display,
            status: component.status,
            isFlightLeg: true,
            isPrivateFlightLeg: true, // Distinguish from commercial flight legs
            legIndex: legIndex,
            totalLegs: component.component_data.private_flight_legs.length,
            flightLeg: leg,
            originalComponent: component
          });
        });
      } 
      else if (component.component_type === 'hotel' && 
                 component.component_data?.check_in && 
                 component.component_data?.check_out) {
        
        // Create separate timeline items for hotel check-in and check-out
        const hotelName = component.component_data.hotel_name || component.title || 'Hotel';
        const checkInDate = component.component_data.check_in;
        const checkOutDate = component.component_data.check_out;
        
        // Check-in timeline entry
        processed.push({
          ...component,
          id: `${component.id}-checkin`,
          originalId: component.id,
          title: `Check-in: ${hotelName}`,
          start_datetime: checkInDate,
          end_datetime: null,
          location: getHotelFullLocation(component.component_data, component.location),
          status: component.status, // CRITICAL: Explicitly preserve status field
          isHotelEvent: true,
          eventType: 'checkin',
          hotelData: component.component_data,
          originalComponent: component,
          description: `Check-in at ${hotelName}`,
          notes: component.notes
        });
        
        // Check-out timeline entry
        processed.push({
          ...component,
          id: `${component.id}-checkout`,
          originalId: component.id,
          title: `Check-out: ${hotelName}`,
          start_datetime: checkOutDate,
          end_datetime: null,
          location: getHotelFullLocation(component.component_data, component.location),
          status: component.status, // CRITICAL: Explicitly preserve status field
          isHotelEvent: true,
          eventType: 'checkout',
          hotelData: component.component_data,
          originalComponent: component,
          description: `Check-out from ${hotelName}`,
          notes: component.notes
        });
      } else if ((component.component_type === 'rental_car' || component.component_type === 'car_rental') &&
                 (component.start_datetime || component.component_data?.pickup_datetime || component.component_data?.pickup_date)) {

        // Create separate timeline items for rental car pickup and return
        const rentalCompany = component.component_data?.rental_company ||
                              component.component_data?.rental_car_company ||
                              component.title ||
                              'Car Rental';
        const vehicleType = component.component_data?.vehicle_type ||
                            component.component_data?.car_type ||
                            '';
        const displayName = vehicleType ? `${rentalCompany} - ${vehicleType}` : rentalCompany;

        // Get pickup datetime - prefer start_datetime (clean, no timezone suffix), then pickup_datetime, then combine date + time
        // Note: pickup_datetime may have incorrect timezone suffix from backend processing
        let pickupDateTime = component.start_datetime || component.component_data?.pickup_datetime;
        if (!pickupDateTime && component.component_data?.pickup_date) {
          const pickupDate = component.component_data.pickup_date;
          const pickupTime = component.component_data.pickup_time || '12:00';
          pickupDateTime = pickupDate.includes('T') ? pickupDate : `${pickupDate}T${pickupTime}`;
        }

        // Get dropoff datetime - prefer end_datetime (clean), then dropoff_datetime, then combine date + time
        let dropoffDateTime = component.end_datetime || component.component_data?.dropoff_datetime;
        if (!dropoffDateTime && component.component_data?.dropoff_date) {
          const dropoffDate = component.component_data.dropoff_date;
          const dropoffTime = component.component_data.dropoff_time || '12:00';
          dropoffDateTime = dropoffDate.includes('T') ? dropoffDate : `${dropoffDate}T${dropoffTime}`;
        }

        const pickupLocation = component.component_data?.pickup_location || component.location || '';
        const dropoffLocation = component.component_data?.dropoff_location || pickupLocation || '';

        // Pickup timeline entry
        if (pickupDateTime) {
          processed.push({
            ...component,
            id: `${component.id}-pickup`,
            originalId: component.id,
            title: `Pickup: ${displayName}`,
            start_datetime: pickupDateTime,
            end_datetime: null,
            location: pickupLocation,
            status: component.status,
            isRentalCarEvent: true,
            eventType: 'pickup',
            rentalData: component.component_data,
            originalComponent: component,
            description: `Pick up rental car at ${pickupLocation || 'location TBD'}`,
            notes: component.notes
          });
        }

        // Return timeline entry
        if (dropoffDateTime) {
          processed.push({
            ...component,
            id: `${component.id}-return`,
            originalId: component.id,
            title: `Return: ${displayName}`,
            start_datetime: dropoffDateTime,
            end_datetime: null,
            location: dropoffLocation,
            status: component.status,
            isRentalCarEvent: true,
            eventType: 'return',
            rentalData: component.component_data,
            originalComponent: component,
            description: `Return rental car at ${dropoffLocation || 'location TBD'}`,
            notes: component.notes
          });
        }
      } else {
        // Regular component
        // For hotels without check_in/check_out, still use the hotel address if available
        const location = component.component_type === 'hotel'
          ? getHotelFullLocation(component.component_data, component.location)
          : component.location;
        processed.push({
          ...component,
          location: location,
          status: component.status, // CRITICAL: Explicitly preserve status field
          isFlightLeg: false,
          isHotelEvent: false,
          originalComponent: component
        });
      }
    });
    
    return processed;
  }, [components, components.map(c => c.status).join(','), components.map(c => c.title).join(','), components.map(c => c.component_data?.hotel_address || c.component_data?.address || '').join(',')]);
  // CRITICAL: Added component status, title, and hotel address dependencies to force re-processing when component data changes

  // Group commercial flight legs that are on the same flight (same route + time + flight number)
  // This allows multiple passengers (from separate components) to be displayed on a single card
  const groupedComponents = useMemo(() => {
    const result = [];
    const flightLegGroups = new Map(); // Key: grouping key, Value: array of components

    // First pass: identify commercial flight legs that can be grouped
    processedComponents.forEach(comp => {
      // Only group commercial flight legs (not private flights, hotels, etc.)
      if (comp.isFlightLeg && !comp.isPrivateFlightLeg && comp.flightLeg) {
        const leg = comp.flightLeg;
        // Create grouping key from route + departure time + flight number
        const depAirport = (leg.departure_airport || leg.departure_airport_code || '').toUpperCase();
        const arrAirport = (leg.arrival_airport || leg.arrival_airport_code || '').toUpperCase();
        const depTime = leg.departure_datetime || (leg.departure_date && leg.departure_time ? `${leg.departure_date}T${leg.departure_time}` : null) || leg.departure_time || comp.start_datetime || '';
        const flightNum = (leg.flight_number || '').toUpperCase().replace(/\s+/g, '');

        // Only group if we have enough info to identify the flight
        if (depAirport && arrAirport && (depTime || flightNum)) {
          const groupKey = `${depAirport}-${arrAirport}-${depTime}-${flightNum}`;

          if (!flightLegGroups.has(groupKey)) {
            flightLegGroups.set(groupKey, []);
          }
          flightLegGroups.get(groupKey).push(comp);
        } else {
          // Not enough info to group - add as standalone
          result.push(comp);
        }
      } else {
        // Non-groupable component - add as-is
        result.push(comp);
      }
    });

    // Second pass: process grouped flight legs
    flightLegGroups.forEach((groupedLegs, groupKey) => {
      if (groupedLegs.length === 1) {
        // Only one component in group - no need to merge
        result.push(groupedLegs[0]);
      } else {
        // Multiple components for same flight leg - merge them
        const primaryLeg = groupedLegs[0]; // Use first as primary

        // Collect all passengers from all grouped components
        const allPassengers = [];
        const allOriginalComponents = [];

        groupedLegs.forEach(leg => {
          const origComp = leg.originalComponent || leg;
          allOriginalComponents.push(origComp);

          // Get passengers from this component
          const passengers = origComp.component_data?.passengers || [];
          passengers.forEach(pax => {
            // Add source component ID to passenger for tracking
            allPassengers.push({
              ...pax,
              _sourceComponentId: origComp.id
            });
          });
        });

        // Create merged component
        const mergedComponent = {
          ...primaryLeg,
          id: `grouped-${groupKey}`, // Unique ID for the grouped display
          isGroupedFlightLeg: true,
          groupedComponents: allOriginalComponents, // All original components for edit/delete
          groupedPassengers: allPassengers, // All passengers from all components
          // Override component_data.passengers with merged list
          component_data: {
            ...primaryLeg.component_data,
            passengers: allPassengers
          }
        };

        result.push(mergedComponent);
      }
    });

    return result;
  }, [processedComponents]);

  // State for component ordering (initially based on processed components)
  const [componentOrder, setComponentOrder] = useState([]);
  // Initialize view mode from localStorage first (immediate), then backend preferences
  // Support external control via externalViewMode prop
  const [internalViewMode, setInternalViewMode] = useState(() => {
    // Try localStorage first for immediate access
    const localStorageKey = `timeline2_view_mode_${user?.id || 'default'}`;
    const savedMode = localStorage.getItem(localStorageKey);
    if (savedMode === 'compact' || savedMode === 'summary' || savedMode === 'full') {
      return savedMode;
    }
    // Fall back to user preferences or default
    return user?.ui_preferences?.timeline2_view_mode || 'compact';
  });
  const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;

  // Reset expanded compact rows when leaving compact mode
  useEffect(() => {
    if (viewMode !== 'compact') setCompactExpandedIds(new Set());
  }, [viewMode]);

  const [hiddenComponents, setHiddenComponents] = useState(new Set()); // Track hidden component IDs

  // Sync view mode with user preferences when user data loads (for cross-device sync)
  useEffect(() => {
    // Skip sync if view mode is externally controlled
    if (externalViewMode !== undefined) return;

    const savedViewMode = user?.ui_preferences?.timeline2_view_mode;
    const localStorageKey = `timeline2_view_mode_${user?.id || 'default'}`;
    const localMode = localStorage.getItem(localStorageKey);

    // If no local storage value but backend has one, use backend value
    if (!localMode && savedViewMode && (savedViewMode === 'compact' || savedViewMode === 'summary' || savedViewMode === 'full')) {
      setInternalViewMode(savedViewMode);
      localStorage.setItem(localStorageKey, savedViewMode);
    }
  }, [user?.id, user?.ui_preferences?.timeline2_view_mode, externalViewMode]);

  // Handle view mode change and persist to localStorage + backend
  const handleViewModeChange = useCallback(async (newMode) => {
    // If externally controlled, call the external handler
    if (onExternalViewModeChange) {
      onExternalViewModeChange(newMode);
    } else {
      setInternalViewMode(newMode);
    }

    // Save to localStorage immediately for fast persistence across navigation
    const localStorageKey = `timeline2_view_mode_${user?.id || 'default'}`;
    localStorage.setItem(localStorageKey, newMode);

    // Also save to backend for cross-device/session persistence
    try {
      // Only send the timeline2_view_mode field
      const res = await axios.put('/users/me/preferences', { timeline2_view_mode: newMode });
      // Update user context with new preferences
      if (user && setUser) {
        setUser({ ...user, ui_preferences: res.data });
      }
    } catch (e) {
      console.warn('Failed to save view mode preference to backend:', e);
      // View mode is still saved locally even if backend save fails
    }
  }, [user, setUser]);

  // Toggle hide/show for a component
  const toggleHideComponent = (componentId) => {
    setHiddenComponents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(componentId)) {
        newSet.delete(componentId);
      } else {
        newSet.add(componentId);
      }
      return newSet;
    });
  };

  // Check if a component is marked as confidential
  const isComponentConfidential = useCallback((componentId) => {
    const overrides = trip?.component_permission_overrides || [];
    const override = overrides.find(o => o.component_id === componentId);
    return override?.is_confidential || false;
  }, [trip?.component_permission_overrides]);

  // Check if current user can toggle component confidentiality
  // (creator, supervisor, or org admin)
  const canToggleComponentConfidentiality = useMemo(() => {
    if (!user || !trip) return false;
    // View-only mode (speaker portal): never
    if (isReadOnly) return false;
    // Org admins can always toggle
    if (user.is_organization_admin) return true;
    // Trip creator can toggle
    if (trip.created_by === user.id) return true;
    // Check if user is a supervisor of the trip creator
    const managedUserIds = user.managed_user_ids || [];
    if (trip.created_by && managedUserIds.includes(trip.created_by)) return true;
    return false;
  }, [user, trip, isReadOnly]);

  // Toggle component confidentiality
  const handleToggleComponentConfidentiality = useCallback(async (componentId) => {
    if (!canToggleComponentConfidentiality) return;

    try {
      const { getApiBase, getAuthHeaders } = await import('../../utils/api');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const currentValue = isComponentConfidential(componentId);
      await axios.patch(
        `${apiBase}/trips/${trip.id}/components/${componentId}/confidentiality`,
        { is_confidential: !currentValue },
        { headers }
      );

      // Refresh trip data to get updated permission overrides
      if (onRefreshComponents) {
        onRefreshComponents();
      }
    } catch (err) {
      console.error('Failed to toggle component confidentiality:', err);
    }
  }, [trip?.id, canToggleComponentConfidentiality, isComponentConfidential, onRefreshComponents]);

  // Open confidentiality settings modal
  const handleOpenConfidentialityModal = useCallback((componentId) => {
    const component = components.find(c => c.id === componentId);
    setConfidentialityTarget({
      isUnfiled: false,
      tripId: trip?.id,
      componentId: componentId,
      title: component?.title || `${component?.component_type?.replace(/_/g, ' ')} Component`
    });
    setShowConfidentialityModal(true);
  }, [trip?.id, components]);

  // Handle confidentiality update (refresh trip data)
  const handleConfidentialityUpdate = useCallback(() => {
    if (onRefreshComponents) {
      onRefreshComponents();
    }
  }, [onRefreshComponents]);

  // Handle revealing a confidential placeholder component
  const handleRevealConfidential = useCallback(async (componentId) => {
    if (!trip?.id || !componentId) return;

    setRevealingComponentId(componentId);
    try {
      const { getApiBase, getAuthHeaders } = await import('../../utils/api');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.post(
        `${apiBase}/trips/${trip.id}/components/${componentId}/reveal`,
        {},
        { headers }
      );

      // Refresh trip data to get the revealed component
      if (onRefreshComponents) {
        onRefreshComponents();
      }
    } catch (err) {
      console.error('Failed to reveal confidential component:', err);
      // Could add toast notification here
    } finally {
      setRevealingComponentId(null);
    }
  }, [trip?.id, onRefreshComponents]);

  // Handle concealing (hiding) a revealed confidential component
  const handleConcealConfidential = useCallback(async (componentId) => {
    if (!trip?.id || !componentId) return;

    setConcealingComponentId(componentId);
    try {
      const { getApiBase, getAuthHeaders } = await import('../../utils/api');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.post(
        `${apiBase}/trips/${trip.id}/components/${componentId}/conceal`,
        {},
        { headers }
      );

      // Refresh trip data to get the placeholder again
      if (onRefreshComponents) {
        onRefreshComponents();
      }
    } catch (err) {
      console.error('Failed to conceal confidential component:', err);
      // Could add toast notification here
    } finally {
      setConcealingComponentId(null);
    }
  }, [trip?.id, onRefreshComponents]);

  // Local state for timeline2_order to prevent infinite re-renders
  const [timeline2Order, setTimeline2Order] = useState({});

  // Sync timeline2Order from trip prop - update when trip.timeline2_order changes
  // This ensures we get the latest data from the server after refreshes
  useEffect(() => {
    const tripOrder = trip?.timeline2_order;
    if (tripOrder && Object.keys(tripOrder).length > 0) {
      setTimeline2Order(tripOrder);
    }
  }, [trip?.id, trip?.timeline2_order]); // Also depend on timeline2_order changes

  // Initialize and update component order when grouped components change
  useEffect(() => {
    const newOrder = groupedComponents.map(comp => comp.id);
    setComponentOrder(newOrder);
  }, [groupedComponents]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sort components based on current order (use grouped components)
  const sortedComponents = useMemo(() => {
    const componentMap = new Map(groupedComponents.map(comp => [comp.id, comp]));

    // Filter out any IDs that no longer exist in grouped components
    const validOrder = componentOrder.filter(id => componentMap.has(id));

    // Add any new grouped components that aren't in the order yet
    const newComponents = groupedComponents.filter(comp => !validOrder.includes(comp.id));
    const fullOrder = [...validOrder, ...newComponents.map(comp => comp.id)];

    // Update order state if we added new components
    if (fullOrder.length !== componentOrder.length) {
      setComponentOrder(fullOrder);
    }

    return fullOrder.map(id => componentMap.get(id)).filter(Boolean);
  }, [groupedComponents, componentOrder]);

  // Classify components by type and sort chronologically
  const { tbdComponents, fixedComponents, allComponents } = useMemo(() => {
    // Helper function to apply TBD positioning based on saved anchor data
    const applyTBDPositioning = (fixedComponents, tbdComponents, timeline2Order) => {
      // Helper function to find component index by ID (checking both id and originalId)
      const findComponentIndex = (list, targetId) => {
        if (!targetId) return -1;
        return list.findIndex(comp =>
          comp.id === targetId ||
          comp.originalId === targetId ||
          (comp.originalComponent && comp.originalComponent.id === targetId)
        );
      };

      // Build a reference list of ALL components to find anchor positions
      // Fixed components first (in chronological order), then TBD components
      const allComponentsRef = [...fixedComponents, ...tbdComponents];

      // Helper function to calculate where to insert a TBD component
      const calculateTBDInsertPosition = (currentList, anchorData, allRef) => {
        const { primary_anchor_id, secondary_anchor_id, position } = anchorData;

        // First try to find anchors in the current list
        let primaryIndex = findComponentIndex(currentList, primary_anchor_id);
        let secondaryIndex = findComponentIndex(currentList, secondary_anchor_id);

        // If anchor not found in current list, find it in all components reference
        // and calculate relative position based on what IS in current list
        if (primaryIndex === -1 && primary_anchor_id) {
          const refIndex = findComponentIndex(allRef, primary_anchor_id);
          if (refIndex !== -1) {
            // Find the closest component that IS in currentList
            // Look backwards from refIndex to find an anchor that exists in currentList
            for (let i = refIndex - 1; i >= 0; i--) {
              const existingIndex = findComponentIndex(currentList, allRef[i].id);
              if (existingIndex !== -1) {
                primaryIndex = existingIndex;
                break;
              }
            }
          }
        }

        switch (position) {
          case 'before':
            return primaryIndex >= 0 ? primaryIndex : 0;
          case 'after':
            return primaryIndex >= 0 ? primaryIndex + 1 : currentList.length;
          case 'between':
            if (primaryIndex >= 0 && secondaryIndex >= 0) {
              return Math.min(primaryIndex, secondaryIndex) + 1;
            }
            // If only primary found, insert after it
            if (primaryIndex >= 0) {
              return primaryIndex + 1;
            }
            // If only secondary found, insert before it
            if (secondaryIndex >= 0) {
              return secondaryIndex;
            }
            return currentList.length;
          case 'start':
            return 0;
          case 'end':
            return currentList.length;
          default:
            return currentList.length; // Default to end
        }
      };

      // Start with chronologically sorted fixed components as the base
      const result = [...fixedComponents];

      // Process TBD components with saved positioning
      const processedTBD = new Set();

      // Sort TBD components so those with anchor data are processed in a better order
      // Components anchored to fixed components should be processed first
      const tbdWithAnchors = tbdComponents.filter(c => timeline2Order[c.id]);
      const tbdWithoutAnchors = tbdComponents.filter(c => !timeline2Order[c.id]);

      // Process TBD components with anchors first
      tbdWithAnchors.forEach(tbdComp => {
        const anchorData = timeline2Order[tbdComp.id];
        if (anchorData && !processedTBD.has(tbdComp.id)) {
          const insertPosition = calculateTBDInsertPosition(result, anchorData, allComponentsRef);
          result.splice(insertPosition, 0, tbdComp);
          processedTBD.add(tbdComp.id);
        }
      });

      // Append remaining TBD components without saved positions
      tbdWithoutAnchors.forEach(tbdComp => {
        if (!processedTBD.has(tbdComp.id)) {
          result.push(tbdComp);
          processedTBD.add(tbdComp.id);
        }
      });

      return result;
    };

    const getSortTime = (comp) => {
      try {
        // Prefer explicit start_datetime if present
        if (comp?.start_datetime) {
          const t = parseDatetimeForSortValue(comp.start_datetime);
          if (t !== null) return t;
        }
        // Flight leg specific times (use the shared resolver so
        // departure_datetime / full-datetime fields sort correctly)
        if (comp?.isFlightLeg && comp?.flightLeg) {
          const resolved = resolveLegDateTime(comp.flightLeg, 'departure');
          const t = parseDatetimeForSortValue(resolved);
          if (t !== null) return t;
        }
        // Meetings: use meeting_date if present
        const cd = comp?.component_data || {};
        if (cd.meeting_date) {
          const t = parseDatetimeForSortValue(cd.meeting_date);
          if (t !== null) return t;
        }
        // Private flights without explicit start: try departure_date
        if (cd.departure_date) {
          const t = parseDatetimeForSortValue(cd.departure_date);
          if (t !== null) return t;
        }
        // Fall back to end_datetime
        if (comp?.end_datetime) {
          const t = parseDatetimeForSortValue(comp.end_datetime);
          if (t !== null) return t;
        }
      } catch (e) {}
      return Number.POSITIVE_INFINITY; // TBD items go last
    };

    // Sort fixed components chronologically
    const componentsSorted = [...sortedComponents].sort((a, b) => {
      const ta = getSortTime(a);
      const tb = getSortTime(b);
      if (ta === tb) return 0;
      return ta < tb ? -1 : 1;
    });

    const tbd = [];
    const fixed = [];
    componentsSorted.forEach(component => {
      if (isTBDComponent(component)) {
        tbd.push(component);
      } else {
        fixed.push(component);
      }
    });

    // Apply timeline2_order positioning for TBD components
    const integratedComponents = applyTBDPositioning(fixed, tbd, timeline2Order);

    return {
      tbdComponents: tbd,
      fixedComponents: fixed,
      allComponents: integratedComponents
    };
  }, [sortedComponents, timeline2Order]); // Use local timeline2Order state instead of trip?.timeline2_order

  // Jump to today (or next upcoming) in compact mode
  const handleJumpToToday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = allComponents.find(c => {
      if (!c.start_datetime) return false;
      const d = new Date(c.start_datetime);
      return d >= today;
    });
    if (target && compactRowRefs.current[target.id]) {
      compactRowRefs.current[target.id].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [allComponents]);

  // Derive distinct component types that exist in the current timeline (for filter UI)
  const availableTypesList = useMemo(() => {
    const TYPE_LABELS = {
      hotel: 'Hotel',
      meeting: 'Meeting',
      speaker_meeting: 'Speaker Meeting',
      restaurant: 'Restaurant',
      private_flight: 'Private Flight',
      commercial_flight: 'Commercial Flight',
      rental_car: 'Rental Car',
      chauffeur_service: 'Chauffeur Service',
      airport_vip_service: 'Airport VIP Service',
      memo: 'Memo',
      event: 'Event',
      general: 'General',
    };
    const seen = new Set();
    allComponents.forEach(c => {
      const ct = c.originalComponent?.component_type || c.component_type;
      if (ct) seen.add(ct);
    });
    return Array.from(seen)
      .sort((a, b) => (TYPE_LABELS[a] || a).localeCompare(TYPE_LABELS[b] || b));
  }, [allComponents]);

  // ?bookTransfer=<timeline item id>[&bookDirection=to|from] (from the executive
  // timeline's cards) starts that transfer's booking once the timeline loads.
  const bookTransferHandled = useRef(false);
  useEffect(() => {
    if (bookTransferHandled.current || isReadOnly || allComponents.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('bookTransfer');
    if (!id) return;
    bookTransferHandled.current = true;
    const direction = params.get('bookDirection');
    params.delete('bookTransfer');
    params.delete('bookDirection');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    const index = allComponents.findIndex(c => c.id === id);
    if (index < 0) return;
    const item = allComponents[index];
    if (item.isHotelEvent) {
      const { from, to } = hotelTransferEnds(allComponents, index, isTransferStop);
      bookTransferBetween(from, to);
      return;
    }
    // Other items (designed cards): a ride to them from the stop before, or
    // with &bookDirection=from, away from them to the stop after.
    if (!isTransferStop(item)) return;
    const isOther = c => (c.originalId || c.id) !== (item.originalId || item.id) && isTransferStop(c);
    if (direction === 'from') {
      bookTransferBetween(item, allComponents.slice(index + 1).find(isOther) || null);
    } else {
      bookTransferBetween(allComponents.slice(0, index).reverse().find(isOther) || null, item);
    }
  }, [allComponents]);

  // Function to persist TBD positioning to backend
  const persistTBDPositioning = async (componentId, anchorData) => {
    try {
      // Construct full URL to ensure proper routing
      const { getApiBase, getAuthHeaders } = await import('../../utils/api');
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const url = `${apiBase}/trips/${trip.id}`;

      await axios.put(url, {
        timeline2_order: {
          [componentId]: anchorData
        }
      }, { headers });
    } catch (error) {
      console.error('Failed to persist TBD position:', error);
      throw error;
    }
  };

  // Enhanced drag end handler with anchor calculation and persistence
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    // View-only mode: never persist reordering
    if (isReadOnly) {
      return;
    }

    // If no drop target detected, exit early
    if (!over) {
      return;
    }

    // Check if this is a sticky note being dragged
    if (active.id.startsWith('sticky-note-')) {
      const noteId = active.id.replace('sticky-note-', '');

      // Calculate new position based on drop target
      let newPosition;
      if (over.id === 'timeline-start') {
        newPosition = { position_type: 'start', rank: 0 };
      } else if (over.id === 'timeline-end') {
        newPosition = { position_type: 'end', rank: 0 };
      } else if (over.id.startsWith('drop-after-')) {
        const afterComponentId = over.id.replace('drop-after-', '');
        // Find the next component after this one
        const afterIndex = allComponents.findIndex(c => c.id === afterComponentId);
        const nextComponent = allComponents[afterIndex + 1];
        if (nextComponent) {
          newPosition = {
            position_type: 'between',
            anchor_before_id: afterComponentId,
            anchor_after_id: nextComponent.id,
            rank: 0
          };
        } else {
          // No next component, position at end
          newPosition = { position_type: 'end', rank: 0 };
        }
      } else if (over.id.startsWith('sticky-note-')) {
        // Dropped on another sticky note - same position
        return;
      } else {
        // Dropped on a component - place inside it with calculated offset
        // Calculate position as percentage within the component
        let offset_x = 5; // Default to 5% from left
        let offset_y = 5; // Default to 5% from top

        // Try to calculate actual drop position if we have rect info
        if (over.rect && active.rect?.current?.translated) {
          const componentRect = over.rect;
          const noteRect = active.rect.current.translated;

          // Calculate relative position as percentage
          const relativeX = noteRect.left - componentRect.left;
          const relativeY = noteRect.top - componentRect.top;

          offset_x = Math.max(0, Math.min(80, (relativeX / componentRect.width) * 100));
          offset_y = Math.max(0, Math.min(80, (relativeY / componentRect.height) * 100));
        }

        newPosition = {
          position_type: 'inside',
          attached_to_component_id: over.id,
          rank: 0,
          offset_x: Math.round(offset_x),
          offset_y: Math.round(offset_y)
        };
      }

      // Update the sticky note position
      await handleRepositionNote(noteId, newPosition);
      return;
    }

    if (active.id !== over.id) {
      // Get the component being moved
      const activeComponent = allComponents.find(c => c.id === active.id);
      if (!activeComponent || !isTBDComponent(activeComponent)) {
        return; // Only TBD components can be repositioned
      }

      // Capture original order for potential revert
      const originalOrder = [...componentOrder];

      // Find the current position of the dragged component in componentOrder
      const oldIdx = componentOrder.indexOf(active.id);
      if (oldIdx === -1) {
        return; // Component not found in order
      }

      let newOrder = [...componentOrder];
      let finalNewIndex;

      // Handle drop targets - calculate where to insert based on componentOrder
      if (over.id === 'timeline-start') {
        // Insert at the very beginning
        newOrder.splice(oldIdx, 1);
        newOrder.unshift(active.id);
        finalNewIndex = 0;
      } else if (over.id === 'timeline-end') {
        // Insert at the very end
        newOrder.splice(oldIdx, 1);
        newOrder.push(active.id);
        finalNewIndex = newOrder.length - 1;
      } else if (over.id.startsWith('drop-after-')) {
        // Drop after a specific component - find that component in componentOrder
        const afterComponentId = over.id.replace('drop-after-', '');
        const afterIdxInOrder = componentOrder.indexOf(afterComponentId);

        if (afterIdxInOrder === -1) {
          // Target component not found in order, abort
          return;
        }

        // Remove from current position
        newOrder.splice(oldIdx, 1);

        // Calculate insertion point - adjust if we removed from before the target
        let insertIdx = afterIdxInOrder;
        if (oldIdx < afterIdxInOrder) {
          // We removed from before the target, so the target index shifted down by 1
          insertIdx = afterIdxInOrder; // Insert after the component (which is now at afterIdxInOrder - 1 after removal, so insert at afterIdxInOrder)
        } else {
          // We removed from after the target, so insert right after the target
          insertIdx = afterIdxInOrder + 1;
        }

        newOrder.splice(insertIdx, 0, active.id);
        finalNewIndex = insertIdx;
      } else {
        // Regular component drop - find the target component in componentOrder
        const targetIdx = componentOrder.indexOf(over.id);
        if (targetIdx === -1) {
          return; // Target not found
        }

        newOrder.splice(oldIdx, 1);
        const insertIdx = oldIdx < targetIdx ? targetIdx - 1 : targetIdx;
        newOrder.splice(insertIdx, 0, active.id);
        finalNewIndex = insertIdx;
      }

      // Determine the new anchor. An explicit drop on the start/end zone pins
      // the item to the very top/bottom with a 'start'/'end' anchor -
      // calculateAnchorDataFromOrder would instead anchor it 'before'/'after' a
      // specific fixed component, which fails to reach the very top when the
      // first fixed component itself moves around.
      let anchorData;
      if (over.id === 'timeline-start') {
        anchorData = { primary_anchor_id: null, secondary_anchor_id: null, position: 'start', rank: 0 };
      } else if (over.id === 'timeline-end') {
        anchorData = { primary_anchor_id: null, secondary_anchor_id: null, position: 'end', rank: 0 };
      } else {
        anchorData = calculateAnchorDataFromOrder(newOrder, allComponents, activeComponent.id, finalNewIndex);
      }

      // The displayed position is driven by the saved anchor, not componentOrder
      // alone, so only bail when BOTH are unchanged. (A TBD item can already sit
      // at componentOrder[0] while displayed lower via a stale anchor - gating on
      // order alone would then swallow a real move to the top.)
      const prevAnchor = timeline2Order[active.id];
      const anchorUnchanged = !!prevAnchor &&
        prevAnchor.position === anchorData.position &&
        (prevAnchor.primary_anchor_id ?? null) === (anchorData.primary_anchor_id ?? null) &&
        (prevAnchor.secondary_anchor_id ?? null) === (anchorData.secondary_anchor_id ?? null);
      if (newOrder.join(',') === originalOrder.join(',') && anchorUnchanged) {
        return;
      }

      // Update local order immediately for responsive UI
      setComponentOrder(newOrder);

      try {
        // Persist to backend
        await persistTBDPositioning(activeComponent.id, anchorData);

        // Update local state to reflect the change
        setTimeline2Order(prev => ({
          ...prev,
          [activeComponent.id]: anchorData
        }));
      } catch (error) {
        console.error('Failed to save TBD position:', error);

        // Revert the local UI changes instead of reloading the page
        setComponentOrder(originalOrder);

        // Show user-friendly error message (you could use a toast notification here)
        alert('Failed to save timeline position. Please try again.');
      }
    }
  };

  // Function to calculate anchor data based on new component order
  const calculateAnchorDataFromOrder = (newOrder, allComponents, draggedComponentId, newIndex) => {
    // Create a map of components for quick lookup
    const componentMap = new Map(allComponents.map(comp => [comp.id, comp]));

    // Map the new order to actual components
    const orderedComponents = newOrder.map(id => componentMap.get(id)).filter(Boolean);

    // Get only fixed components as potential anchors
    const fixedComponents = orderedComponents.filter(c => !isTBDComponent(c));

    if (fixedComponents.length === 0) {
      return {
        primary_anchor_id: null,
        secondary_anchor_id: null,
        position: 'start',
        rank: 0
      };
    }

    // Find the fixed components before and after the dragged component's new position
    let beforeComponent = null;
    let afterComponent = null;

    // Search backward from the new position
    for (let i = newIndex - 1; i >= 0; i--) {
      const comp = orderedComponents[i];
      if (comp && comp.id !== draggedComponentId && !isTBDComponent(comp)) {
        beforeComponent = comp;
        break;
      }
    }

    // Search forward from the new position
    for (let i = newIndex + 1; i < orderedComponents.length; i++) {
      const comp = orderedComponents[i];
      if (comp && comp.id !== draggedComponentId && !isTBDComponent(comp)) {
        afterComponent = comp;
        break;
      }
    }

    // Calculate anchor data based on surrounding fixed components
    if (beforeComponent && afterComponent) {
      return {
        primary_anchor_id: beforeComponent.id,
        secondary_anchor_id: afterComponent.id,
        position: 'between',
        rank: 0
      };
    } else if (beforeComponent) {
      return {
        primary_anchor_id: beforeComponent.id,
        secondary_anchor_id: null,
        position: 'after',
        rank: 0
      };
    } else if (afterComponent) {
      return {
        primary_anchor_id: afterComponent.id,
        secondary_anchor_id: null,
        position: 'before',
        rank: 0
      };
    } else {
      return {
        primary_anchor_id: null,
        secondary_anchor_id: null,
        position: newIndex === 0 ? 'start' : 'end',
        rank: 0
      };
    }
  };

  // Derive trip executives for ID->name mapping in cards
  // Provide full executive directory for reliable ID->name mapping on cards
  const tripExecs = useMemo(() => (Array.isArray(executives) ? executives : []), [Array.isArray(executives) ? executives.map(e => e.id).join(',') : '']);

  const enrichedTrip = useMemo(() => ({
    ...(trip || {}),
    executives: tripExecs
  }), [trip?.id, tripExecs.map(e => e.id).join(',')]);

  // Refs to track previous trigger values to prevent infinite loops
  const prevTriggerPdfSelectAllRef = useRef(0);
  const prevTriggerPdfExportRef = useRef(0);

  // Watch for triggerPdfSelectAll prop changes from compact header
  useEffect(() => {
    // Only run if the trigger value actually increased (not on other re-renders)
    if (triggerPdfSelectAll > prevTriggerPdfSelectAllRef.current && allComponents) {
      prevTriggerPdfSelectAllRef.current = triggerPdfSelectAll;
      // Toggle: if all selected, deselect all; otherwise select all
      const allIds = allComponents.map(c => c.id);
      if (pdfSelectedItems.size === allIds.length) {
        setPdfSelectedItems(new Set());
      } else {
        setPdfSelectedItems(new Set(allIds));
      }
    }
  }, [triggerPdfSelectAll, allComponents, pdfSelectedItems.size, setPdfSelectedItems]);

  // Watch for triggerPdfExport prop changes from compact header
  useEffect(() => {
    // Only run if the trigger value actually increased (not on other re-renders)
    if (triggerPdfExport > prevTriggerPdfExportRef.current && allComponents && pdfSelectedItems.size > 0) {
      prevTriggerPdfExportRef.current = triggerPdfExport;
      generateTimelinePdf(pdfSelectedItems, allComponents, trip, stickyNotes, pdfFormat);
    }
  }, [triggerPdfExport, allComponents, pdfSelectedItems, trip, stickyNotes, generateTimelinePdf, pdfFormat]);

  return (
    <div className={hideHeader ? "space-y-2" : "space-y-4 sm:space-y-6"}>
      {/* Sticky Header - hidden when using compact header mode */}
      {!hideHeader && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-ui-violet-bg rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-ui-violet-line sticky top-0 z-40 shadow-sm"
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-ui-violet-strong rounded-lg sm:rounded-xl text-ui-on-primary flex-shrink-0">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-ui-ink">Timeline</h2>
                {urbanrideSelectionMode && (
                  <p className="text-xs sm:text-sm text-ui-ink-3 line-clamp-2 sm:line-clamp-none">
                    Tap "Add to Itinerary" to build your route
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
              {/* Add Note Button - only show when there are components */}
              {!urbanrideSelectionMode && !pdfExportMode && allComponents?.length > 0 && (
                <button
                  onClick={() => handleCreateNote()}
                  className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-medium text-ui-amber-fg bg-ui-amber-bg hover:bg-ui-amber-line/60 active:bg-ui-amber-line border border-ui-amber-line rounded-lg transition-colors touch-target"
                  title="Add a sticky note to the timeline"
                >
                  <StickyNoteIcon className="w-4 h-4" />
                  <span className="hidden xs:inline">Add</span> Note
                </button>
              )}
              {/* PDF Export Button - only show when there are components */}
              {!urbanrideSelectionMode && !pdfExportMode && allComponents?.length > 0 && (
                <button
                  onClick={() => {
                    handleEnterPdfExportMode();
                    // Select all items when entering PDF mode (using allComponents from closure)
                    setTimeout(() => {
                      const allIds = allComponents.map(c => c.id);
                      selectAllPdfItems(allIds);
                    }, 0);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-medium text-ui-violet-fg bg-ui-violet-bg hover:bg-ui-violet-line/60 active:bg-ui-violet-line border border-ui-violet-line rounded-lg transition-colors touch-target"
                  title="Export timeline as PDF"
                >
                  <FileDown className="w-4 h-4" />
                  <span className="hidden xs:inline">Export</span> PDF
                </button>
              )}
              {/* Urbanride Button - hidden in PDF export mode, requires urbanride_quickbook feature */}
              {!pdfExportMode && urbanrideQuickbookEnabled && (
                <Button
                  onClick={toggleUrbanrideSelectionMode}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-all touch-target active:scale-[0.98] ${
                    urbanrideSelectionMode
                      ? 'bg-ui-primary text-ui-on-primary hover:bg-ui-primary-hover'
                      : 'bg-ui-surface border border-ui-control text-ui-ink-2 hover:bg-ui-hover hover:border-ui-blue-solid'
                  }`}
                >
                  <Car className="w-4 h-4" />
                  <span className="hidden xs:inline">Urbanride</span>
                  <span className="xs:hidden">Ride</span>
                  {urbanrideSelectionMode && (
                    <X className="w-4 h-4 ml-1" />
                  )}
                </Button>
              )}
              {/* ── Type Filter (hidden for view-only speakers — their small
                     timeline doesn't need narrowing) ──────────────────────── */}
              {!urbanrideSelectionMode && !pdfExportMode && !isReadOnly && allComponents?.length > 0 && (
                <div className="relative" ref={typeFilterRef}>
                  <button
                    onClick={() => { setShowTypeFilterDropdown(v => !v); setShowDateFilterDropdown(false); }}
                    className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-medium border rounded-lg transition-colors touch-target ${
                      typeFilters.size > 0
                        ? 'bg-ui-dash text-ui-ink border-ui-faint'
                        : 'text-ui-ink-2 bg-ui-hover border-ui-line hover:bg-ui-control'
                    }`}
                    title="Filter by component type"
                  >
                    <CheckSquare className="w-4 h-4" />
                    <span className="hidden xs:inline">
                      {typeFilters.size > 0 ? `${availableTypesList.length - typeFilters.size}/${availableTypesList.length} types` : 'Types'}
                    </span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showTypeFilterDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showTypeFilterDropdown && (() => {
                    const TYPE_LABELS = {
                      hotel: 'Hotel', meeting: 'Meeting', speaker_meeting: 'Speaker Meeting', restaurant: 'Restaurant',
                      private_flight: 'Private Flight', commercial_flight: 'Commercial Flight',
                      rental_car: 'Rental Car', chauffeur_service: 'Chauffeur Service',
                      airport_vip_service: 'Airport VIP Service',
                      memo: 'Memo', event: 'Event', general: 'General',
                    };
                    const TYPE_COLORS = {
                      hotel: 'rgb(var(--ui-violet-solid))', meeting: 'rgb(var(--ui-green-solid))', speaker_meeting: 'rgb(var(--ui-red-solid))', restaurant: 'rgb(var(--ui-orange-solid))',
                      private_flight: 'rgb(var(--ui-violet-solid))', commercial_flight: 'rgb(var(--ui-blue-solid))',
                      rental_car: 'rgb(var(--ui-green-solid))', chauffeur_service: 'rgb(var(--ui-teal-solid))',
                      airport_vip_service: 'rgb(var(--ui-amber-solid))',
                      memo: 'rgb(var(--ui-amber-solid))', event: 'rgb(var(--ui-pink-solid))', general: 'rgb(var(--ui-faint))',
                    };
                    return (
                      <div className="absolute right-0 top-full mt-1 w-56 bg-ui-raised border border-ui-line rounded-xl shadow-xl z-50 overflow-hidden">
                        <div className="px-3 py-2 border-b border-ui-divider flex items-center justify-between">
                          <span className="text-xs font-semibold text-ui-muted uppercase tracking-wide">Component Types</span>
                          {typeFilters.size > 0 && (
                            <button
                              onClick={() => setTypeFilters(new Set())}
                              className="text-xs text-ui-violet-fg font-medium"
                            >
                              Show all
                            </button>
                          )}
                        </div>
                        <div className="py-1 max-h-64 overflow-y-auto">
                          {availableTypesList.map(ct => {
                            const isExcluded = typeFilters.has(ct);
                            return (
                              <button
                                key={ct}
                                onClick={() => {
                                  setTypeFilters(prev => {
                                    const next = new Set(prev);
                                    if (next.has(ct)) next.delete(ct); else next.add(ct);
                                    return next;
                                  });
                                }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                                  isExcluded ? 'opacity-50 bg-ui-sunk' : 'hover:bg-ui-hover'
                                }`}
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: TYPE_COLORS[ct] || 'rgb(var(--ui-faint))' }}
                                />
                                <span className={`flex-1 ${isExcluded ? 'line-through text-ui-faint' : 'text-ui-ink-2'}`}>
                                  {TYPE_LABELS[ct] || ct}
                                </span>
                                {!isExcluded && <Check className="w-3.5 h-3.5 text-ui-faint" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ── Date Filter ──────────────────────────────────────────── */}
              {!urbanrideSelectionMode && !pdfExportMode && allComponents?.length > 0 && (
                <div className="relative" ref={dateFilterRef}>
                  <button
                    onClick={() => { setShowDateFilterDropdown(v => !v); setShowTypeFilterDropdown(false); }}
                    className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-medium border rounded-lg transition-colors touch-target ${
                      dateFilter !== 'all'
                        ? 'bg-ui-dash text-ui-ink border-ui-faint'
                        : 'text-ui-ink-2 bg-ui-hover border-ui-line hover:bg-ui-control'
                    }`}
                    title="Filter by date"
                  >
                    <CalendarIcon className="w-4 h-4" />
                    <span className="hidden xs:inline">
                      {dateFilter === 'today' ? 'Today'
                        : dateFilter === 'tomorrow' ? 'Tomorrow'
                        : dateFilter === 'upcoming' ? 'Upcoming'
                        : dateFilter === 'range'
                          ? (dateFilterRange.start || dateFilterRange.end)
                            ? `${dateFilterRange.start || '…'} – ${dateFilterRange.end || '…'}`
                            : 'Range'
                          : 'Dates'}
                    </span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showDateFilterDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showDateFilterDropdown && (
                    <div className="absolute right-0 top-full mt-1 w-64 bg-ui-raised border border-ui-line rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="px-3 py-2 border-b border-ui-divider">
                        <span className="text-xs font-semibold text-ui-muted uppercase tracking-wide">Date Filter</span>
                      </div>
                      <div className="py-1">
                        {[
                          { key: 'all', label: 'All dates' },
                          { key: 'today', label: 'Today' },
                          { key: 'tomorrow', label: 'Tomorrow' },
                          { key: 'upcoming', label: 'Upcoming' },
                          { key: 'range', label: 'Date range…' },
                        ].map(({ key, label }) => (
                          <button
                            key={key}
                            onClick={() => {
                              setDateFilter(key);
                              if (key !== 'range') setShowDateFilterDropdown(false);
                            }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-ui-hover ${
                              dateFilter === key ? 'text-ui-violet-fg font-medium' : 'text-ui-ink-2'
                            }`}
                          >
                            {dateFilter === key
                              ? <Check className="w-3.5 h-3.5 text-ui-violet-fg" />
                              : <span className="w-3.5 h-3.5" />
                            }
                            {label}
                          </button>
                        ))}
                      </div>
                      {dateFilter === 'range' && (
                        <div className="px-3 pb-3 pt-1 border-t border-ui-divider space-y-2">
                          <div>
                            <label className="block text-xs text-ui-muted mb-1">From</label>
                            <input
                              type="date"
                              value={dateFilterRange.start}
                              onChange={e => setDateFilterRange(prev => ({ ...prev, start: e.target.value }))}
                              className="w-full text-sm border border-ui-line rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-ui-violet-solid"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-ui-muted mb-1">To</label>
                            <input
                              type="date"
                              value={dateFilterRange.end}
                              onChange={e => setDateFilterRange(prev => ({ ...prev, end: e.target.value }))}
                              className="w-full text-sm border border-ui-line rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-ui-violet-solid"
                            />
                          </div>
                          {(dateFilterRange.start || dateFilterRange.end) && (
                            <button
                              onClick={() => { setDateFilterRange({ start: '', end: '' }); setDateFilter('all'); }}
                              className="text-xs text-ui-muted hover:text-ui-ink"
                            >
                              Clear range
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* PDF Export Mode Controls */}
              {pdfExportMode && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const allIds = allComponents.map(c => c.id);
                      if (pdfSelectedItems.size === allIds.length) {
                        deselectAllPdfItems();
                      } else {
                        selectAllPdfItems(allIds);
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-medium text-ui-ink-2 bg-ui-surface hover:bg-ui-hover border border-ui-control rounded-lg transition-colors touch-target"
                  >
                    {pdfSelectedItems.size === allComponents.length ? (
                      <>
                        <Square className="w-4 h-4" />
                        <span className="hidden sm:inline">Deselect All</span>
                      </>
                    ) : (
                      <>
                        <CheckSquare className="w-4 h-4" />
                        <span className="hidden sm:inline">Select All</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => generateTimelinePdf(pdfSelectedItems, allComponents, trip, stickyNotes)}
                    disabled={pdfSelectedItems.size === 0}
                    className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-medium rounded-lg transition-colors touch-target ${
                      pdfSelectedItems.size > 0
                        ? 'text-ui-on-primary bg-ui-primary hover:bg-ui-primary-hover'
                        : 'text-ui-faint bg-ui-hover cursor-not-allowed'
                    }`}
                  >
                    <FileDown className="w-4 h-4" />
                    <span className="hidden sm:inline">Export</span> ({pdfSelectedItems.size})
                  </button>
                  <button
                    onClick={handleExitPdfExportMode}
                    className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm font-medium text-ui-ink-2 bg-ui-surface hover:bg-ui-hover border border-ui-control rounded-lg transition-colors touch-target"
                    title="Cancel PDF export"
                  >
                    <X className="w-4 h-4" />
                    <span className="hidden sm:inline">Cancel</span>
                  </button>
                </div>
              )}
              {!urbanrideSelectionMode && !pdfExportMode && !quickEditComponent && (
                <Timeline2ViewToggle
                  viewMode={viewMode}
                  onToggle={handleViewModeChange}
                />
              )}
            </div>
          </div>

          {/* Selection Mode Instructions - Simplified */}
          {urbanrideSelectionMode && (
            <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-ui-blue-bg border border-ui-blue-line rounded-lg">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-ui-blue-line/50 rounded-lg flex-shrink-0">
                  <Car className="w-4 h-4 sm:w-5 sm:h-5 text-ui-blue-fg" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ui-blue-fg">
                    Build your chauffeur route
                  </p>
                  <p className="text-xs text-ui-blue-fg hidden sm:block">
                    First location = Pickup | Last location = Dropoff | Middle locations = Stops
                  </p>
                  <p className="text-xs text-ui-blue-fg sm:hidden">
                    First = Pickup • Last = Dropoff
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Edit Mode Instructions */}
          {quickEditComponent && !urbanrideSelectionMode && (
            <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-ui-blue-bg border border-ui-blue-line rounded-lg">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-ui-blue-line/50 rounded-lg flex-shrink-0">
                  <Car className="w-4 h-4 sm:w-5 sm:h-5 text-ui-blue-fg" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ui-blue-fg">
                    Editing chauffeur route
                  </p>
                  <p className="text-xs text-ui-blue-fg">
                    Click cards to add locations to your itinerary
                  </p>
                </div>
                <button
                  onClick={handleQuickEditCancel}
                  className="flex-shrink-0 text-ui-blue-fg/70 hover:text-ui-blue-fg p-1"
                  title="Close quick edit"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Bundle Selection Mode Instructions */}
          {bundleSelectionMode && !urbanrideSelectionMode && !quickEditComponent && (
            <div className={`mt-3 sm:mt-4 p-2.5 sm:p-3 rounded-lg border bg-ui-violet-bg border-ui-violet-line`}>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 bg-ui-violet-line/50`}>
                  <FolderOpen className={`w-4 h-4 sm:w-5 sm:h-5 text-ui-violet-fg`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium text-ui-violet-fg`}>
                    Managing bundle: {bundleSelectionMode.title}
                  </p>
                  <p className={`text-xs text-ui-violet-fg`}>
                    Click items to add or remove them from this bundle
                  </p>
                </div>
                <button
                  onClick={handleBundleCancel}
                  className={`flex-shrink-0 p-1 rounded transition-colors text-ui-violet-fg hover:bg-ui-violet-line/50`}
                  title="Cancel bundle editing"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* PDF Export Mode Instructions */}
          {pdfExportMode && (
            <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-ui-violet-bg border border-ui-violet-line rounded-lg">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-ui-violet-line/50 rounded-lg flex-shrink-0">
                  <FileDown className="w-4 h-4 sm:w-5 sm:h-5 text-ui-violet-fg" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ui-violet-fg">
                    Export Timeline to PDF
                  </p>
                  <p className="text-xs text-ui-violet-fg hidden sm:block">
                    Uncheck items you want to exclude from the PDF, then click Export
                  </p>
                  <p className="text-xs text-ui-violet-fg sm:hidden">
                    Uncheck to exclude • Click Export when ready
                  </p>
                </div>
              </div>
            </div>
          )}

        </motion.div>
      )}

      {/* Urbanride selection mode banner - shown when header is hidden but mode is active */}
      {hideHeader && urbanrideSelectionMode && (
        <div className="p-2.5 sm:p-3 bg-ui-blue-bg border border-ui-blue-line rounded-lg mx-1">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-ui-blue-line/50 rounded-lg flex-shrink-0">
              <Car className="w-4 h-4 sm:w-5 sm:h-5 text-ui-blue-fg" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ui-blue-fg">
                Build your chauffeur route
              </p>
              <p className="text-xs text-ui-blue-fg hidden sm:block">
                Tap "Add to Itinerary" on components • First = Pickup | Last = Dropoff | Middle = Stops
              </p>
              <p className="text-xs text-ui-blue-fg sm:hidden">
                First = Pickup • Last = Dropoff
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Urbanride Quick Booking Modal */}
      {showUrbanrideBooking && (
        <UrbanrideQuickBooking
          selectedComponents={selectedComponents}
          tripId={trip?.id}
          tripExecutives={tripExecs}
          allExecutives={allExecutives}
          onSuccess={handleUrbanrideBookingSuccess}
          onCancel={handleUrbanrideBookingCancel}
          onUpdateComponents={onRefreshComponents}
        />
      )}

      {/* ── Inline filter bar (shown when header is hidden, e.g. TripDetails2 compact header mode) ── */}
      {hideHeader && !urbanrideSelectionMode && !pdfExportMode && !quickEditComponent && allComponents?.length > 0 && (
        <div className={`flex items-center gap-2 flex-wrap px-1`}>
          {/* Type Filter (hidden for view-only speakers) */}
          {!isReadOnly && (
          <div className="relative" ref={typeFilterRef}>
            <button
              onClick={() => { setShowTypeFilterDropdown(v => !v); setShowDateFilterDropdown(false); }}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
                typeFilters.size > 0
                  ? 'bg-ui-dash text-ui-ink border-ui-faint'
                  : 'text-ui-ink-2 bg-ui-hover border-ui-line hover:bg-ui-control'
              }`}
              title="Filter by component type"
            >
              <CheckSquare className="w-3.5 h-3.5" />
              <span>
                {typeFilters.size > 0 ? `${availableTypesList.length - typeFilters.size}/${availableTypesList.length}` : 'Types'}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showTypeFilterDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showTypeFilterDropdown && (() => {
              const TYPE_LABELS = {
                hotel: 'Hotel', meeting: 'Meeting', restaurant: 'Restaurant',
                private_flight: 'Private Flight', commercial_flight: 'Commercial Flight',
                rental_car: 'Rental Car', chauffeur_service: 'Chauffeur Service',
                airport_vip_service: 'Airport VIP Service',
                memo: 'Memo', event: 'Event', general: 'General',
              };
              const TYPE_COLORS = {
                hotel: 'rgb(var(--ui-violet-solid))', meeting: 'rgb(var(--ui-green-solid))', restaurant: 'rgb(var(--ui-orange-solid))',
                private_flight: 'rgb(var(--ui-violet-solid))', commercial_flight: 'rgb(var(--ui-blue-solid))',
                rental_car: 'rgb(var(--ui-green-solid))', chauffeur_service: 'rgb(var(--ui-teal-solid))',
                airport_vip_service: 'rgb(var(--ui-amber-solid))',
                memo: 'rgb(var(--ui-amber-solid))', event: 'rgb(var(--ui-pink-solid))', general: 'rgb(var(--ui-faint))',
              };
              return (
                <div className={`absolute left-0 top-full mt-1 w-56 border rounded-xl shadow-xl z-50 overflow-hidden bg-ui-raised border-ui-line`}>
                  <div className={`px-3 py-2 border-b flex items-center justify-between border-ui-divider`}>
                    <span className={`text-xs font-semibold uppercase tracking-wide text-ui-ink-3`}>Component Types</span>
                    {typeFilters.size > 0 && (
                      <button onClick={() => setTypeFilters(new Set())} className="text-xs text-ui-violet-fg font-medium">
                        Show all
                      </button>
                    )}
                  </div>
                  <div className="py-1 max-h-64 overflow-y-auto">
                    {availableTypesList.map(ct => {
                      const isExcluded = typeFilters.has(ct);
                      return (
                        <button
                          key={ct}
                          onClick={() => {
                            setTypeFilters(prev => {
                              const next = new Set(prev);
                              next.has(ct) ? next.delete(ct) : next.add(ct);
                              return next;
                            });
                          }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                            isExcluded
                              ? 'opacity-50 bg-ui-hover'
                              : 'hover:bg-ui-hover'
                          }`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: TYPE_COLORS[ct] || 'rgb(var(--ui-faint))' }} />
                          <span className={`flex-1 ${isExcluded ? 'line-through' : ''} text-ui-ink-2`}>
                            {TYPE_LABELS[ct] || ct}
                          </span>
                          {!isExcluded && <Check className={`w-3.5 h-3.5 text-ui-muted`} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
          )}

          {/* Date Filter */}
          <div className="relative" ref={dateFilterRef}>
            <button
              onClick={() => { setShowDateFilterDropdown(v => !v); setShowTypeFilterDropdown(false); }}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
                dateFilter !== 'all'
                  ? 'bg-ui-dash text-ui-ink border-ui-faint'
                  : 'text-ui-ink-2 bg-ui-hover border-ui-line hover:bg-ui-control'
              }`}
              title="Filter by date"
            >
              <CalendarIcon className="w-3.5 h-3.5" />
              <span>
                {dateFilter === 'today' ? 'Today'
                  : dateFilter === 'tomorrow' ? 'Tomorrow'
                  : dateFilter === 'upcoming' ? 'Upcoming'
                  : dateFilter === 'range'
                    ? (dateFilterRange.start || dateFilterRange.end ? `${dateFilterRange.start || '…'} – ${dateFilterRange.end || '…'}` : 'Range')
                    : 'Dates'}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showDateFilterDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showDateFilterDropdown && (
              <div className={`absolute left-0 top-full mt-1 w-64 border rounded-xl shadow-xl z-50 overflow-hidden bg-ui-raised border-ui-line`}>
                <div className={`px-3 py-2 border-b border-ui-divider`}>
                  <span className={`text-xs font-semibold uppercase tracking-wide text-ui-ink-3`}>Date Filter</span>
                </div>
                <div className="py-1">
                  {[
                    { key: 'all', label: 'All dates' },
                    { key: 'today', label: 'Today' },
                    { key: 'tomorrow', label: 'Tomorrow' },
                    { key: 'upcoming', label: 'Upcoming' },
                    { key: 'range', label: 'Date range…' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => {
                        setDateFilter(key);
                        if (key !== 'range') setShowDateFilterDropdown(false);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-ui-hover ${dateFilter === key ? 'text-ui-violet-fg font-medium' : 'text-ui-ink-2'}`}
                    >
                      {dateFilter === key
                        ? <Check className="w-3.5 h-3.5 text-ui-violet-fg" />
                        : <span className="w-3.5 h-3.5" />
                      }
                      {label}
                    </button>
                  ))}
                </div>
                {dateFilter === 'range' && (
                  <div className={`px-3 pb-3 pt-1 border-t space-y-2 border-ui-divider`}>
                    <div>
                      <label className={`block text-xs mb-1 text-ui-ink-3`}>From</label>
                      <input
                        type="date"
                        value={dateFilterRange.start}
                        onChange={e => setDateFilterRange(prev => ({ ...prev, start: e.target.value }))}
                        className={`w-full text-sm border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-ui-violet-solid bg-ui-sunk border-ui-control text-ui-ink`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 text-ui-ink-3`}>To</label>
                      <input
                        type="date"
                        value={dateFilterRange.end}
                        onChange={e => setDateFilterRange(prev => ({ ...prev, end: e.target.value }))}
                        className={`w-full text-sm border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-ui-violet-solid bg-ui-sunk border-ui-control text-ui-ink`}
                      />
                    </div>
                    {(dateFilterRange.start || dateFilterRange.end) && (
                      <button
                        onClick={() => { setDateFilterRange({ start: '', end: '' }); setDateFilter('all'); }}
                        className={`text-xs hover:underline text-ui-ink-3 hover:text-ui-ink`}
                      >
                        Clear range
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Active filter clear-all */}
          {(typeFilters.size > 0 || dateFilter !== 'all') && (
            <button
              onClick={() => { setTypeFilters(new Set()); setDateFilter('all'); setDateFilterRange({ start: '', end: '' }); }}
              className="text-xs text-ui-violet-fg font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Unified Timeline Content - Split layout when Urbanride mode or Quick Edit is active */}
      <div className={`${(urbanrideSelectionMode || quickEditComponent || bundleSelectionMode) ? 'flex flex-col lg:flex-row gap-4 lg:gap-6' : ''}`}>
        {/* Left side: Timeline (full width normally, narrower in selection mode) */}
        <div className={`${(urbanrideSelectionMode || quickEditComponent || bundleSelectionMode) ? 'flex-1 transition-all duration-300 pb-48 lg:pb-0' : 'w-full'}`}>
          <div className="space-y-4">
            {/* Unified Timeline */}
            {allComponents.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                layout
              >
                <div className={`rounded-lg sm:rounded-xl p-2 sm:p-4 border mb-2 sm:mb-4 bg-ui-surface border-ui-line`}>
                  {urbanrideSelectionMode && (
                    <>
                      <h3 className={`text-base sm:text-lg font-semibold mb-1.5 sm:mb-2 flex items-center gap-2 text-ui-ink`}>
                        <CalendarIcon className={`w-4 h-4 text-ui-blue-fg`} />
                        Select Locations
                      </h3>
                      <p className={`text-xs sm:text-sm mb-3 sm:mb-4 text-ui-ink-3`}>
                        Tap "Add to Itinerary" on locations to include them in your route.
                      </p>
                    </>
                  )}

                  {/* Active filter indicator */}
                  {(typeFilters.size > 0 || dateFilter !== 'all') && (
                    <div className="flex items-center gap-2 px-1 mb-2 flex-wrap">
                      <span className="text-xs text-ui-muted">Filtered:</span>
                      {dateFilter !== 'all' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-ui-hover text-ui-ink-3 rounded-full text-xs">
                          {dateFilter === 'today' ? 'Today'
                            : dateFilter === 'tomorrow' ? 'Tomorrow'
                            : dateFilter === 'upcoming' ? 'Upcoming'
                            : dateFilter === 'range'
                              ? `${dateFilterRange.start || '…'} – ${dateFilterRange.end || '…'}`
                              : dateFilter}
                          <button onClick={() => { setDateFilter('all'); setDateFilterRange({ start: '', end: '' }); }} className="ml-0.5 hover:text-ui-ink">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {typeFilters.size > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-ui-hover text-ui-ink-3 rounded-full text-xs">
                          {typeFilters.size} type{typeFilters.size > 1 ? 's' : ''} hidden
                          <button onClick={() => setTypeFilters(new Set())} className="ml-0.5 hover:text-ui-ink">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      <button
                        onClick={() => { setTypeFilters(new Set()); setDateFilter('all'); setDateFilterRange({ start: '', end: '' }); }}
                        className="text-xs text-ui-violet-fg font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}

                  {/* Compact view navigation bar */}
                  {viewMode === 'compact' && !pdfExportMode && (
                    <div className={`flex items-center gap-3 px-1 mb-2 pb-2 border-b border-ui-line`}>
                      <span className={`text-xs text-ui-ink-3`}>
                        {allComponents.length} item{allComponents.length !== 1 ? 's' : ''}
                      </span>
                      {!urbanrideSelectionMode && (
                        <button
                          onClick={handleJumpToToday}
                          className="text-xs text-ui-violet-fg font-medium underline underline-offset-2"
                        >
                          Jump to today
                        </button>
                      )}
                      {urbanrideSelectionMode && selectedComponents?.length > 0 && (
                        <span className={`text-xs font-medium text-ui-blue-fg`}>
                          {selectedComponents.length} selected
                        </span>
                      )}
                      <div className="flex-1" />
                      {!urbanrideSelectionMode && (compactExpandedIds.size > 0 ? (
                        <button
                          onClick={() => setCompactExpandedIds(new Set())}
                          className={`text-xs font-medium text-ui-ink-3 hover:text-ui-ink`}
                        >
                          Collapse all
                        </button>
                      ) : (
                        <button
                          onClick={() => setCompactExpandedIds(new Set(allComponents.map(c => c.id)))}
                          className={`text-xs font-medium text-ui-ink-3 hover:text-ui-ink`}
                        >
                          Expand all
                        </button>
                      ))}
                    </div>
                  )}

                  <DndContext
                    sensors={sensors}
                    collisionDetection={customCollisionDetection}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={[...allComponents.map(c => c.id), ...stickyNotes.map(n => `sticky-note-${n.id}`)]}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className={viewMode === 'compact' ? 'space-y-0.5' : 'space-y-2 sm:space-y-3'}>
                        {/* Start drop zone - hidden in selection mode, PDF export mode, compact mode, and read-only view */}
                        {!urbanrideSelectionMode && !pdfExportMode && viewMode !== 'compact' && !isReadOnly && (
                          <DroppableZone
                            id="timeline-start"
                            className={`h-12 sm:h-20 w-full border-2 border-dashed transition-colors rounded-lg my-2 sm:my-4 bg-ui-violet-bg border-ui-violet-line hover:border-ui-violet-solid hover:bg-ui-violet-line/40`}
                          >
                            <div className={`text-xs sm:text-sm text-center py-2.5 sm:py-6 font-semibold text-ui-violet-fg`}>Drop here to place at start</div>
                          </DroppableZone>
                        )}

                        {/* Sticky notes at start of timeline (not shown in compact mode) */}
                        {viewMode !== 'compact' && !urbanrideSelectionMode && !pdfExportMode && getNotesAtPosition('start').map(note => (
                          <StickyNote
                            key={note.id}
                            note={note}
                            onToggleExpand={handleToggleNoteExpand}
                            onEdit={handleEditNote}
                            onDelete={handleDeleteNote}
                            isDraggable={!isReadOnly}
                            currentUserId={user?.id}
                          />
                        ))}

                        {/* Insert button at start of timeline */}
                        {!urbanrideSelectionMode && !pdfExportMode && allComponents.length > 0 && (
                          <InsertComponentButton
                            onClick={() => handleOpenQuickAdd({
                              position: 'start',
                              afterComponentId: allComponents.filter(c => viewMode === 'full' || !hiddenComponents.has(c.id))[0]?.id
                            })}
                          />
                        )}

                        {allComponents
                          .filter(component => {
                            // Bundle children are always hidden from the main timeline —
                            // they appear in the sub-timeline inside their bundle card.
                            if (component.bundle_id) return false;
                            // viewMode hidden-component filter
                            if (viewMode !== 'full' && hiddenComponents.has(component.id)) return false;
                            // Component type filter (typeFilters = set of excluded types)
                            if (typeFilters.size > 0) {
                              const ct = component.originalComponent?.component_type || component.component_type;
                              if (typeFilters.has(ct)) return false;
                            }
                            // Date filter - shared semantics with the exec
                            // dashboard timeline (utils/timelineFilters):
                            // "Upcoming" keeps items until a per-type grace
                            // period after their real end time (2h after
                            // landing for flights, 1h after end for
                            // meetings/events/etc.) and keeps TBD items.
                            if (dateFilter !== 'all') {
                              const range = {
                                from: dateFilterRange.start ? new Date(dateFilterRange.start + 'T00:00:00') : null,
                                to: dateFilterRange.end ? new Date(dateFilterRange.end + 'T00:00:00') : null,
                              };
                              if (!passesTimeFilter(component, dateFilter, { range })) return false;
                            }
                            return true;
                          })
                          .map((component, index, filteredComponents) => {
                          const isComponentTBD = isTBDComponent(component);
                          const componentType = component.originalComponent?.component_type || component.component_type;
                          const isEligibleForUrbanride = isUrbanrideEligible(componentType);
                          const isHidden = hiddenComponents.has(component.id);
                          const prevComponent = index > 0 ? filteredComponents[index - 1] : null;
                          const typeStyle = componentTypeStyles[componentType] || { backgroundColor: 'rgb(var(--ui-hover))', borderColor: 'rgb(var(--ui-control))' };

                          // Cards view (modern): hotel check-in/check-out get the stay and check-out cards
                          const hotelCardMode = viewMode === 'cards' && component.isHotelEvent && !isComponentTBD &&
                            !urbanrideSelectionMode && !quickEditComponent && !bundleSelectionMode;
                          const transferEnds = hotelCardMode ? hotelTransferEnds(filteredComponents, index, isTransferStop) : {};
                          const transferFrom = component.eventType === 'checkin' ? transferEnds.from : null;
                          const transferTo = component.eventType === 'checkout' ? transferEnds.to : null;

                          // Cards view: flights, rides, rental cars, meals, meetings, events,
                          // VIP services and memos get their designed card
                          const designedKind = !component.isHotelEvent && !isComponentTBD && !urbanrideSelectionMode &&
                            !quickEditComponent && !bundleSelectionMode ? tripCardKind(component) : null;
                          const designCardKind = viewMode === 'cards' ? designedKind : null;
                          const isOtherStop = c => c.id !== component.id && (c.originalId || c.id) !== (component.originalId || component.id) && isTransferStop(c);
                          const stopBefore = designedKind ? filteredComponents.slice(0, index).reverse().find(isOtherStop) || null : null;
                          const stopAfter = designedKind ? filteredComponents.slice(index + 1).find(isOtherStop) || null : null;

                          // Full view: the classic card renders inside the designed card
                          const canBookDesigned = !isReadOnly && isEligibleForUrbanride;
                          const fullDesignedCard = viewMode === 'full' && designedKind ? {
                            canViewTrip: false,
                            onOpenConfidentiality: canToggleComponentConfidentiality ? () => handleOpenConfidentialityModal(component.id) : undefined,
                            isConfidential: isComponentConfidential(component.id),
                            rideTo: findLinkedRide(component, 'to'),
                            rideFrom: findLinkedRide(component, 'from'),
                            prevStop: stopBefore,
                            nextStop: stopAfter,
                            onBookTo: canBookDesigned && stopBefore ? () => bookTransferBetween(stopBefore, component) : undefined,
                            onBookFrom: canBookDesigned && stopAfter ? () => bookTransferBetween(component, stopAfter) : undefined,
                          } : undefined;

                          // ── Bundle logic ─────────────────────────────────────────
                          const isBundle = componentType === 'bundle';
                          const bundleChildIds = isBundle
                            ? (component.component_data?.child_component_ids || [])
                            : [];
                          const bundleChildCount = bundleChildIds.length;
                          const isBundleExpanded = isBundle && expandedBundleIds.has(component.id);

                          // Resolve child components for the sub-timeline (only when expanded)
                          const bundleChildren = isBundleExpanded
                            ? bundleChildIds
                                .map(cid => (components || []).find(c => c.id === cid))
                                .filter(Boolean)
                                .sort((a, b) => {
                                  const ta = a.start_datetime ? new Date(a.start_datetime).getTime() : Infinity;
                                  const tb = b.start_datetime ? new Date(b.start_datetime).getTime() : Infinity;
                                  return ta - tb;
                                })
                            : [];

                          // ── Compact mode: single-line row ──────────────────────────
                          if (viewMode === 'compact' && !pdfExportMode) {
                            const notesForComponent = [
                              ...getNotesInside(component.id),
                              ...getNotesAtPosition('attached', component.id),
                              ...(prevComponent ? getNotesBetween(prevComponent.id, component.id) : []),
                            ];
                            return (
                              <div
                                key={component.id}
                                ref={el => { compactRowRefs.current[component.id] = el; }}
                              >
                                <CompactTimelineRow
                                  component={component}
                                  typeStyle={typeStyle}
                                  isExpanded={compactExpandedIds.has(component.id)}
                                  onToggleExpand={toggleCompactExpanded}
                                  rowIndex={index}
                                  hasNotes={notesForComponent.length > 0}
                                  selectionMode={urbanrideSelectionMode || !!quickEditComponent || !!bundleSelectionMode}
                                  isSelected={bundleSelectionMode ? bundleSelectedIds.has(component.id) : isComponentSelected(component)}
                                  onToggleSelect={bundleSelectionMode ? handleBundleToggleComponent : (quickEditComponent ? handleQuickEditCardClick : toggleComponentSelection)}
                                  isSelectable={bundleSelectionMode ? (componentType !== 'bundle' && (!component.bundle_id || component.bundle_id === bundleSelectionMode.id)) : isEligibleForUrbanride}
                                  onEditComponent={(comp) => onEditComponent(comp.originalComponent || comp)}
                                  onDeleteComponent={(comp) => onDeleteComponent(comp.originalComponent || comp)}
                                  onReassignComponent={onReassignComponent}
                                  allTrips={allTrips}
                                  currentTripId={trip?.id}
                                  trip={enrichedTrip}
                                  insideNotes={getNotesInside(component.id)}
                                  onToggleNoteExpand={handleToggleNoteExpand}
                                  onEditNote={handleEditNote}
                                  onDeleteNote={handleDeleteNote}
                                  onNotePositionChange={handleFloatingNotePositionChange}
                                  onNoteMoveToComponent={handleMoveNoteToComponent}
                                  currentUserId={user?.id}
                                  isConfidential={isComponentConfidential(component.id)}
                                  canToggleConfidentiality={canToggleComponentConfidentiality}
                                  onToggleConfidentiality={handleToggleComponentConfidentiality}
                                  onOpenConfidentialityModal={handleOpenConfidentialityModal}
                                  onRevealConfidential={handleRevealConfidential}
                                  onConcealConfidential={handleConcealConfidential}
                                  isRevealingId={revealingComponentId}
                                  isConcealingId={concealingComponentId}
                                  onQuickEdit={handleQuickEdit}
                                  onAddVipServiceForLeg={onAddVipServiceForLeg}
                                  onInlineUpdateComponent={onInlineUpdateComponent}
                                  formatDateTime={formatDateTime}
                                  getStatusColor={getStatusColor}
                                  getComponentTypeName={getComponentTypeName}
                                  componentTypeStyles={componentTypeStyles}
                                  executives={tripExecs}
                                />
                                {/* Bundle expand/collapse + sub-timeline (compact) */}
                                {isBundle && (
                                  <div className="mt-1 ml-2">
                                    <div className="flex items-center gap-2">
                                      {bundleChildCount > 0 && (
                                        <button
                                          onClick={() => toggleBundleExpand(component.id)}
                                          className="flex items-center gap-1 text-xs text-ui-violet-fg transition-colors"
                                        >
                                          {isBundleExpanded
                                            ? <ChevronDown className="w-3 h-3" />
                                            : <Plus className="w-3 h-3" />}
                                          <span>{bundleChildCount} item{bundleChildCount !== 1 ? 's' : ''}</span>
                                        </button>
                                      )}
                                      {!urbanrideSelectionMode && !pdfExportMode && !quickEditComponent && !bundleSelectionMode && (
                                        <button
                                          onClick={() => handleStartBundleSelection(component)}
                                          className="flex items-center gap-1 text-xs text-ui-violet-fg transition-colors"
                                        >
                                          <Settings className="w-3 h-3" />
                                          <span>Manage Items</span>
                                        </button>
                                      )}
                                    </div>
                                    {isBundleExpanded && (
                                      <div className="mt-1 ml-2 border-l-2 border-ui-violet-line pl-2 space-y-1">
                                        {bundleChildren.map(child => {
                                          const childType = child.component_type;
                                          const childTypeStyle = componentTypeStyles[childType] || { backgroundColor: 'rgb(var(--ui-hover))', borderColor: 'rgb(var(--ui-control))' };
                                          return (
                                            <CompactTimelineRow
                                              key={child.id}
                                              component={child}
                                              typeStyle={childTypeStyle}
                                              isExpanded={compactExpandedIds.has(child.id)}
                                              onToggleExpand={toggleCompactExpanded}
                                              rowIndex={0}
                                              hasNotes={false}
                                              selectionMode={false}
                                              isSelected={false}
                                              onToggleSelect={() => {}}
                                              isSelectable={false}
                                              onEditComponent={(comp) => onEditComponent(comp.originalComponent || comp)}
                                              onDeleteComponent={(comp) => onDeleteComponent(comp.originalComponent || comp)}
                                              onReassignComponent={onReassignComponent}
                                              allTrips={allTrips}
                                              currentTripId={trip?.id}
                                              trip={enrichedTrip}
                                              insideNotes={[]}
                                              onToggleNoteExpand={handleToggleNoteExpand}
                                              onEditNote={handleEditNote}
                                              onDeleteNote={handleDeleteNote}
                                              onNotePositionChange={handleFloatingNotePositionChange}
                                              onNoteMoveToComponent={handleMoveNoteToComponent}
                                              currentUserId={user?.id}
                                              isConfidential={false}
                                              canToggleConfidentiality={false}
                                              onToggleConfidentiality={undefined}
                                              onOpenConfidentialityModal={undefined}
                                              onRevealConfidential={handleRevealConfidential}
                                              onConcealConfidential={handleConcealConfidential}
                                              isRevealingId={revealingComponentId}
                                              isConcealingId={concealingComponentId}
                                              onQuickEdit={handleQuickEdit}
                                              onAddVipServiceForLeg={onAddVipServiceForLeg}
                                              onInlineUpdateComponent={onInlineUpdateComponent}
                                              formatDateTime={formatDateTime}
                                              getStatusColor={getStatusColor}
                                              getComponentTypeName={getComponentTypeName}
                                              componentTypeStyles={componentTypeStyles}
                                              executives={tripExecs}
                                            />
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // ── Full / Summary mode: existing DraggableTimelineCard ───
                          return (
                            <React.Fragment key={component.id}>
                              {/* Sticky notes between components */}
                              {!urbanrideSelectionMode && !pdfExportMode && prevComponent && getNotesBetween(prevComponent.id, component.id).map(note => (
                                <StickyNote
                                  key={note.id}
                                  note={note}
                                  onToggleExpand={handleToggleNoteExpand}
                                  onEdit={handleEditNote}
                                  onDelete={handleDeleteNote}
                                  isDraggable={!isReadOnly}
                                  currentUserId={user?.id}
                                />
                              ))}

                              {/* Sticky notes attached to this component (before) */}
                              {!urbanrideSelectionMode && !pdfExportMode && getNotesAtPosition('attached', component.id).map(note => (
                                <StickyNote
                                  key={note.id}
                                  note={note}
                                  onToggleExpand={handleToggleNoteExpand}
                                  onEdit={handleEditNote}
                                  onDelete={handleDeleteNote}
                                  isDraggable={!isReadOnly}
                                  currentUserId={user?.id}
                                />
                              ))}

                              {/* PDF Export Mode - wrap with checkbox */}
                              {pdfExportMode ? (
                                <div className="flex items-start gap-3">
                                  <button
                                    onClick={() => togglePdfItemSelection(component.id)}
                                    className="flex-shrink-0 mt-4 p-1 hover:bg-ui-violet-bg rounded transition-colors"
                                    title={pdfSelectedItems.has(component.id) ? 'Exclude from PDF' : 'Include in PDF'}
                                  >
                                    {pdfSelectedItems.has(component.id) ? (
                                      <CheckSquare className="w-5 h-5 text-ui-violet-fg" />
                                    ) : (
                                      <Square className="w-5 h-5 text-ui-faint" />
                                    )}
                                  </button>
                                  <div className={`flex-1 transition-opacity ${pdfSelectedItems.has(component.id) ? 'opacity-100' : 'opacity-50'}`}>
                                    <DraggableTimelineCard
                                      component={component}
                                      onEditComponent={() => {}}
                                      onDeleteComponent={() => {}}
                                      onReassignComponent={() => {}}
                                      allTrips={[]}
                                      currentTripId={trip?.id}
                                      isDraggable={false}
                                      formatDateTime={formatDateTime}
                                      getStatusColor={getStatusColor}
                                      getComponentTypeName={getComponentTypeName}
                                      componentTypeStyles={componentTypeStyles}
                                      viewMode={viewMode}
                                      trip={enrichedTrip}
                                      selectionMode={false}
                                      isSelected={false}
                                      onToggleSelect={() => {}}
                                      isSelectable={false}
                                      isHidden={false}
                                      onToggleHide={() => {}}
                                      insideNotes={[]}
                                      onToggleNoteExpand={() => {}}
                                      onEditNote={() => {}}
                                      onDeleteNote={() => {}}
                                      onNotePositionChange={() => {}}
                                      onNoteMoveToComponent={() => {}}
                                      currentUserId={user?.id}
                                      isConfidential={isComponentConfidential(component.id)}
                                      canToggleConfidentiality={false}
                                      onToggleConfidentiality={undefined}
                                      onRevealConfidential={handleRevealConfidential}
                                      onConcealConfidential={handleConcealConfidential}
                                      isRevealingId={revealingComponentId}
                                      isConcealingId={concealingComponentId}
                                    />
                                  </div>
                                </div>
                              ) : designCardKind ? (
                                (() => {
                                  const canBook = !isReadOnly && isEligibleForUrbanride;
                                  return (
                                    <TripTimelineCard
                                      component={component}
                                      trip={enrichedTrip}
                                      prefs={user?.ui_preferences}
                                      onEdit={isReadOnly ? undefined : () => onEditComponent(component.originalComponent || component)}
                                      onDelete={isReadOnly ? undefined : onDeleteComponent}
                                      onQuickEdit={isReadOnly ? undefined : () => handleQuickEdit(component.originalComponent || component)}
                                      isConfidential={isComponentConfidential(component.id)}
                                      onOpenConfidentiality={canToggleComponentConfidentiality ? () => handleOpenConfidentialityModal(component.id) : undefined}
                                      insideNotes={getNotesInside(component.id)}
                                      noteHandlers={{ onToggleNoteExpand: handleToggleNoteExpand, onEditNote: handleEditNote, onDeleteNote: handleDeleteNote }}
                                      currentUserId={user?.id}
                                      rideTo={findLinkedRide(component, 'to')}
                                      rideFrom={findLinkedRide(component, 'from')}
                                      prevStop={stopBefore}
                                      nextStop={stopAfter}
                                      onBookTo={canBook && stopBefore ? () => bookTransferBetween(stopBefore, component) : undefined}
                                      onBookFrom={canBook && stopAfter ? () => bookTransferBetween(component, stopAfter) : undefined}
                                    />
                                  );
                                })()
                              ) : hotelCardMode ? (
                                (() => {
                                  const hotelProps = {
                                    component,
                                    onDelete: isReadOnly ? undefined : onDeleteComponent,
                                    onEdit: isReadOnly ? undefined : () => onEditComponent(component.originalComponent || component),
                                    isConfidential: isComponentConfidential(component.id),
                                    onOpenConfidentiality: canToggleComponentConfidentiality ? () => handleOpenConfidentialityModal(component.id) : undefined,
                                    insideNotes: getNotesInside(component.id),
                                    noteHandlers: { onToggleNoteExpand: handleToggleNoteExpand, onEditNote: handleEditNote, onDeleteNote: handleDeleteNote },
                                    currentUserId: user?.id,
                                    prefs: user?.ui_preferences,
                                  };
                                  const hotelData = component.hotelData || component.component_data;
                                  return component.eventType === 'checkout' ? (
                                    <HotelCheckoutCard
                                      {...hotelProps}
                                      departureTransfer={findHotelTransfer(hotelData, 'departure')}
                                      nextStop={describeStop(transferTo)}
                                      onBookDeparture={isReadOnly ? undefined : () => bookTransferBetween(component, transferTo)}
                                    />
                                  ) : (
                                    <HotelStayCard
                                      {...hotelProps}
                                      trip={enrichedTrip}
                                      arrivalTransfer={findHotelTransfer(hotelData, 'arrival')}
                                      arrivalFrom={transferFrom?.title}
                                      onBookArrival={isReadOnly || !transferFrom ? undefined : () => bookTransferBetween(transferFrom, component)}
                                    />
                                  );
                                })()
                              ) : (
                                <>
                                <DraggableTimelineCard
                                  component={component}
                                  onEditComponent={(comp) => {
                                    // If it's a flight leg or hotel event, edit the original component
                                    const componentToEdit = comp.originalComponent || comp;
                                    onEditComponent(componentToEdit);
                                  }}
                                  onDeleteComponent={(comp) => {
                                    // If it's a flight leg or hotel event, delete the original component
                                    const componentToDelete = comp.originalComponent || comp;
                                    onDeleteComponent(componentToDelete);
                                  }}
                                  onReassignComponent={onReassignComponent}
                                  allTrips={allTrips}
                                  currentTripId={trip?.id}
                                  isDraggable={isComponentTBD && !urbanrideSelectionMode && !bundleSelectionMode && !isReadOnly}
                                  formatDateTime={formatDateTime}
                                  getStatusColor={getStatusColor}
                                  getComponentTypeName={getComponentTypeName}
                                  componentTypeStyles={componentTypeStyles}
                                  viewMode={viewMode}
                                  trip={enrichedTrip}
                                  // Selection props (Urbanride, Quick Edit, or Bundle mode)
                                  selectionMode={urbanrideSelectionMode || !!quickEditComponent || !!bundleSelectionMode}
                                  isSelected={bundleSelectionMode ? bundleSelectedIds.has(component.id) : isComponentSelected(component)}
                                  onToggleSelect={bundleSelectionMode ? handleBundleToggleComponent : (quickEditComponent ? handleQuickEditCardClick : toggleComponentSelection)}
                                  isSelectable={bundleSelectionMode ? (componentType !== 'bundle' && (!component.bundle_id || component.bundle_id === bundleSelectionMode.id)) : isEligibleForUrbanride}
                                  // Hide component props
                                  isHidden={isHidden}
                                  onToggleHide={() => toggleHideComponent(component.id)}
                                  // Sticky notes inside this component
                                  insideNotes={!urbanrideSelectionMode ? getNotesInside(component.id) : []}
                                  onToggleNoteExpand={handleToggleNoteExpand}
                                  onEditNote={handleEditNote}
                                  onDeleteNote={handleDeleteNote}
                                  onNotePositionChange={handleFloatingNotePositionChange}
                                  onNoteMoveToComponent={handleMoveNoteToComponent}
                                  currentUserId={user?.id}
                                  // Component confidentiality props
                                  isConfidential={isComponentConfidential(component.id)}
                                  canToggleConfidentiality={canToggleComponentConfidentiality}
                                  onToggleConfidentiality={handleToggleComponentConfidentiality}
                                  onOpenConfidentialityModal={handleOpenConfidentialityModal}
                                  // Confidential placeholder reveal/conceal props
                                  onRevealConfidential={handleRevealConfidential}
                                  onConcealConfidential={handleConcealConfidential}
                                  isRevealingId={revealingComponentId}
                                  isConcealingId={concealingComponentId}
                                  // Quick Edit callback for existing chauffeur_service bookings
                                  onQuickEdit={!urbanrideSelectionMode && !pdfExportMode && !quickEditComponent && !bundleSelectionMode ? handleQuickEdit : undefined}
                                  onAddVipServiceForLeg={!urbanrideSelectionMode && !pdfExportMode && !quickEditComponent && !bundleSelectionMode ? onAddVipServiceForLeg : undefined}
                                  onInlineUpdateComponent={!urbanrideSelectionMode && !pdfExportMode && !quickEditComponent && !bundleSelectionMode ? onInlineUpdateComponent : undefined}
                                  designedCard={fullDesignedCard}
                                />
                                {/* Bundle expand/collapse + sub-timeline (full/summary mode) */}
                                {isBundle && (
                                  <div className={`mt-2 mb-2`}>
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {bundleChildCount > 0 && (
                                        <button
                                          onClick={() => toggleBundleExpand(component.id)}
                                          className={`flex items-center gap-1.5 text-xs font-medium transition-colors px-2 sm:px-3 py-1.5 rounded-lg text-ui-violet-fg hover:bg-ui-violet-bg`}
                                        >
                                          {isBundleExpanded
                                            ? <ChevronDown className="w-3.5 h-3.5" />
                                            : <Plus className="w-3.5 h-3.5" />}
                                          <FolderOpen className="w-3.5 h-3.5" />
                                          <span>{isBundleExpanded ? 'Hide' : 'Show'} {bundleChildCount} <span className="hidden sm:inline">bundled </span>item{bundleChildCount !== 1 ? 's' : ''}</span>
                                        </button>
                                      )}
                                      {!urbanrideSelectionMode && !pdfExportMode && !quickEditComponent && !bundleSelectionMode && (
                                        <button
                                          onClick={() => handleStartBundleSelection(component)}
                                          className={`flex items-center gap-1.5 text-xs font-medium transition-colors px-2 sm:px-3 py-1.5 rounded-lg text-ui-violet-fg hover:bg-ui-violet-bg`}
                                        >
                                          <Settings className="w-3.5 h-3.5" />
                                          <span><span className="hidden sm:inline">Manage </span>Items</span>
                                        </button>
                                      )}
                                    </div>
                                    {isBundleExpanded && (
                                      <div className={`mt-2 ml-3 border-l-2 pl-3 space-y-3 border-ui-violet-line`}>
                                        {bundleChildren.map(child => {
                                          const childType = child.component_type;
                                          return (
                                            <DraggableTimelineCard
                                              key={child.id}
                                              component={child}
                                              onEditComponent={(comp) => onEditComponent(comp.originalComponent || comp)}
                                              onDeleteComponent={(comp) => onDeleteComponent(comp.originalComponent || comp)}
                                              onReassignComponent={onReassignComponent}
                                              allTrips={allTrips}
                                              currentTripId={trip?.id}
                                              isDraggable={false}
                                              formatDateTime={formatDateTime}
                                              getStatusColor={getStatusColor}
                                              getComponentTypeName={getComponentTypeName}
                                              componentTypeStyles={componentTypeStyles}
                                              viewMode={viewMode}
                                              trip={enrichedTrip}
                                              selectionMode={false}
                                              isSelected={false}
                                              onToggleSelect={() => {}}
                                              isSelectable={false}
                                              isHidden={false}
                                              onToggleHide={() => {}}
                                              insideNotes={[]}
                                              onToggleNoteExpand={handleToggleNoteExpand}
                                              onEditNote={handleEditNote}
                                              onDeleteNote={handleDeleteNote}
                                              onNotePositionChange={handleFloatingNotePositionChange}
                                              onNoteMoveToComponent={handleMoveNoteToComponent}
                                              currentUserId={user?.id}
                                              isConfidential={false}
                                              canToggleConfidentiality={false}
                                              onToggleConfidentiality={undefined}
                                              onRevealConfidential={handleRevealConfidential}
                                              onConcealConfidential={handleConcealConfidential}
                                              isRevealingId={revealingComponentId}
                                              isConcealingId={concealingComponentId}
                                              onQuickEdit={handleQuickEdit}
                                              onAddVipServiceForLeg={onAddVipServiceForLeg}
                                              onInlineUpdateComponent={onInlineUpdateComponent}
                                            />
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                                </>
                              )}
                              {/* Combined drop zone + insert button between components (planner only) */}
                              {!urbanrideSelectionMode && !pdfExportMode && !isReadOnly && (
                                <DropZoneWithInsert
                                  dropId={`drop-after-${component.id}`}
                                  onInsert={() => {
                                    const nextComponent = filteredComponents[index + 1];
                                    // Use component.id (not originalId) to anchor to the specific item clicked
                                    // This is important for multi-leg flights where all legs share the same originalId
                                    const anchorInfo = {
                                      beforeComponentId: component.id,
                                      afterComponentId: nextComponent ? nextComponent.id : null,
                                      position: nextComponent ? 'between' : 'end'
                                    };
                                    handleOpenQuickAdd(anchorInfo);
                                  }}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}

                        {/* Sticky notes at end of timeline (not shown in compact mode) */}
                        {viewMode !== 'compact' && !urbanrideSelectionMode && !pdfExportMode && getNotesAtPosition('end').map(note => (
                          <StickyNote
                            key={note.id}
                            note={note}
                            onToggleExpand={handleToggleNoteExpand}
                            onEdit={handleEditNote}
                            onDelete={handleDeleteNote}
                            isDraggable={!isReadOnly}
                            currentUserId={user?.id}
                          />
                        ))}

                        {/* End drop zone - hidden in selection mode, PDF export mode, compact mode, and read-only view */}
                        {viewMode !== 'compact' && !urbanrideSelectionMode && !pdfExportMode && !isReadOnly && (
                          <DroppableZone
                            id="timeline-end"
                            className={`h-12 sm:h-20 w-full border-2 border-dashed transition-colors rounded-lg my-2 sm:my-4 bg-ui-violet-bg border-ui-violet-line hover:border-ui-violet-solid hover:bg-ui-violet-line/40`}
                          >
                            <div className={`text-xs sm:text-sm text-center py-2.5 sm:py-6 font-semibold text-ui-violet-fg`}>Drop here to place at end</div>
                          </DroppableZone>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </motion.div>
            )}

            {/* Empty State */}
            {allComponents.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`text-center py-8 sm:py-12 rounded-lg sm:rounded-xl border px-4 bg-ui-surface border-ui-line`}
              >
                <CalendarIcon className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-ui-muted`} />
                <h3 className={`text-base sm:text-lg font-semibold mb-1.5 sm:mb-2 text-ui-ink`}>No Components Yet</h3>
                <p className={`text-sm sm:text-base text-ui-ink-3`}>Add components to see them here</p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Right side: Booking Summary, Quick Edit, or Bundle Selection panel */}
        {/* On mobile: fixed bottom sheet, on desktop: sticky sidebar */}
        <AnimatePresence>
          {/* Bundle Selection side panel */}
          {bundleSelectionMode && !urbanrideSelectionMode && !quickEditComponent && (
            <>
              {/* Desktop sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 20, width: 0 }}
                animate={{ opacity: 1, x: 0, width: 340 }}
                exit={{ opacity: 0, x: 20, width: 0 }}
                transition={{ duration: 0.3 }}
                className="hidden lg:block flex-shrink-0 sticky top-24 self-start h-[calc(100vh-7rem)] overflow-hidden rounded-xl shadow-lg"
              >
                <BundleSelectionSummary
                  bundle={bundleSelectionMode}
                  selectedIds={bundleSelectedIds}
                  tripComponents={components}
                  onRemove={(id) => setBundleSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; })}
                  onSave={handleBundleSave}
                  onCancel={handleBundleCancel}
                  hasChanges={bundleHasChanges}
                  isSaving={bundleSaving}
                />
              </motion.div>

              {/* Mobile bottom sheet */}
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.3 }}
                className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 shadow-2xl rounded-t-2xl max-h-[45vh] overflow-y-auto safe-area-inset-bottom bg-ui-surface border-t border-ui-line`}
              >
                <BundleSelectionSummary
                  bundle={bundleSelectionMode}
                  selectedIds={bundleSelectedIds}
                  tripComponents={components}
                  onRemove={(id) => setBundleSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; })}
                  onSave={handleBundleSave}
                  onCancel={handleBundleCancel}
                  hasChanges={bundleHasChanges}
                  isSaving={bundleSaving}
                />
              </motion.div>
            </>
          )}

          {/* Quick Edit side panel */}
          {quickEditComponent && !urbanrideSelectionMode && (
            <>
              {/* Desktop sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 20, width: 0 }}
                animate={{ opacity: 1, x: 0, width: 420 }}
                exit={{ opacity: 0, x: 20, width: 0 }}
                transition={{ duration: 0.3 }}
                className="hidden lg:block flex-shrink-0 sticky top-24 self-start h-[calc(100vh-7rem)] overflow-hidden rounded-xl shadow-lg"
              >
                <UrbanrideQuickBooking
                  selectedComponents={[]}
                  tripId={trip?.id}
                  tripExecutives={tripExecs}
                  allExecutives={allExecutives}
                  onSuccess={handleQuickEditSuccess}
                  onCancel={handleQuickEditCancel}
                  onUpdateComponents={onRefreshComponents}
                  editMode={true}
                  sidePanel={true}
                  existingComponentId={quickEditComponent.id}
                  existingUrcn={quickEditComponent.component_data?.urbanride_urcn || quickEditComponent.confirmation_number}
                  existingComponentData={quickEditComponent.component_data}
                  incomingTimelineComponent={quickEditIncomingComponent}
                  onClearIncomingComponent={() => setQuickEditIncomingComponent(null)}
                />
              </motion.div>

              {/* Mobile: still use full-screen modal for small screens */}
              <div className="lg:hidden">
                <UrbanrideQuickBooking
                  selectedComponents={[]}
                  tripId={trip?.id}
                  tripExecutives={tripExecs}
                  allExecutives={allExecutives}
                  onSuccess={handleQuickEditSuccess}
                  onCancel={handleQuickEditCancel}
                  onUpdateComponents={onRefreshComponents}
                  editMode={true}
                  existingComponentId={quickEditComponent.id}
                  existingUrcn={quickEditComponent.component_data?.urbanride_urcn || quickEditComponent.confirmation_number}
                  existingComponentData={quickEditComponent.component_data}
                  incomingTimelineComponent={quickEditIncomingComponent}
                  onClearIncomingComponent={() => setQuickEditIncomingComponent(null)}
                />
              </div>
            </>
          )}

          {urbanrideSelectionMode && (
            <>
              {/* Desktop sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 20, width: 0 }}
                animate={{ opacity: 1, x: 0, width: 380 }}
                exit={{ opacity: 0, x: 20, width: 0 }}
                transition={{ duration: 0.3 }}
                className="hidden lg:block flex-shrink-0 sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto"
              >
                <UrbanrideBookingSummary
                  selectedComponents={selectedComponents.filter(c => !c.isReturnLeg)}
                  onRemoveComponent={handleRemoveFromSelection}
                  onReorderComponents={handleReorderComponents}
                  onStartBooking={startUrbanrideBooking}
                  onCancel={toggleUrbanrideSelectionMode}
                  onAddManualLocation={handleAddManualLocation}
                  tripExecutives={tripExecs}
                />
              </motion.div>

              {/* Mobile bottom sheet */}
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.3 }}
                className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 shadow-2xl rounded-t-2xl max-h-[45vh] overflow-y-auto safe-area-inset-bottom bg-ui-surface border-t border-ui-line`}
              >
                <div className={`sticky top-0 px-4 py-2 flex items-center justify-between bg-ui-surface border-b border-ui-divider`}>
                  <div className="flex items-center gap-2">
                    <Car className={`w-4 h-4 text-ui-blue-fg`} />
                    <span className={`font-medium text-sm text-ui-ink`}>Route Builder</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full bg-ui-blue-bg text-ui-blue-fg`}>
                      {selectedComponents.filter(c => !c.isReturnLeg).length} stops
                    </span>
                  </div>
                  <button
                    onClick={toggleUrbanrideSelectionMode}
                    className={`p-2 touch-target text-ui-ink-3 hover:text-ui-ink`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-3">
                  <UrbanrideBookingSummary
                    selectedComponents={selectedComponents.filter(c => !c.isReturnLeg)}
                    onRemoveComponent={handleRemoveFromSelection}
                    onReorderComponents={handleReorderComponents}
                    onStartBooking={startUrbanrideBooking}
                    onCancel={toggleUrbanrideSelectionMode}
                    onAddManualLocation={handleAddManualLocation}
                    tripExecutives={tripExecs}
                    isMobile={true}
                  />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Ghost note during placement */}
      {isPlacingNote && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: Math.max(10, placingNotePosition.x - 50),
            top: placingNotePosition.y - 15,
          }}
        >
          <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-ui-amber-bg border-2 border-ui-amber-solid rounded-lg shadow-lg opacity-90">
            <StickyNoteIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-ui-amber-fg" />
            <span className="text-xs sm:text-sm font-medium text-ui-amber-fg">New Note</span>
          </div>
          <div className="text-[10px] sm:text-xs text-ui-amber-fg mt-1 text-center bg-ui-amber-bg px-1.5 sm:px-2 py-0.5 rounded whitespace-nowrap">
            <span className="sm:hidden">Release to place</span>
            <span className="hidden sm:inline">Release to place • Esc to cancel</span>
          </div>
        </div>
      )}

      {/* Sticky Note Modal */}
      {showStickyNoteModal && (
        <StickyNoteModal
          note={editingNote}
          onClose={() => {
            setShowStickyNoteModal(false);
            setEditingNote(null);
            setPendingNotePosition(null);
          }}
          onSave={handleSaveNote}
          defaultPosition={pendingNotePosition}
          defaultComponentId={
            // Fallback for editing or if no pending position
            !editingNote && !pendingNotePosition && allComponents.length > 0
              ? allComponents.filter(c => viewMode === 'full' || !hiddenComponents.has(c.id))[0]?.id
              : null
          }
        />
      )}

      {/* Confidentiality Settings Modal */}
      {showConfidentialityModal && confidentialityTarget && (
        <ConfidentialitySettingsModal
          isOpen={showConfidentialityModal}
          onClose={() => {
            setShowConfidentialityModal(false);
            setConfidentialityTarget(null);
          }}
          target={confidentialityTarget}
          onUpdate={handleConfidentialityUpdate}
        />
      )}

      {/* Quick Add Component Modal */}
      {showQuickAddModal && (
        <div className="fixed inset-0 bg-ui-overlay/40 flex items-center justify-center z-[70]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`rounded-xl p-4 sm:p-6 w-full max-w-md mx-4 shadow-xl bg-ui-raised border border-ui-line`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold text-ui-ink`}>Add TBD Component</h3>
              <button
                onClick={handleCloseQuickAdd}
                className={`transition-colors text-ui-ink-3 hover:text-ui-ink`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className={`text-sm mb-4 text-ui-ink-3`}>
              Enter an optional title and select a component type. It will be created with TBD dates/times so you can position it in your timeline.
            </p>

            {/* Title input */}
            <div className={`mb-5 p-3 rounded-lg border bg-ui-violet-bg border-ui-violet-line`}>
              <label htmlFor="quick-add-title" className={`block text-sm font-semibold mb-2 text-ui-violet-fg`}>
                Component Title
              </label>
              <input
                ref={quickAddTitleInputRef}
                id="quick-add-title"
                type="text"
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
                placeholder="Enter a title for this component..."
                className={`w-full px-3 py-2.5 rounded-lg focus:ring-2 focus:ring-ui-violet-solid text-sm bg-ui-sunk border border-ui-control text-ui-ink placeholder-ui-faint focus:border-ui-violet-solid`}
                disabled={isCreatingComponent}
              />
              <p className={`text-xs mt-1.5 text-ui-violet-fg`}>Leave blank for a default title</p>
            </div>

            <p className={`text-sm font-medium mb-3 text-ui-ink-2`}>Select component type:</p>

            <div className="grid grid-cols-2 gap-3">
              {quickAddComponentTypes.map((compType) => {
                const Icon = compType.icon;
                return (
                  <button
                    key={compType.type}
                    onClick={() => handleQuickAddComponent(compType.type)}
                    disabled={isCreatingComponent}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      isCreatingComponent ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    } border-ui-control hover:border-ui-violet-line hover:bg-ui-violet-bg`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${compType.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`text-sm font-medium text-ui-ink`}>{compType.title}</span>
                  </button>
                );
              })}
            </div>

            {isCreatingComponent && (
              <div className={`mt-4 text-center text-sm text-ui-ink-3`}>
                Creating component...
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Timeline2;